import { useMemo, useState, useEffect, useRef } from 'react';
import EmailDescription from './EmailDescription';
import {
  Drawer,
  Text,
  Loader,
  Button,
  Collapse,
  Skeleton,
  Stack,
  Group,
  Alert,
  Badge,
  Textarea,
  Modal,
  FileButton,
  MultiSelect,
  Switch,
  NumberInput,
  Select,
  TextInput,
  ScrollArea,
  SegmentedControl,
  ActionIcon,
  Paper,
  Divider,
  Popover,
  Tooltip,
  Indicator,
  Menu,
  UnstyledButton,
  Stepper,
  Checkbox,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { parseGermanDate } from '../pages/projects/projectLabels';
import { workdaysAheadYmd } from '../pages/todos/todoLabels';
import { MentionTextarea } from './MentionTextarea';
import {
  IconAlertTriangle,
  IconChevronDown,
  IconChevronRight,
  IconMessage,
  IconPaperclip,
  IconRefresh,
  IconListCheck,
  IconLock,
  IconPencil,
  IconClockPlus,
  IconClockShare,
  IconClockPlay,
  IconClockPause,
  IconSend,
  IconEdit,
  IconFileText,
  IconHistory,
  IconExternalLink,
  IconInfoCircle,
  IconUserCheck,
  IconUserOff,
  IconUsers,
  IconMinus,
  IconPlus,
  IconBuilding,
  IconAt,
  IconUserPlus,
  IconCheck,
  IconSparkles,
  IconMail,
  IconMailOpened,
  IconPhoto,
  IconMessagePlus,
  IconDownload,
  IconShieldCheck,
  IconShieldQuestion,
  IconShieldX,
  IconEye,
  IconX,
  IconTrash,
  IconArrowBackUp,
  IconShare2,
} from '@tabler/icons-react';
import { ClockInHint } from './attendance/ClockInHint';
import { useMutation, useQuery, useQueryClient, useIsFetching } from '@tanstack/react-query';
import { useDebouncedValue, useMediaQuery } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { parseDurationInput } from '../lib/durationInput';
import { bookingPlausibilityWarning } from '../lib/bookingPlausibility';
import { useAuthStore } from '../stores/auth';
import { mayBookForOthers, proposalHint } from '../lib/timeProposals';
import { useUnsavedDrafts } from '../stores/unsavedDrafts';
import { useBookingArticles } from '../hooks/useBookingArticles';
import { usePersonColors } from '../hooks/usePersonColors';
import { api, ApiError, WeclappTicket, WeclappTicketComment, WeclappDocumentRef, WeclappPerformanceRecord, TIME_PROPOSALS_QUERY_KEY } from '../lib/api';
import type { AttachmentScanStatus } from '../lib/api/domains/weclapp';
import type {
  WeclappCustomerCandidate,
  WeclappTicketHistoryEntry,
  WeclappTicketMail,
} from '../lib/api';
import { buildInlineImage, safeAltText, htmlToPlainText, plainTextToHtml } from '../lib/inlineImage';
import { ExternalRecipientsList } from './ExternalRecipientsList';
import { KianReplyProposals, isKianReplyProposalComment } from './KianReplyProposals';
import { isCustomerMissing } from '../lib/weclappCustomer';
import { showTicketUpdateNotice } from '../lib/ticketUpdateNotice';
import { useRegisterKianContext } from '../hooks/useRegisterKianContext';
import { WysiwygEditor } from './WysiwygEditor';
import { HandoverModal } from './HandoverModal';
import { HandoverBanner } from './HandoverBanner';
import { IconTransfer, IconBell, IconBellRinging, IconBrandWhatsapp, IconPhone, IconRestore } from '@tabler/icons-react';
import { Link } from 'react-router-dom';
import { KianAssistUsageHint } from './KianAssistUsageHint';
import { kianNotice } from './KianUsageLine';
import QRCode from 'qrcode';
import { KianAnalysisPanel } from './KianAnalysisPanel';
import { ProposeCustomerButton } from './ProposeCustomerButton';
import { weclappStatusColor, weclappStatusColorVar } from '../lib/ticketStatusColor';
import { StatusMailCheckbox } from './StatusMailChoice';
import type { StatusMailPreview } from '../lib/api/domains/weclapp';
import { TicketBillingScopeControl } from './TicketBillingScopeControl';
import { WeclappTicketReminderPanel, ticketTodosKey } from './TicketReminderControls';
import { CustomerNameLink } from './CustomerNameLink';
import { CallButton } from './call/CallButton';
import { CustomerCallButton } from './call/CustomerCallButton';
import { CustomerOpeningStatusBadge } from './customer/openingHours/CustomerOpeningStatusBadge';
import { formatRelativeTime } from '../lib/formatters';
import { restoreNotice } from '../lib/ticketTrashRestoreNotice';
import { personRingStyle } from '../lib/personColors';

// ============================================
// PROPS
// ============================================

interface WeclappTicketCockpitProps {
  ticketId: string | null;
  onClose: () => void;
  /**
   * Stapel-Ebene des Drawers. Default 500. Das Kunden-Modal (600) liegt darüber,
   * damit das Flyout beim Klick auf den Kundennamen VOR dem Cockpit erscheint;
   * öffnet umgekehrt das Kunden-Modal ein Ticket, hebt es das Cockpit auf 650.
   */
  zIndex?: number;
}

// ============================================
// HELPER
// ============================================

/** Entfernt das „(username)"-Suffix aus aufgelösten weclapp-Namen für die Anzeige. */
function displayAuthorName(name?: string): string | undefined {
  return name?.replace(/\s*\([^)]*\)\s*$/, '').trim() || name;
}

function parseTicketDate(ms?: string | number): Date | null {
  if (!ms) return null;
  // weclapp-Zeitstempel kommen mal als Unix-ms (ggf. als String), mal als ISO-String.
  // Numerisch zuerst versuchen; bei ISO-Strings (Number(...) === NaN) direkt parsen.
  const numeric = typeof ms === 'string' ? Number(ms) : ms;
  const date = Number.isFinite(numeric) ? new Date(numeric) : new Date(ms);
  return isNaN(date.getTime()) ? null : date;
}

/** „Do., 24.09.26, 19:38" — der Wochentag steht immer dabei. */
function formatDate(ms?: string | number): string {
  const date = parseTicketDate(ms);
  if (!date) return '–';
  return date.toLocaleString('de-DE', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** „Do., 24.09.26, 19:38 · vor 4 Tagen" — für Stellen, an denen das Alter zählt. */
function formatDateWithAge(ms?: string | number): string {
  const date = parseTicketDate(ms);
  if (!date) return '–';
  return `${formatDate(ms)} · ${formatRelativeTime(date)}`;
}

// ============================================
// SUBCOMPONENTS
// ============================================

// Gebündelte Cockpit-Daten. Alle Sub-Listen lesen denselben Query-Key
// (['weclapp-cockpit', ticketId]) → React Query dedupliziert zu EINEM Request.
type CockpitData = Awaited<ReturnType<typeof api.getWeclappTicketCockpit>>;

function CommentList({ ticketId }: { ticketId: string }) {
  const isMobile = useMediaQuery('(max-width: 48em)');
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['weclapp-cockpit', ticketId],
    queryFn: () => api.getWeclappTicketCockpit(ticketId),
    select: (d) => ({
      comments: d.comments,
      pendingBookings: d.pendingTimeBookings ?? [],
      stale: d.commentsStale === true,
    }),
    staleTime: 30000,
  });

  if (isLoading) {
    return (
      <Stack gap="xs" mt="xs">
        <Skeleton height={60} radius="sm" />
        <Skeleton height={60} radius="sm" />
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert color="red" variant="light" mt="xs">
        Fehler beim Laden der Kommentare.{' '}
        <Button size="compact-xs" variant="subtle" onClick={() => refetch()}>
          Erneut versuchen
        </Button>
        {error instanceof Error && error.message && (
          <Text size="xs" c="red.7" mt={4} style={{ wordBreak: 'break-word' }}>
            {error.message}
          </Text>
        )}
      </Alert>
    );
  }

  const comments = data?.comments ?? [];
  // Angehakte Ticketkommentare geparkter Zeitbuchungen, die noch nicht in Weclapp stehen —
  // pro Buchung ggf. eine interne UND eine öffentliche Zeile (gleicher Text).
  const pendingComments = (data?.pendingBookings ?? []).flatMap((b) => {
    const variants: Array<{ internal: boolean }> = [];
    if (b.commentInternalPending) variants.push({ internal: true });
    if (b.commentPublicPending) variants.push({ internal: false });
    return variants.map((v) => ({
      key: `${b.id}-${v.internal ? 'int' : 'pub'}`,
      internal: v.internal,
      comment: b.comment,
      technicianName: b.technicianName,
      createdAt: b.createdAt,
      status: b.status,
    }));
  });

  // Der Verlauf kommt aus dem lokalen Spiegel; ist weclapp gerade nicht erreichbar, fehlt
  // darin womöglich eine Antwort von vor zwei Minuten — und ein Verlauf ohne diese Antwort
  // sieht aus wie ein Verlauf ohne Antwort. Deshalb sagen, was man sieht.
  const staleNotice = data?.stale ? (
    <Alert color="yellow" variant="light" mt="xs" p="xs">
      <Text size="xs">
        Weclapp ist gerade nicht erreichbar — angezeigt wird der letzte bekannte Stand. Neue
        Kommentare von dort können fehlen.
      </Text>
    </Alert>
  ) : null;

  if (comments.length === 0 && pendingComments.length === 0) {
    return (
      <>
        {staleNotice}
        <Text size="sm" c="dimmed" mt="xs">
          Keine Kommentare vorhanden
        </Text>
      </>
    );
  }

  return (
    <Stack gap="xs" mt="xs">
      {staleNotice}
      {pendingComments.map((p) => (
        <div
          key={p.key}
          style={{
            background: 'var(--mantine-color-dark-6)',
            borderRadius: 6,
            padding: '0.5rem 0.75rem',
            borderLeft: `3px solid var(--mantine-color-${p.status === 'ERROR' ? 'red' : 'yellow'}-6)`,
            opacity: 0.85,
          }}
        >
          <Group gap="xs" mb={4}>
            {p.internal ? (
              <IconLock size={14} color="var(--mantine-color-orange-4)" />
            ) : (
              <IconMessage size={14} color="var(--mantine-color-blue-4)" />
            )}
            {p.technicianName && (
              <Text size="sm" fw={600} c="gray.2">
                {displayAuthorName(p.technicianName)}
              </Text>
            )}
            <Text size="xs" c="dimmed">
              {formatDate(p.createdAt)}
            </Text>
            {p.internal ? (
              <Badge size="xs" color="orange" variant="light">
                Intern
              </Badge>
            ) : (
              <Badge size="xs" color="blue" variant="light">
                Öffentlich — Kunde sieht ihn
              </Badge>
            )}
            <Tooltip
              label={
                p.status === 'ERROR'
                  ? 'Der Kommentar gehört zu einer Zeitbuchung, deren automatisches Nachbuchen aufgegeben wurde — ein Admin muss unter Sync-Verwaltung nachholen.'
                  : 'Der Kommentar gehört zu einer zwischengespeicherten Zeitbuchung und wird automatisch mitveröffentlicht, sobald Weclapp sie annimmt.'
              }
              multiline
              maw={360}
            >
              <Badge size="xs" variant="light" color={p.status === 'ERROR' ? 'red' : 'yellow'}>
                {p.status === 'ERROR' ? 'Nachbuchen fehlgeschlagen' : 'Wartet auf Weclapp'}
              </Badge>
            </Tooltip>
          </Group>
          <Text size={isMobile ? 'md' : 'sm'} style={{ whiteSpace: 'pre-wrap' }}>
            {p.comment}
          </Text>
        </div>
      ))}
      {comments.map((c: WeclappTicketComment) => (
        <div
          key={c.id}
          style={{
            background: 'var(--mantine-color-dark-6)',
            borderRadius: 6,
            padding: '0.5rem 0.75rem',
            // Wartende Kommentare bekommen denselben gelben Rand wie die wartenden
            // Zeitbuchungs-Kommentare darüber: Der Unterschied „steht in Weclapp" vs. „liegt
            // hier" ist wichtiger als „intern" vs. „öffentlich".
            borderLeft: c.pendingSync
              ? '3px solid var(--mantine-color-yellow-6)'
              : c.internal
                ? '3px solid var(--mantine-color-orange-6)'
                : '3px solid var(--mantine-color-blue-6)',
          }}
        >
          <Group gap="xs" mb={4}>
            {c.internal ? <IconLock size={14} color="var(--mantine-color-orange-4)" /> : <IconMessage size={14} color="var(--mantine-color-blue-4)" />}
            {c.createdByUserName && (
              <Text size="sm" fw={600} c="gray.2">
                {displayAuthorName(c.createdByUserName)}
              </Text>
            )}
            <Text size="xs" c="dimmed">
              {formatDate(c.createdDate)}
            </Text>
            {/* Intern war bisher das einzige Etikett — ein Kommentar ohne Etikett sah
                nach „nichts Besonderes" aus, obwohl ihn der Kunde liest. */}
            {c.internal ? (
              <Badge size="xs" color="orange" variant="light">
                Intern
              </Badge>
            ) : (
              <Badge size="xs" color="blue" variant="light">
                Öffentlich — Kunde sieht ihn
              </Badge>
            )}
            {/* KIAN-Antwortvorschlag: interner Kommentar, öffentlich stellen über die Karte oberhalb. */}
            {c.internal && isKianReplyProposalComment(c.comment) && (
              <Tooltip
                label="Diese Antwort hat KIAN vorgeschlagen. Sie ist intern — beim Kunden ist sie erst, wenn jemand sie oben unter „Antwortvorschlag von KIAN“ öffentlich stellt."
                multiline
                maw={360}
              >
                <Badge size="xs" color="grape" variant="light">
                  KIAN-Antwortvorschlag
                </Badge>
              </Tooltip>
            )}
            {/* Geschrieben, während Weclapp nicht erreichbar war. Ohne Kennzeichnung sähe der
                Kommentar aus wie jeder andere — und bei einem öffentlichen wäre das eine
                falsche Zusage: Der Kunde hat ihn noch nicht. */}
            {c.pendingSync && (
              <Tooltip
                label="Steht schon im Portal, in Weclapp noch nicht — wird automatisch übertragen, sobald Weclapp ihn annimmt. Bis dahin sieht ihn dort niemand."
                multiline
                maw={360}
              >
                <Badge size="xs" color="yellow" variant="light">
                  Wartet auf Weclapp
                </Badge>
              </Tooltip>
            )}
            {/* Erwähnungen aus Weclapp (recipientUsers). Wer nur im ServiceHub
                arbeitet, hat bisher nicht gesehen, dass er angesprochen wurde. */}
            {(c.mentionedUserNames ?? []).map((name) => (
              <Badge key={name} size="xs" color="grape" variant="light">
                @{displayAuthorName(name)}
              </Badge>
            ))}
          </Group>
          {/* Formatierte Fassung bevorzugen, sonst Klartext. Der Inhalt stammt aus Weclapp
              und wird deshalb IMMER durch DOMPurify geschickt — dasselbe Verfahren wie in
              EmailDescription, kein zweites erfinden. Weclapp liefert HTML teils auch im
              Klartext-Feld `comment` (<p>…&nbsp;</p>) — dann ebenfalls als HTML rendern. */}
          {c.htmlComment ? (
            <EmailDescription html={c.htmlComment} />
          ) : c.comment && (/<[a-z!/][^>]*>/i.test(c.comment) || /&(?:nbsp|amp|lt|gt|quot|#\d+);/i.test(c.comment)) ? (
            <EmailDescription html={c.comment} />
          ) : (
            <Text
              size={isMobile ? 'md' : 'sm'}
              c="gray.1"
              lh={1.55}
              style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
            >
              {c.comment}
            </Text>
          )}
        </div>
      ))}
    </Stack>
  );
}

/**
 * Eintrag im E-Mail-Archiv.
 *
 * `origin: 'ticket'` markiert die aus dem Ticket rekonstruierte Ursprungsmail — Weclapp
 * archiviert bei Mail2Ticket nicht jede Mail als `archivedEmail`, die erste Mail steckt
 * dann nur in der Ticketbeschreibung. Ohne diesen Eintrag stand im Archiv „keine
 * E-Mails", obwohl das Ticket sichtbar aus einer Mail entstanden ist — Absender und
 * Zeitpunkt waren nirgends zu sehen.
 */
type MailEntry = WeclappTicketMail & { origin?: 'ticket' };

/** Kopfzeile einer archivierten Mail — Von/An/CC/Datum, in Liste und Vorschau gleich. */
function mailPartyLine(m: WeclappTicketMail): string {
  // Bei eingehenden Mails auch den Empfänger: Weclapps Benachrichtigungen („Anfrage … wurde
  // erstellt") kommen alle von support@ — ohne „An" sahen zwei Mails an verschiedene
  // Empfänger wie eine doppelte aus.
  return m.direction === 'INCOMING'
    ? `Von: ${m.fromAddress ?? '?'}${m.toAddress ? ` · An: ${m.toAddress}` : ''}`
    : `An: ${m.toAddress ?? '?'}`;
}

/** Zeitpunkt einer Mail, wie ihn die Karte anzeigt — danach wird auch sortiert. */
function mailTime(m: WeclappTicketMail): number {
  const t = new Date(m.sentDate ?? m.receivedDate ?? '').getTime();
  return Number.isFinite(t) ? t : -Infinity;
}

/** Weclapp liefert Mail-Rümpfe mal als HTML, mal als Klartext — Erkennung wie bei Kommentaren. */
function mailBodyIsHtml(body: string): boolean {
  return /<[a-z!/][^>]*>/i.test(body) || /&(?:nbsp|amp|lt|gt|quot|#\d+);/i.test(body);
}

/**
 * Vollansicht einer archivierten Mail.
 *
 * Die Liste zeigt nur zwei Zeilen Anriss — bei Tickets, deren Beschreibung auf „siehe
 * Mails" verweist, steckt der eigentliche Sachverhalt aber genau dort. Der Klick auf
 * einen Eintrag öffnet deshalb die ganze Mail; mit Zurück/Weiter liest man den Thread
 * durch, ohne die Vorschau zu schließen.
 */
function MailPreviewModal({
  mails,
  index,
  onIndex,
  onClose,
}: {
  mails: MailEntry[];
  index: number | null;
  onIndex: (i: number) => void;
  onClose: () => void;
}) {
  const isMobile = useMediaQuery('(max-width: 48em)');
  const mail = index === null ? null : mails[index];
  const date = mail ? (mail.sentDate ?? mail.receivedDate) : undefined;
  const body = mail?.body?.trim() ?? '';

  return (
    <Modal
      opened={mail !== null}
      onClose={onClose}
      title={
        <Group gap="xs" wrap="nowrap">
          {mail?.direction === 'INCOMING'
            ? <IconMailOpened size={16} color="var(--mantine-color-blue-4)" />
            : <IconMail size={16} color="var(--mantine-color-gray-5)" />}
          <Text fw={600} lineClamp={1}>{mail?.subject ?? '(kein Betreff)'}</Text>
          {mail?.origin === 'ticket' && <OriginMailBadge />}
        </Group>
      }
      size="xl"
      zIndex={800}
      centered
      fullScreen={isMobile}
    >
      {mail && (
        <Stack gap="sm">
          <Stack gap={2}>
            {mail.fromAddress && (
              <Text size="xs" c="dimmed" style={{ wordBreak: 'break-word' }}>Von: {mail.fromAddress}</Text>
            )}
            {mail.toAddress && (
              <Text size="xs" c="dimmed" style={{ wordBreak: 'break-word' }}>An: {mail.toAddress}</Text>
            )}
            {mail.ccAddress && (
              <Text size="xs" c="dimmed" style={{ wordBreak: 'break-word' }}>CC: {mail.ccAddress}</Text>
            )}
            {date && <Text size="xs" c="dimmed">{formatDate(date)}</Text>}
          </Stack>
          <Divider />
          <ScrollArea.Autosize mah={isMobile ? '65vh' : '60vh'}>
            {body ? (
              <EmailDescription html={body} plain={!mailBodyIsHtml(body)} />
            ) : (
              <Text size="sm" c="dimmed">
                Zu dieser Mail ist im Weclapp-Archiv kein Inhalt gespeichert.
              </Text>
            )}
          </ScrollArea.Autosize>
          {mails.length > 1 && index !== null && (
            <Group justify="space-between" mt="xs">
              <Button
                size="xs"
                variant="light"
                leftSection={<IconChevronRight size={13} style={{ transform: 'rotate(180deg)' }} />}
                disabled={index === 0}
                onClick={() => onIndex(index - 1)}
              >
                Neuere
              </Button>
              <Text size="xs" c="dimmed">
                {index + 1} / {mails.length}
              </Text>
              <Button
                size="xs"
                variant="light"
                rightSection={<IconChevronRight size={13} />}
                disabled={index === mails.length - 1}
                onClick={() => onIndex(index + 1)}
              >
                Ältere
              </Button>
            </Group>
          )}
        </Stack>
      )}
    </Modal>
  );
}

/** Kennzeichnet die rekonstruierte Ursprungsmail, damit niemand sie für Archivdaten hält. */
function OriginMailBadge() {
  return (
    <Tooltip
      label="Aus dem Ticket rekonstruiert — Weclapp hat zu dieser Mail keinen Archiv-Eintrag."
      withArrow
      multiline
      w={260}
    >
      <Badge size="xs" color="grape" variant="light" style={{ flexShrink: 0 }}>
        Ursprungs-Mail
      </Badge>
    </Tooltip>
  );
}

/** Ein Eintrag im E-Mail-Archiv — Klick öffnet die Vollansicht. */
function MailCard({ mail, onOpen }: { mail: MailEntry; onOpen: () => void }) {
  const [hovered, setHovered] = useState(false);
  const date = mail.sentDate ?? mail.receivedDate;
  const isIncoming = mail.direction === 'INCOMING';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderRadius: 6,
        padding: '8px 10px',
        background: 'var(--mantine-color-default)',
        border: `1px solid ${hovered ? 'var(--mantine-color-blue-5)' : 'var(--mantine-color-default-border)'}`,
        cursor: 'pointer',
      }}
    >
      <Group gap="xs" mb={4} wrap="nowrap">
        {isIncoming
          ? <IconMailOpened size={12} color="var(--mantine-color-blue-4)" />
          : <IconMail size={12} color="var(--mantine-color-gray-5)" />}
        <Text size="xs" c="dimmed" style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {mailPartyLine(mail)}
        </Text>
        {date && (
          <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap' }}>
            {formatDate(date)}
          </Text>
        )}
        {mail.origin === 'ticket' && <OriginMailBadge />}
        <IconEye size={13} color={hovered ? 'var(--mantine-color-blue-4)' : 'var(--mantine-color-dimmed)'} />
      </Group>
      <Text size="sm" fw={500} lineClamp={1}>
        {mail.subject ?? '(kein Betreff)'}
      </Text>
      {mail.body && (
        <Text size="xs" c="dimmed" lineClamp={2} mt={2} style={{ whiteSpace: 'pre-wrap' }}>
          {mail.body.replace(/<[^>]+>/g, ' ').trim()}
        </Text>
      )}
    </div>
  );
}

function MailList({ ticketId }: { ticketId: string }) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['weclapp-cockpit', ticketId],
    queryFn: () => api.getWeclappTicketCockpit(ticketId),
    select: (d) => ({ mails: d.mails, ticket: d.ticket }),
    staleTime: 30000,
  });
  /** Index der geöffneten Mail in der Liste — null = Vorschau zu. */
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const mails = useMemo<MailEntry[]>(() => {
    // Neueste zuerst, nach dem Zeitpunkt, der auf der Karte steht. Der Spiegel sortierte nach
    // `sentDate` allein — Mails ohne `sentDate` (nur `receivedDate`) landeten dabei ganz oben
    // und die Liste sah durcheinander aus (10:30, 10:34, 14:44, 10:34).
    const byTimeDesc = (list: MailEntry[]) => [...list].sort((a, b) => mailTime(b) - mailTime(a));
    const archived: MailEntry[] = byTimeDesc(data?.mails ?? []);
    const ticket = data?.ticket;
    const sender = ticket?.senderEmail?.trim();
    if (!ticket || !sender) return archived;
    // Liegt die Ursprungsmail schon im Archiv, nicht doppelt zeigen.
    const alreadyArchived = archived.some((m) =>
      (m.fromAddress ?? '').toLowerCase().includes(sender.toLowerCase()),
    );
    if (alreadyArchived) return archived;
    const origin: MailEntry = {
      id: `ticket-origin-${ticket.id}`,
      origin: 'ticket',
      subject: ticket.subject,
      fromAddress: ticket.senderName ? `${ticket.senderName} <${sender}>` : sender,
      receivedDate: ticket.createdDate,
      direction: 'INCOMING',
      body: ticket.description,
    };
    return byTimeDesc([...archived, origin]);
  }, [data]);

  if (isLoading) {
    return (
      <Stack gap="xs" mt="xs">
        <Skeleton height={50} radius="sm" />
        <Skeleton height={50} radius="sm" />
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert color="red" variant="light" mt="xs">
        Fehler beim Laden der E-Mails.{' '}
        <Button size="compact-xs" variant="subtle" onClick={() => refetch()}>
          Erneut versuchen
        </Button>
      </Alert>
    );
  }

  if (mails.length === 0) {
    return (
      <Text size="sm" c="dimmed" mt="xs">
        Keine E-Mails vorhanden
      </Text>
    );
  }

  return (
    <Stack gap="xs" mt="xs">
      {mails.map((m: MailEntry, i: number) => (
        <MailCard key={m.id} mail={m} onOpen={() => setOpenIndex(i)} />
      ))}
      <MailPreviewModal
        mails={mails}
        index={openIndex}
        onIndex={setOpenIndex}
        onClose={() => setOpenIndex(null)}
      />
    </Stack>
  );
}

/**
 * Ticketnummer als Teilen-Schaltfläche.
 *
 * Ein Klick legt einen Deeplink in die Zwischenablage — Techniker schicken sich damit
 * Tickets zu, statt Nummern abzutippen. Der Link öffnet das Ticket direkt im Cockpit
 * (Anmeldung im Serviceportal vorausgesetzt) und trägt die Ticketnummer statt der
 * internen Weclapp-ID, damit man im Chat sieht, worum es geht.
 */
function TicketShareBadge({ ticketNumber, ticketId }: { ticketNumber?: string; ticketId: string }) {
  const [copied, setCopied] = useState(false);
  const ref = ticketNumber?.trim() || ticketId;
  const link = `${window.location.origin}/t/${encodeURIComponent(ref)}`;

  const copy = async () => {
    try {
      // navigator.clipboard gibt es nur in sicheren Kontexten — sonst der alte Weg
      // über ein temporäres Textfeld, sonst bliebe der Klick im Feld wirkungslos.
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        const el = document.createElement('textarea');
        el.value = link;
        el.style.position = 'fixed';
        el.style.opacity = '0';
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      setCopied(true);
      notifications.show({
        title: 'Link kopiert',
        message: `Deeplink zu ${ref} liegt in der Zwischenablage.`,
        color: 'teal',
      });
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      notifications.show({
        title: 'Kopieren fehlgeschlagen',
        message: link,
        color: 'red',
        autoClose: false,
      });
    }
  };

  return (
    <>
    <Tooltip label={copied ? 'Kopiert!' : 'Deeplink zu diesem Ticket kopieren'} withArrow>
      <Badge
        variant="gradient"
        gradient={{ from: 'var(--fb-orange, orange)', to: '#e65c00' }}
        style={{ fontFamily: 'monospace', flexShrink: 0, cursor: 'pointer' }}
        leftSection={copied ? <IconCheck size={11} /> : <IconShare2 size={11} />}
        onClick={copy}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            void copy();
          }
        }}
      >
        #{ticketNumber}
      </Badge>
    </Tooltip>
    {/* Im ServiceHub angelegt, von weclapp noch nicht angenommen (interne ID `local-…`). Ohne
        Kennzeichnung hielte man die S-Nummer für die endgültige — und der Kunde hat noch keine
        Eingangsbestätigung aus weclapp bekommen. */}
    {ticketId.startsWith('local-') && (
      <Tooltip
        label="Das Ticket ist im ServiceHub angelegt und wird automatisch in weclapp nachgereicht. Danach bekommt es die weclapp-Nummer; Links mit dieser Nummer funktionieren weiter."
        multiline
        maw={360}
      >
        <Badge size="sm" color="yellow" variant="light" style={{ flexShrink: 0 }}>
          Wartet auf weclapp
        </Badge>
      </Tooltip>
    )}
    </>
  );
}

/** Weclapp liefert Datumswerte mal als epoch-ms(-String), mal als ISO. */
function formatTicketDateTime(v: string): string {
  const n = Number(v);
  const d = Number.isFinite(n) && n > 1e12 ? new Date(n) : new Date(v);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Gegenstück zu formatTicketDateTime: gleicher Parser, aber als Date (für Übernahme in die Zeitbuchung). */
function parseTicketDateTime(v: string): Date | null {
  const n = Number(v);
  const d = Number.isFinite(n) && n > 1e12 ? new Date(n) : new Date(v);
  return isNaN(d.getTime()) ? null : d;
}


/** HTML → Klartext für das Pflichtfeld `comment` (Fallback für Ansichten ohne HTML). */
function AddCommentForm({
  ticketId,
  prefill,
  ticketInfo,
}: {
  ticketId: string;
  /** Für „Nachfassen, falls keine Antwort" beim externen Versand */
  ticketInfo?: { number?: string; subject?: string; partyId?: string | null; partyName?: string | null };
  /**
   * Vorbelegter Entwurf von außen (z. B. „Rückfrage zu offenen Punkten" aus der
   * KIAN-Optimierung). Neue Objekt-Identität = neuer Auftrag, damit dieselbe Rückfrage
   * auch ein zweites Mal geöffnet werden kann.
   */
  prefill?: { html: string; internal: boolean } | null;
}) {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const [opened, setOpened] = useState(false);
  const [html, setHtml] = useState('');
  // Technikerkommentare sind standardmäßig intern (nicht für Kunden sichtbar)
  const [isInternal, setIsInternal] = useState(true);
  const [mentionedUserIds, setMentionedUserIds] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  /**
   * Fassung vor dem letzten KIAN-Durchlauf. Solange sie gesetzt ist, steht im Editor ein
   * Entwurf, den der Techniker noch nicht freigegeben hat — er kann ihn bearbeiten,
   * tiefer laufen lassen oder verwerfen. Gesendet wird ausschließlich per Senden-Button.
   */
  const [kianPrevHtml, setKianPrevHtml] = useState<string | null>(null);
  const [kianMode, setKianMode] = useState<'STANDARD' | 'DEEP' | null>(null);
  /** Rückfrage vor dem externen Versand — inklusive Empfängerliste. */
  const [confirmExternalOpen, setConfirmExternalOpen] = useState(false);
  /** Nachfassen, falls der Kunde bis dahin nicht antwortet — gewählt im Bestätigungsfenster. */
  const [followUpOn, setFollowUpOn] = useState(false);
  const [followUpDate, setFollowUpDate] = useState<string | null>(() => workdaysAheadYmd(2));

  // Prefill von außen: Composer öffnen und Entwurf setzen. Gesendet wird nichts —
  // der Techniker formuliert weiter und drückt selbst auf Senden.
  useEffect(() => {
    if (!prefill) return;
    setHtml(prefill.html);
    setIsInternal(prefill.internal);
    setKianPrevHtml(null);
    setOpened(true);
  }, [prefill]);

  const cockpitKey = ['weclapp-cockpit', ticketId];

  // Für die Erwähnungs-Auswahl. Der Editor kann kein Inline-@ wie das frühere Textfeld,
  // deshalb hier eine explizite Auswahl — die Benachrichtigung bleibt damit erhalten.
  const { data: techData } = useQuery({
    queryKey: ['portal-technicians'],
    queryFn: () => api.getPortalTechnicians(),
    staleTime: 120_000,
    enabled: opened,
  });
  const technicianOptions = (techData?.technicians ?? [])
    .filter((t) => !t.isCurrentUser)
    .map((t) => ({ value: t.id, label: t.displayName }));

  /** Idempotenz-Referenz: eine pro ausgefülltem Formular (Doppelklick-/Retry-Schutz). */
  const commentExtRef = useRef<string>(crypto.randomUUID());

  // Angefangenen Kommentar beim Cockpit-Schließen nicht stumm verwerfen. Der Text
  // überlebt das Schließen des Composer-Modals (State liegt hier) — verloren geht er
  // erst, wenn der ganze Drawer zugeht; genau davor fragt der Drawer jetzt nach.
  const setDraft = useUnsavedDrafts((s) => s.setDraft);
  // Abhängigkeit ist bewusst NUR das Ja/Nein, nicht der Text: mit `html` in den Deps
  // lief bei jedem Tastendruck erst das Cleanup (Draft löschen) und dann das Setzen —
  // zwei Store-Änderungen pro Zeichen, die das komplette Cockpit neu rendern.
  const hasCommentDraft = useMemo(() => htmlToPlainText(html).trim().length > 0, [html]);
  useEffect(() => {
    const key = `comment:${ticketId}`;
    setDraft(key, hasCommentDraft ? 'angefangener Kommentar' : null);
    return () => setDraft(key, null);
  }, [hasCommentDraft, ticketId, setDraft]);

  const resetForm = () => {
    setHtml('');
    setIsInternal(true);
    setMentionedUserIds([]);
    setKianPrevHtml(null);
    setKianMode(null);
    commentExtRef.current = crypto.randomUUID();
  };

  const mutation = useMutation({
    mutationFn: (vars: {
      text: string;
      htmlComment: string;
      internal: boolean;
      mentions: string[];
      /** 'JJJJ-MM-TT' — Nachfass-Erinnerung nach dem Versand */
      followUp?: string | null;
    }) =>
      api.addWeclappTicketComment(ticketId, {
        comment: vars.text,
        htmlComment: vars.htmlComment,
        internal: vars.internal,
        mentionedUserIds: vars.mentions,
        externalReference: commentExtRef.current,
      }),
    // Optimistic Update: Kommentar sofort in der gebündelten Cockpit-Liste anzeigen.
    onMutate: async (vars) => {
      await queryClient.cancelQueries({ queryKey: cockpitKey });
      const previous = queryClient.getQueryData<CockpitData>(cockpitKey);
      const optimistic: WeclappTicketComment = {
        id: `optimistic-${Date.now()}`,
        comment: vars.text,
        htmlComment: vars.htmlComment,
        internal: vars.internal,
        createdDate: new Date().toISOString(),
        createdByUserName: user?.displayName,
      };
      queryClient.setQueryData<CockpitData>(cockpitKey, (old) =>
        old ? { ...old, comments: [optimistic, ...old.comments] } : old
      );
      setConfirmExternalOpen(false);
      setOpened(false);
      resetForm();
      return { previous };
    },
    onError: (e: Error, _vars, context) => {
      // Rollback auf den Stand vor dem optimistischen Update.
      if (context?.previous) queryClient.setQueryData(cockpitKey, context.previous);
      notifications.show({ title: 'Fehler', message: e.message, color: 'red' });
    },
    onSuccess: (res, vars) => {
      showTicketUpdateNotice(
        res,
        'Kommentar gespeichert',
        '',
        'Weclapp ist derzeit nicht erreichbar. Der Kommentar liegt im Portal und wird automatisch übertragen, sobald es wieder antwortet.',
      );
      if (vars.followUp) {
        const subject = ticketInfo?.subject ?? ticketInfo?.number ?? 'Ticket';
        api
          .createTodo({
            title: `Nachfassen: ${subject}`.slice(0, 300),
            due: { date: vars.followUp, time: null },
            source: 'TICKET',
            resolveWhen: 'CUSTOMER_REPLIED',
            links: {
              weclappTicket: {
                id: ticketId,
                number: ticketInfo?.number ?? null,
                subject: ticketInfo?.subject ?? null,
                partyId: ticketInfo?.partyId ?? null,
                partyName: ticketInfo?.partyName ?? null,
              },
            },
          })
          .then(() => {
            void queryClient.invalidateQueries({ queryKey: ['todos'] });
            notifications.show({
              title: 'Nachfassen gesetzt',
              message: 'Du wirst erinnert — außer der Kunde antwortet vorher.',
              color: 'teal',
            });
          })
          .catch((e: Error) => notifications.show({ title: 'Nachfassen nicht gesetzt', message: e.message, color: 'red' }));
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: cockpitKey });
    },
  });

  /**
   * KIAN-Entwurf. Steht schon Text im Feld, wird er umformuliert; ist es leer, schlägt
   * KIAN anhand des Tickets etwas vor. Das Ergebnis ersetzt den Editorinhalt — die
   * vorherige Fassung bleibt in `kianPrevHtml`, damit „Zurück" nichts kostet.
   */
  const draftMutation = useMutation({
    mutationFn: (vars: { mode: 'STANDARD' | 'DEEP'; intent?: 'AUTO' | 'POLISH' | 'INSTRUCT' }) =>
      api.draftWeclappTicketComment(ticketId, {
        draft: htmlToPlainText(html) || undefined,
        // HTML mitgeben: so bleiben Stichpunkte/Absätze des Technikers beim
        // Umformulieren erhalten, statt zu Fließtext plattgebügelt zu werden.
        draftHtml: htmlToPlainText(html).trim() ? html : undefined,
        internal: isInternal,
        mode: vars.mode,
        intent: vars.intent ?? 'AUTO',
      }),
    onSuccess: (res, vars) => {
      // Beim Nachschärfen (Deep auf einen Entwurf) bleibt das ursprüngliche Original
      // erhalten — sonst führte „Zurück" nur zum vorherigen KIAN-Entwurf.
      setKianPrevHtml((prev) => prev ?? html);
      setKianMode(vars.mode);
      setHtml(res.html || plainTextToHtml(res.text));
      queryClient.invalidateQueries({ queryKey: ['kian-assist-usage'] });
    },
    onError: (e: Error) => {
      // Auch der abgelehnte Aufruf ändert den Stand (429 nennt den Verbrauch).
      queryClient.invalidateQueries({ queryKey: ['kian-assist-usage'] });
      notifications.show({ title: 'KIAN-Entwurf fehlgeschlagen', message: e.message, color: 'red' });
    },
  });

  const restoreBeforeKian = () => {
    if (kianPrevHtml === null) return;
    setHtml(kianPrevHtml);
    setKianPrevHtml(null);
    setKianMode(null);
  };

  /**
   * Bild einfügen: als Ticket-Dokument hochladen und als <img> auf die Download-Route
   * des Portals verweisen. Bewusst KEINE base64-Data-URI im Kommentar — die würde den
   * Kommentartext um ein Vielfaches aufblähen und in Weclapp unbrauchbar machen.
   */
  const handleImage = async (file: File | null) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      notifications.show({ title: 'Nur Bilder', message: 'Bitte eine Bilddatei wählen.', color: 'red' });
      return;
    }
    setUploading(true);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result).split(',')[1] ?? '');
        reader.onerror = () => reject(new Error('Datei konnte nicht gelesen werden'));
        reader.readAsDataURL(file);
      });
      // Originaldatei immer als Dokument ans Ticket — sie bleibt vollständig und
      // in voller Auflösung erhalten.
      await api.uploadWeclappAttachment(ticketId, {
        base64,
        filename: file.name,
        mimeType: file.type,
      });

      // Zusätzlich eine verkleinerte Kopie in den Text einbetten. Bewusst als
      // data-URI statt als Verweis auf unsere Download-Route: die funktioniert nur
      // mit passender Anmeldung und löst in Weclapps eigener Oberfläche ins Leere.
      const inline = await buildInlineImage(file);
      if (inline.dataUri) {
        setHtml(
          (prev) => `${prev}<p><img src="${inline.dataUri}" alt="${safeAltText(file.name)}" /></p>`,
        );
      } else {
        notifications.show({
          title: 'Als Datei angehängt',
          message: inline.reason ?? 'Vorschau im Text nicht möglich.',
          color: 'yellow',
        });
      }
      queryClient.invalidateQueries({ queryKey: cockpitKey });
    } catch (err) {
      notifications.show({
        title: 'Upload fehlgeschlagen',
        message: err instanceof Error ? err.message : 'Unbekannter Fehler',
        color: 'red',
      });
    } finally {
      setUploading(false);
    }
  };

  const plain = htmlToPlainText(html);

  const handleSend = () => {
    if (!plain) return;
    // Externe Kommentare werden vom Kunden gesehen (und i. d. R. per E-Mail zugestellt) —
    // vor versehentlichem Versand absichern. Die Rückfrage listet auf, wer am Ticket hängt.
    if (!isInternal) {
      setConfirmExternalOpen(true);
      return;
    }
    submit();
  };

  const submit = () => {
    // Erwähnte Namen zusätzlich in den Text schreiben, damit der Kommentar auch in
    // Weclapp lesbar bleibt — dort gibt es die Erwähnungs-Metadaten nicht.
    const mentionNames = technicianOptions
      .filter((o) => mentionedUserIds.includes(o.value))
      .map((o) => `@${o.label}`)
      .join(' ');
    const suffix = mentionNames ? `\n\n${mentionNames}` : '';
    const htmlSuffix = mentionNames ? `<p>${mentionNames}</p>` : '';

    mutation.mutate({
      text: `${plain}${suffix}`,
      htmlComment: `${html}${htmlSuffix}`,
      internal: isInternal,
      mentions: mentionedUserIds,
      followUp: !isInternal && followUpOn ? followUpDate : null,
    });
    setFollowUpOn(false);
    setFollowUpDate(workdaysAheadYmd(2));
  };

  return (
    <>
      <Button
        variant="light"
        leftSection={<IconMessagePlus size={16} />}
        onClick={() => setOpened(true)}
        mt="sm"
      >
        Kommentar schreiben
      </Button>

      {/* Composer im Modal statt inline: im Flyout ist zu wenig Platz für einen Editor
          mit Werkzeugleiste, und das Schreiben soll die Kommentarliste nicht verdrängen. */}
      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title="Kommentar schreiben"
        size="lg"
        zIndex={700}
        centered
      >
        <Stack gap="sm">
          <WysiwygEditor
            value={html}
            onChange={setHtml}
            placeholder="Kommentar eingeben… (Screenshots lassen sich direkt einfügen)"
            minHeight={180}
            onImagePaste={handleImage}
          />

          <Group gap="xs" align="flex-end">
            <FileButton onChange={handleImage} accept="image/*">
              {(props: Record<string, unknown>) => (
                <Button
                  {...props}
                  size="xs"
                  variant="light"
                  leftSection={<IconPhoto size={14} />}
                  loading={uploading}
                >
                  Bild einfügen
                </Button>
              )}
            </FileButton>
            <MultiSelect
              label="Erwähnen"
              placeholder={technicianOptions.length ? 'Techniker wählen…' : 'Lade…'}
              data={technicianOptions}
              value={mentionedUserIds}
              onChange={setMentionedUserIds}
              searchable
              clearable
              maxValues={10}
              size="xs"
              style={{ flex: 1, minWidth: 200 }}
              comboboxProps={{ withinPortal: true, zIndex: 1100 }}
            />
          </Group>

          {kianPrevHtml !== null && (
            <Alert color="violet" variant="light" p="xs" icon={<IconSparkles size={16} />}>
              <Group justify="space-between" wrap="nowrap" gap="xs">
                <Text size="xs">
                  <strong>KIAN-Entwurf{kianMode === 'DEEP' ? ' (Deep)' : ''}</strong> — bitte prüfen
                  und bei Bedarf anpassen. Gesendet wird erst mit „
                  {isInternal ? 'Senden' : 'An Kunde senden'}“.
                </Text>
                <Button
                  size="compact-xs"
                  variant="subtle"
                  color="gray"
                  onClick={restoreBeforeKian}
                  disabled={draftMutation.isPending}
                  style={{ flexShrink: 0 }}
                >
                  Zurück
                </Button>
              </Group>
            </Alert>
          )}

          {!isInternal && (
            <Alert color="red" variant="light" p="xs" icon={<IconAlertTriangle size={16} />}>
              <Stack gap={8}>
                <Text size="xs">
                  Externer Kommentar — wird dem <strong>Kunden</strong> zugestellt (E-Mail).
                </Text>
                <ExternalRecipientsList ticketId={ticketId} />
              </Stack>
            </Alert>
          )}

          {!user?.hasWeclappApiToken && (
            <Text size="xs" c="dimmed">
              Hinweis: Ohne eigenen Weclapp-API-Token wird der Kommentar technisch unter dem
              API-Benutzer angelegt — dein Name wird automatisch im Kommentar vermerkt.
            </Text>
          )}

          <Group justify="space-between">
            <Switch
              label={isInternal ? 'Intern' : 'Extern (Kunde)'}
              checked={isInternal}
              onChange={(e) => setIsInternal(e.currentTarget.checked)}
              size="sm"
              color={isInternal ? 'blue' : 'red'}
            />
            <Group gap="xs">
              <KianAssistUsageHint />
              <Button variant="subtle" color="gray" onClick={() => setOpened(false)}>
                Abbrechen
              </Button>
              {/* Zweiter Weg neben „Senden“: KIAN bringt den Text in Form bzw. schlägt
                  bei leerem Feld einen vor — Tonfall richtet sich nach intern/extern. */}
              <Button.Group>
                <Button
                  variant="light"
                  color="violet"
                  leftSection={<IconSparkles size={14} />}
                  loading={draftMutation.isPending}
                  disabled={uploading || mutation.isPending}
                  onClick={() => draftMutation.mutate({ mode: 'STANDARD' })}
                >
                  {plain ? 'KIAN optimieren' : 'KIAN vorschlagen'}
                </Button>
                <Menu position="top-end" withinPortal zIndex={1100}>
                  <Menu.Target>
                    <Button
                      variant="light"
                      color="violet"
                      px={6}
                      disabled={uploading || mutation.isPending || draftMutation.isPending}
                      aria-label="Weitere KIAN-Optionen"
                    >
                      <IconChevronDown size={14} />
                    </Button>
                  </Menu.Target>
                  <Menu.Dropdown>
                    {plain && (
                      <>
                        <Menu.Item
                          leftSection={<IconSparkles size={14} />}
                          onClick={() => draftMutation.mutate({ mode: 'STANDARD', intent: 'INSTRUCT' })}
                        >
                          Als Anweisung ausformulieren (Ticketkontext nutzen)
                        </Menu.Item>
                        <Menu.Item
                          leftSection={<IconSparkles size={14} />}
                          onClick={() => draftMutation.mutate({ mode: 'STANDARD', intent: 'POLISH' })}
                        >
                          Nur in Form bringen (Inhalt unangetastet)
                        </Menu.Item>
                        <Menu.Divider />
                      </>
                    )}
                    <Menu.Item
                      leftSection={<IconSparkles size={14} />}
                      onClick={() => draftMutation.mutate({ mode: 'DEEP' })}
                    >
                      KIAN Deep {plain ? '(gründlicher umformulieren)' : '(gründlicher vorschlagen)'}
                    </Menu.Item>
                    {kianPrevHtml !== null && (
                      <Menu.Item leftSection={<IconArrowBackUp size={14} />} onClick={restoreBeforeKian}>
                        Eigene Fassung wiederherstellen
                      </Menu.Item>
                    )}
                  </Menu.Dropdown>
                </Menu>
              </Button.Group>
              <Button
                color={isInternal ? undefined : 'red'}
                leftSection={<IconSend size={14} />}
                loading={mutation.isPending}
                disabled={!plain || uploading || draftMutation.isPending}
                onClick={handleSend}
              >
                {isInternal ? 'Senden' : 'An Kunde senden'}
              </Button>
            </Group>
          </Group>
        </Stack>
      </Modal>

      {/* Rückfrage vor dem externen Versand. Bewusst ein Modal statt window.confirm:
          der Browser-Dialog kann keine Empfängerliste zeigen und blockiert den Tab. */}
      <Modal
        opened={confirmExternalOpen}
        onClose={() => setConfirmExternalOpen(false)}
        title={<Text fw={600}>An Kunde senden?</Text>}
        size="md"
        zIndex={800}
        centered
      >
        <Stack gap="md">
          <Alert color="red" variant="light" p="xs" icon={<IconAlertTriangle size={16} />}>
            <Text size="sm">
              Dieser Kommentar ist <strong>extern</strong> und wird dem Kunden zugestellt (E-Mail).
            </Text>
          </Alert>
          <ExternalRecipientsList ticketId={ticketId} />
          <Group gap="xs" align="center" wrap="nowrap">
            <Checkbox
              label="Nachfassen, falls keine Antwort bis"
              checked={followUpOn}
              onChange={(e) => {
                const __checked = e.currentTarget.checked;
                setFollowUpOn(__checked);
              }}
            />
            <DateInput
              size="xs"
              w={130}
              dateParser={parseGermanDate}
              valueFormat="DD.MM.YYYY"
              value={followUpDate}
              onChange={(v) => {
                setFollowUpDate(v);
                if (v) setFollowUpOn(true);
              }}
              minDate={new Date()}
              popoverProps={{ zIndex: 900 }}
            />
          </Group>
          {followUpOn && (
            <Text size="xs" c="dimmed" mt={-8}>
              Erinnert dich an dem Tag — antwortet der Kunde vorher (Mail oder Kundenportal) oder wird das Ticket
              geschlossen, entfällt die Erinnerung.
            </Text>
          )}
          <Group justify="flex-end" gap="sm">
            <Button variant="subtle" color="gray" onClick={() => setConfirmExternalOpen(false)}>
              Abbrechen
            </Button>
            <Button
              color="red"
              leftSection={<IconSend size={16} />}
              loading={mutation.isPending}
              onClick={submit}
            >
              An Kunde senden
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

function formatHours(h: number): string {
  const totalMin = Math.round(h * 60);
  const hh = Math.floor(totalMin / 60);
  const mm = totalMin % 60;
  return mm === 0 ? `${hh} h` : `${hh} h ${mm} min`;
}

/** Abrechnungstakt: Weclapp bekommt volle Viertelstunden — erfasst wird minutengenau. */
function roundUpToQuarterHours(h: number): number {
  const min = Math.round(h * 60);
  return Math.ceil(min / 15) * 15 / 60;
}

/** Jetzt, auf die Minute abgeschnitten — Vorgabe fürs Ende einer Buchung. */
function nowToMinute(): Date {
  const d = new Date();
  d.setSeconds(0, 0);
  return d;
}

function toDateTimeLocalValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function AddTimeEntryForm({
  ticketId,
  prefillStart,
}: {
  ticketId: string;
  /** Aus Verlauf/Kopfzeile übernommener Zeitstempel — wird als Startzeit gesetzt. */
  prefillStart?: Date | null;
}) {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [hours, setHours] = useState(1);
  /**
   * Das Ende ist der Anker: Gebucht wird meist direkt nach der Arbeit, das Ende ist also
   * „jetzt". Dauer ändern (Plus/Minus oder eingetippt) verschiebt deshalb den START nach
   * vorn, und die Aufrundung auf den Viertelstunden-Takt legt der Server ebenfalls vor den
   * Start (`endDate` statt `startDate`, routes/weclapp.ts). Bis v1.360.1 hing alles am
   * Start — jede Verlängerung und jede Aufrundung schob das Ende in die Zukunft.
   */
  const [endDate, setEndDate] = useState<Date>(() => nowToMinute());
  /** Eingabefeld der Dauer — frei tippbar, gelesen beim Verlassen/Enter (parseDurationInput). */
  const [hoursInput, setHoursInput] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  /** weclapp-User-ID des Ziel-Technikers — Standard: der Angemeldete. */
  const [technicianId, setTechnicianId] = useState<string | null>(null);
  /**
   * Idempotenz-Referenz: EINE pro ausgefülltem Formular. Doppelklick oder
   * Timeout-Retry mit derselben Referenz erzeugt serverseitig keine zweite Buchung.
   */
  const extRef = useRef<string>(crypto.randomUUID());

  // Angefangenen Zeiteintrag beim Cockpit-Schließen nicht stumm verwerfen.
  const setDraft = useUnsavedDrafts((s) => s.setDraft);
  // Nur das Ja/Nein in den Deps (siehe Kommentar-Formular): sonst schrieb jeder
  // Tastendruck zweimal in den Store und rendert das ganze Cockpit samt
  // Beschreibung neu — das war der springende Bildschirm beim Tippen.
  const hasTimeDraft = comment.trim().length > 0;
  useEffect(() => {
    const key = `time-entry:${ticketId}`;
    setDraft(key, hasTimeDraft ? 'angefangener Zeiteintrag' : null);
    return () => setDraft(key, null);
  }, [hasTimeDraft, ticketId, setDraft]);

  const { data: techData } = useQuery({
    queryKey: ['portal-technicians'],
    queryFn: () => api.getPortalTechnicians(),
    staleTime: 120_000,
  });
  const technicianOptions = (techData?.technicians ?? [])
    .filter((t) => t.weclappUserId)
    .map((t) => ({
      value: t.weclappUserId as string,
      label: t.isCurrentUser ? `${t.displayName} (ich)` : t.displayName,
    }));
  const effectiveTechnicianId = technicianId ?? user?.weclappUserId ?? null;
  const bookingForOther = !!effectiveTechnicianId && effectiveTechnicianId !== user?.weclappUserId;
  // Techniker buchen nicht selbst für Kollegen (v1.404.0): Der Server legt dann einen Zeitvorschlag an.
  const sendsProposal = bookingForOther && !mayBookForOthers(user?.role);
  const otherName =
    technicianOptions.find((o) => o.value === effectiveTechnicianId)?.label ?? 'den Kollegen';

  useEffect(() => {
    setEndDate(nowToMinute());
    extRef.current = crypto.randomUUID();
  }, [ticketId]);

  const startDate = useMemo(() => {
    return new Date(endDate.getTime() - Math.round(hours * 3600 * 1000));
  }, [endDate, hours]);

  /** Start setzen, Ende bleibt: die Dauer ergibt sich daraus. */
  const applyStart = (d: Date) => {
    const diffH = (endDate.getTime() - d.getTime()) / 3_600_000;
    if (diffH > 0 && diffH <= 24) {
      setHours(Math.max(1 / 60, Math.round(diffH * 60) / 60));
    } else {
      // Start nach dem Ende oder mehr als 24 h davor: Dauer behalten, Ende mitziehen.
      setEndDate(new Date(d.getTime() + Math.round(hours * 3600 * 1000)));
    }
  };

  // Übernommener Zeitstempel (Klick im Verlauf oder auf Erstellt/aktualisiert im Kopf):
  // exakt übernehmen, NICHT auf Viertelstunde runden — der Sinn ist ja der genaue Moment.
  // Er wird zum Start, das Ende bleibt „jetzt".
  useEffect(() => {
    if (prefillStart) applyStart(prefillStart);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nur auf einen neuen Zeitstempel reagieren
  }, [prefillStart]);

  const commitHoursInput = () => {
    if (hoursInput === null) return;
    const parsed = parseDurationInput(hoursInput);
    if (parsed !== null) setHours(Math.min(24, parsed));
    else
      notifications.show({
        title: 'Dauer nicht erkannt',
        message: 'Beispiele: 45 (Minuten), 2 (Stunden), 1:15, 1,5 h, 1h 30',
        color: 'orange',
      });
    setHoursInput(null);
  };

  const canSubmit = comment.trim().length > 0;

  // Voraussetzungen vorher klären statt in die Warteliste laufen: Fehlen Kunde oder
  // Bearbeiter, fragt der Assistent sie ab und bucht danach.
  const { data: gapTicket } = useQuery({
    queryKey: ['weclapp-cockpit', ticketId],
    queryFn: () => api.getWeclappTicketCockpit(ticketId),
    select: (d) => d.ticket,
    staleTime: 30000,
  });
  const bookingGaps = ticketGaps(gapTicket, { customer: true, assignee: true });
  const [gapsOpen, setGapsOpen] = useState(false);

  // Ergebnis bleibt im Panel stehen. Eine Toast-Meldung ist weg, bevor man sie liest —
  // beim Buchen sah es dann so aus, als sei nichts passiert (Formular blieb offen).
  const [result, setResult] = useState<{
    ok: boolean;
    bookedHours?: number;
    /** Minutengenau erfasst — weicht bei Aufrundung von bookedHours ab. */
    actualHours?: number;
    error?: string;
    /** Gebucht, aber auf den API-Benutzer statt auf den Techniker geschrieben. */
    warning?: string;
    /** Weclapp hat abgelehnt — Buchung ist lokal geparkt und wird automatisch nachgebucht. */
    pending?: boolean;
    pendingMessage?: string;
    /** Nicht gebucht: Zeitvorschlag an einen Kollegen (v1.404.0) */
    proposedTo?: string;
  } | null>(null);

  useEffect(() => {
    setResult(null);
  }, [ticketId]);

  // KIAN-Optimierer für den Buchungstext: ersetzt den Feldinhalt, Original bleibt
  // für „wiederherstellen" erhalten. Gepostet wird erst mit „Zeit buchen".
  const [commentBeforeKian, setCommentBeforeKian] = useState<string | null>(null);
  const [alsoInternal, setAlsoInternal] = useState(false);
  const [alsoPublic, setAlsoPublic] = useState(false);
  // Leistungsart: null = Standard-Artikel (Einstellungen → Weclapp). Auswahl nur,
  // wenn Alternativen freigegeben sind.
  const [articleId, setArticleId] = useState<string | null>(null);
  const bookingArticles = useBookingArticles();

  const kianDraftMutation = useMutation({
    mutationFn: () =>
      api.draftWeclappTicketComment(ticketId, {
        draft: comment.trim() || undefined,
        internal: false,
        usage: 'TIME_ENTRY',
      }),
    onSuccess: (res) => {
      setCommentBeforeKian(comment);
      setComment(res.text);
    },
    onError: (e: Error) =>
      notifications.show({ title: 'KIAN-Entwurf fehlgeschlagen', message: e.message, color: 'red' }),
  });

  const mutation = useMutation({
    mutationFn: () =>
      api.addWeclappTimeEntry(ticketId, {
        hours,
        comment: comment.trim(),
        // Das Ende ist fest, der Server rechnet den Start (samt Aufrundung) davor.
        endDate: endDate.getTime(),
        ...(effectiveTechnicianId && effectiveTechnicianId !== user?.weclappUserId
          ? { technicianWeclappUserId: effectiveTechnicianId }
          : {}),
        externalReference: extRef.current,
        ...(alsoInternal || alsoPublic
          ? { alsoComment: { internal: alsoInternal, public: alsoPublic } }
          : {}),
        ...(articleId && articleId !== bookingArticles.defaultArticleId ? { articleId } : {}),
      }),
    onSuccess: (res) => {
      setResult(
        res?.proposed
          ? { ok: true, bookedHours: hours, proposedTo: res.proposal?.recipientName ?? otherName }
          : res?.pending
          ? { ok: true, bookedHours: hours, pending: true, pendingMessage: res.message }
          : { ok: true, bookedHours: res?.bookedHours ?? roundUpToQuarterHours(hours), actualHours: hours, warning: res?.warning }
      );
      setHours(1);
      setEndDate(nowToMinute());
      setHoursInput(null);
      setComment('');
      setCommentBeforeKian(null);
      setAlsoInternal(false);
      setAlsoPublic(false);
      setTechnicianId(null);
      setArticleId(null);
      // Neue Referenz erst NACH Erfolg — Retries desselben Formulars bleiben idempotent.
      // Auch bei `pending`: die Buchung ist sicher in der Warteliste gespeichert.
      extRef.current = crypto.randomUUID();
      queryClient.invalidateQueries({ queryKey: ['weclapp-cockpit', ticketId] });
      if (res?.proposed) {
        queryClient.invalidateQueries({ queryKey: TIME_PROPOSALS_QUERY_KEY });
        notifications.show({
          title: 'Zeitvorschlag geschickt',
          message: `${res.proposal?.recipientName ?? otherName} bekommt eine Push-Nachricht und gibt frei.`,
          color: 'grape',
        });
        return;
      }
      notifications.show(
        res?.pending
          ? {
              title: 'Zeit zwischengespeichert',
              message: 'Weclapp hat abgelehnt — die Buchung wird automatisch nachgebucht.',
              color: 'blue',
            }
          : {
              title: 'Zeit gebucht',
              message: res?.warning ? 'Hinweis zur Zuordnung im Panel beachten.' : '',
              color: res?.warning ? 'yellow' : 'green',
            }
      );
    },
    onError: (e: Error) => {
      setResult({ ok: false, error: e.message });
      notifications.show({ title: 'Fehler', message: e.message, color: 'red' });
    },
  });

  if (!user?.weclappUserId) {
    return (
      <Alert color="yellow" variant="light" mt="sm">
        Keine Weclapp User-ID hinterlegt. Bitte Admin kontaktieren.
      </Alert>
    );
  }

  return (
    <Stack gap="xs" mt="sm">
      {/* Nicht eingestempelt? Nur ein Hinweis mit Knopf, keine Sperre (Anwesenheit, v1.405.0). */}
      <ClockInHint context="booking" />
      {/* Frei wählbarer Techniker — Standard: der Angemeldete. Die Buchung läuft dann
          über den globalen Token (Kaskade), nie über den fremden persönlichen Token. */}
      <Select
        label="Buchung als"
        size="xs"
        data={technicianOptions}
        value={effectiveTechnicianId}
        onChange={(v) => setTechnicianId(v)}
        searchable
        allowDeselect={false}
        comboboxProps={{ withinPortal: true, zIndex: 1100 }}
      />
      {sendsProposal ? (
        <Text size="xs" c="grape">
          {proposalHint(otherName)}
        </Text>
      ) : bookingForOther && (
        <Text size="xs" c="orange">
          Buchung für eine andere Person — sie erscheint in Weclapp unter deren Namen
          (bzw. mit Vermerk, falls Weclapp die Fremdbuchung ablehnt).
        </Text>
      )}

      {result && (
        <Alert
          color={result.ok ? (result.proposedTo ? 'grape' : result.pending ? 'blue' : result.warning ? 'yellow' : 'green') : 'red'}
          variant="light"
          withCloseButton
          onClose={() => setResult(null)}
          icon={result.ok && !result.warning && !result.pending ? <IconCheck size={16} /> : <IconAlertTriangle size={16} />}
        >
          {result.ok && result.proposedTo ? (
            <Stack gap={4}>
              <Text size="sm" fw={600}>
                Nicht gebucht — Zeitvorschlag über {formatHours(result.bookedHours ?? 0)} an {result.proposedTo} geschickt.
              </Text>
              <Text size="xs" c="dimmed">
                Gebucht wird erst, wenn {result.proposedTo} freigibt, und dann unter eigenem Namen. Offene
                Vorschläge stehen unter „Zeitvorschläge" (Von mir); dort kannst du ihn auch zurückziehen.
              </Text>
            </Stack>
          ) : result.ok && result.pending ? (
            <Stack gap={4}>
              <Text size="sm" fw={600}>
                {formatHours(result.bookedHours ?? 0)} zwischengespeichert — wird automatisch nachgebucht.
              </Text>
              <Text size="xs" c="dimmed">
                Weclapp hat die Buchung gerade abgelehnt. Sie ist sicher im Portal gespeichert und
                wird automatisch nachgebucht, sobald Weclapp sie annimmt — bitte NICHT erneut buchen.
                Admins sehen die Warteliste unter Sync-Verwaltung.
              </Text>
              {result.pendingMessage && (
                <Text size="xs" c="dimmed" style={{ fontFamily: 'var(--mantine-font-family-monospace)' }}>
                  {result.pendingMessage}
                </Text>
              )}
            </Stack>
          ) : result.ok ? (
            <Stack gap={4}>
              <Text size="sm">
                {formatHours(result.bookedHours ?? 0)} als Leistungsnachweis in Weclapp gebucht.
                {result.actualHours != null && Math.abs(result.actualHours - (result.bookedHours ?? 0)) > 0.001
                  ? ` Erfasst: ${formatHours(result.actualHours)} — auf Viertelstunde aufgerundet.`
                  : ''}
              </Text>
              {result.warning && (
                <Text size="xs" c="dimmed">
                  {result.warning}
                </Text>
              )}
            </Stack>
          ) : (
            <Stack gap={4}>
              <Text size="sm" fw={600}>Zeit wurde NICHT gebucht.</Text>
              <Text
                size="xs"
                style={{
                  fontFamily: 'var(--mantine-font-family-monospace)',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  userSelect: 'text',
                }}
              >
                {result.error}
              </Text>
            </Stack>
          )}
        </Alert>
      )}

      {/* Stunden-Stepper */}
      <div>
        <Text size="xs" fw={500} mb={4}>Dauer</Text>
        <Group gap="xs" align="center">
          <ActionIcon
            variant="default"
            size="md"
            disabled={hours <= 0.25}
            onClick={() => setHours((h) => Math.max(0.25, Math.round((h - 0.25) * 60) / 60))}
          >
            <IconMinus size={14} />
          </ActionIcon>
          <TextInput
            size="sm"
            w={110}
            aria-label="Dauer"
            value={hoursInput ?? formatHours(hours)}
            onFocus={(e) => {
              const __el = e.currentTarget;
              setHoursInput(formatHours(hours));
              requestAnimationFrame(() => __el.select());
            }}
            onChange={(e) => setHoursInput(e.currentTarget.value)}
            onBlur={commitHoursInput}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                commitHoursInput();
              } else if (e.key === 'Escape') {
                setHoursInput(null);
              }
            }}
            styles={{ input: { textAlign: 'center', fontWeight: 600 } }}
          />
          <ActionIcon
            variant="default"
            size="md"
            disabled={hours >= 24}
            onClick={() => setHours((h) => Math.min(24, Math.round((h + 0.25) * 60) / 60))}
          >
            <IconPlus size={14} />
          </ActionIcon>
          {Math.round(hours * 60) % 15 !== 0 && (
            <Text size="xs" c="dimmed">
              → gebucht als {formatHours(roundUpToQuarterHours(hours))} ab{' '}
              {new Date(endDate.getTime() - roundUpToQuarterHours(hours) * 3_600_000).toLocaleTimeString('de-DE', {
                hour: '2-digit',
                minute: '2-digit',
              })}{' '}
              (Viertelstunden-Takt nach vorn, Ende bleibt; erfasst bleibt {formatHours(hours)})
            </Text>
          )}
        </Group>
      </div>

      {bookingArticles.hasChoice && (
        <div>
          <Text size="xs" fw={500} mb={4}>Leistungsart</Text>
          <SegmentedControl
            size="xs"
            fullWidth
            value={articleId ?? bookingArticles.defaultArticleId ?? ''}
            onChange={(v) => setArticleId(v === bookingArticles.defaultArticleId ? null : v)}
            data={bookingArticles.options.map((o) => ({ value: o.id, label: o.name }))}
          />
        </div>
      )}

      <Divider />

      {/* Start- UND Endzeit frei eingebbar. Das Ende ist der Anker: Startzeit ändern passt
          die Dauer an, Dauer ändern verschiebt die Startzeit, Endzeit ändern behält die
          Startzeit und passt die Dauer an. */}
      <Group gap="sm" grow>
        <TextInput
          label="Startzeit"
          required
          size="sm"
          type="datetime-local"
          value={toDateTimeLocalValue(startDate)}
          onChange={(e) => {
            const d = new Date(e.currentTarget.value);
            if (!isNaN(d.getTime())) applyStart(d);
          }}
        />
        <TextInput
          label="Endzeit"
          required
          size="sm"
          type="datetime-local"
          value={toDateTimeLocalValue(endDate)}
          onChange={(e) => {
            const d = new Date(e.currentTarget.value);
            if (isNaN(d.getTime())) return;
            const diffH = (d.getTime() - startDate.getTime()) / 3_600_000;
            // Start bleibt, die Dauer passt sich an. Minutengenau erfassen — aufgerundet wird
            // erst beim Buchen (Server), und zwar nach vorn.
            if (diffH > 0 && diffH <= 24) {
              setHours(Math.max(1 / 60, Math.round(diffH * 60) / 60));
              setEndDate(d);
            } else {
              // Ende vor dem Start (oder > 24 h danach): Dauer behalten, Start wandert mit.
              setEndDate(d);
            }
          }}
          error={hours <= 0 ? 'Endzeit muss nach der Startzeit liegen' : undefined}
        />
      </Group>

      <Textarea
        label="Kommentar"
        required
        value={comment}
        onChange={(e) => setComment(e.currentTarget.value)}
        minRows={2}
        autosize
        size="sm"
        placeholder="Was wurde gemacht?"
        description="Erscheint auf dem Servicebericht des Kunden — kundentauglich formulieren."
      />
      <Group gap="xs" justify="space-between" wrap="wrap">
        <Group gap={6}>
          <Tooltip label="KIAN formuliert den Text kundentauglich für den Servicebericht (postet nichts)">
            <Button
              size="compact-xs"
              variant="light"
              color="grape"
              leftSection={<IconSparkles size={12} />}
              loading={kianDraftMutation.isPending}
              disabled={mutation.isPending}
              onClick={() => kianDraftMutation.mutate()}
            >
              {comment.trim() ? 'KIAN optimieren' : 'KIAN vorschlagen'}
            </Button>
          </Tooltip>
          {commentBeforeKian !== null && (
            <Button
              size="compact-xs"
              variant="subtle"
              color="gray"
              onClick={() => {
                setComment(commentBeforeKian);
                setCommentBeforeKian(null);
              }}
            >
              Original wiederherstellen
            </Button>
          )}
        </Group>
      </Group>

      {/* Mitveröffentlichung: der Buchungstext landet ohnehin auf dem Servicebericht —
          optional zusätzlich als Ticketkommentar (intern fürs Team, öffentlich = Kunde
          sieht ihn sofort per Portal/Mail, nicht erst mit dem Bericht). */}
      <Stack gap={4}>
        <Switch
          size="xs"
          label="Auch als interner Kommentar ins Ticket"
          checked={alsoInternal}
          onChange={(e) => setAlsoInternal(e.currentTarget.checked)}
        />
        <Switch
          size="xs"
          color="orange"
          label={
            <Group gap={6} wrap="nowrap">
              <Text size="xs">Auch als öffentlicher Kommentar</Text>
              <Badge size="xs" color="orange" variant={alsoPublic ? 'filled' : 'light'}>
                Kunde sieht ihn sofort
              </Badge>
            </Group>
          }
          checked={alsoPublic}
          onChange={(e) => setAlsoPublic(e.currentTarget.checked)}
        />
        {/* Ein aktiver Schalter allein war zu leise für eine Aktion, die beim Kunden
            landet — im aktiven Zustand deshalb ein unübersehbarer Hinweis. */}
        {alsoPublic && (
          <Alert
            color="orange"
            variant="light"
            p="xs"
            icon={<IconAlertTriangle size={16} />}
          >
            <Text size="xs">
              <strong>Öffentlich:</strong> Der Text geht sofort an den Kunden (Kundenportal
              und E-Mail) — bitte kundentauglich formulieren.
            </Text>
          </Alert>
        )}
      </Stack>

      <Group justify="flex-end">
        <Button
          size="xs"
          leftSection={sendsProposal ? <IconClockShare size={14} /> : <IconClockPlus size={14} />}
          color={sendsProposal ? 'grape' : undefined}
          loading={mutation.isPending}
          disabled={!canSubmit}
          onClick={() => {
            // Ungewöhnliche Buchung (> 8 h oder Beginn vor 6 Uhr) ausgeschrieben bestätigen lassen —
            // T26-03524: gemeint 10:00–10:30, gebucht 00:00–10:30 = 10,5 h.
            const forOther = effectiveTechnicianId && effectiveTechnicianId !== user?.weclappUserId
              ? technicianOptions.find((o) => o.value === effectiveTechnicianId)?.label
              : undefined;
            const warning = bookingPlausibilityWarning(startDate, endDate, hours, forOther);
            if (warning && !window.confirm(warning)) return;
            if (bookingGaps.length > 0) setGapsOpen(true);
            else mutation.mutate();
          }}
        >
          {sendsProposal ? `Vorschlag an ${otherName} senden` : 'Zeit buchen'}
        </Button>
      </Group>
      {gapTicket && (
        <TicketGapsModal
          opened={gapsOpen}
          onClose={() => setGapsOpen(false)}
          ticket={gapTicket}
          gaps={bookingGaps}
          title="Vor dem Buchen"
          intro="Weclapp nimmt die Zeit an diesem Ticket noch nicht an. Was fehlt, fragen wir der Reihe nach ab — danach wird gebucht."
          finishLabel="Übernehmen und Zeit buchen"
          onFinish={async (assignee) => {
            await applyAssigneeChoice(ticketId, assignee);
            await mutation.mutateAsync();
          }}
        />
      )}
    </Stack>
  );
}

/**
 * Was an einem Ticket fehlt, bevor Weclapp eine Aktion annimmt.
 *
 * - `customer`: Zeit hängt an einer Dienstleistungsaufgabe, und die braucht den Kunden des
 *   Tickets („Tasks with articles must have a customer").
 * - `assignee`: Jeder Status außer „Noch nicht zugewiesen"/Pool verlangt einen Bearbeiter
 *   („assigned user required for internal ticket status: IN_PROGRESS"). Beim Buchen wird
 *   er ebenfalls abgefragt: Wer Zeit auf ein Ticket schreibt, arbeitet daran.
 *
 * Bis v1.360.1 lief beides erst in Weclapps 400 — beim Buchen landete die Zeit in der
 * Warteliste, beim Statuswechsel kam nur eine Fehlermeldung. Jetzt wird vorher gefragt.
 */
type TicketGap = 'customer' | 'assignee';

function ticketGaps(
  ticket: Pick<WeclappTicket, 'partyName' | 'responsibleUserId'> | null | undefined,
  need: { customer?: boolean; assignee?: boolean },
): TicketGap[] {
  if (!ticket) return [];
  const gaps: TicketGap[] = [];
  if (need.customer && isCustomerMissing(ticket.partyName)) gaps.push('customer');
  if (need.assignee && !ticket.responsibleUserId) gaps.push('assignee');
  return gaps;
}

/** Gewählter Bearbeiter aus dem Assistenten — `reason` nur, wenn der Server eine verlangt. */
interface AssigneeChoice {
  userId: string;
  label: string;
  reason?: string;
}

/**
 * Assistent „Konflikte auflösen": fragt der Reihe nach ab, was fehlt (Kunde → Bearbeiter),
 * und führt danach die eigentliche Aktion aus (`onFinish`: buchen, nachbuchen, Status setzen).
 *
 * Der Kunde wird im Schritt selbst zugeordnet (dasselbe Panel wie „Kunde zuordnen" im
 * Kopf). Der Bearbeiter wird nur GEWÄHLT und an `onFinish` übergeben: Beim Statuswechsel
 * muss er im selben PUT mitgehen wie der Status (Weclapp lehnt beide Reihenfolgen einzeln ab).
 */
function TicketGapsModal({
  opened,
  onClose,
  ticket,
  gaps,
  title,
  intro,
  finishLabel,
  onFinish,
}: {
  opened: boolean;
  onClose: () => void;
  ticket: WeclappTicket;
  gaps: TicketGap[];
  title: string;
  intro: string;
  finishLabel: string;
  onFinish: (assignee: AssigneeChoice | null) => Promise<void>;
}) {
  const { user } = useAuthStore();
  const [active, setActive] = useState(0);
  const [assigneeId, setAssigneeId] = useState<string | null>(user?.weclappUserId ?? null);
  const [reason, setReason] = useState('');
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Beim Öffnen die Gaps einfrieren: Nach „Kunde zuordnen" lädt das Cockpit neu, und ein
  // live nachgerechneter Schritt verschwände unter den Füßen des Assistenten.
  const [steps, setSteps] = useState<TicketGap[]>(gaps);

  useEffect(() => {
    if (!opened) return;
    setSteps(gaps);
    setActive(0);
    setAssigneeId(user?.weclappUserId ?? null);
    setReason('');
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- nur beim Öffnen übernehmen
  }, [opened]);

  const { data: techData } = useQuery({
    queryKey: ['portal-technicians'],
    queryFn: () => api.getPortalTechnicians(),
    staleTime: 120_000,
    enabled: opened && steps.includes('assignee'),
  });
  // Zuweisungsregel wie in AssignmentForm (verbindlich ist der Server): sich selbst jeder,
  // andere nur Admin (ohne) bzw. Teamlead (mit Begründung).
  const role = user?.role ?? 'TECHNICIAN';
  const mayAssignOthers = role === 'ADMIN' || role === 'TEAMLEAD';
  const technicianOptions = (techData?.technicians ?? [])
    .filter((t) => t.weclappUserId && (mayAssignOthers || t.isCurrentUser))
    .map((t) => ({
      value: t.weclappUserId as string,
      label: t.isCurrentUser ? `${t.displayName} (ich)` : t.displayName,
    }));
  const choosingOther = !!assigneeId && assigneeId !== user?.weclappUserId;
  const reasonNeeded = choosingOther && role === 'TEAMLEAD';
  const REASON_MIN = 10; // identisch zu ASSIGN_REASON_MIN im Backend

  const stepIndexOf = (gap: TicketGap) => steps.indexOf(gap);
  const finishIndex = steps.length;
  const customerStillMissing = isCustomerMissing(ticket.partyName);

  const finish = async () => {
    setRunning(true);
    setError(null);
    try {
      const choice: AssigneeChoice | null =
        steps.includes('assignee') && assigneeId
          ? {
              userId: assigneeId,
              label: technicianOptions.find((o) => o.value === assigneeId)?.label ?? 'Bearbeiter',
              ...(reasonNeeded ? { reason: reason.trim() } : {}),
            }
          : null;
      await onFinish(choice);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unbekannter Fehler');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Modal opened={opened} onClose={onClose} title={title} size="lg" zIndex={700} centered>
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          {intro}
        </Text>
        <Stepper active={active} onStepClick={(i) => i < active && setActive(i)} size="sm">
          {steps.map((gap) =>
            gap === 'customer' ? (
              <Stepper.Step key="customer" label="Kunde" description="dem Ticket zuordnen">
                <Stack gap="xs" mt="sm">
                  <Text size="sm">
                    Das Ticket hat noch keinen Kunden. Ohne ihn legt Weclapp keine Aufgabe an, auf
                    die gebucht werden kann.
                  </Text>
                  {customerStillMissing ? (
                    <CustomerMatchPanel ticket={ticket} onAssigned={() => setActive(stepIndexOf('customer') + 1)} />
                  ) : (
                    <Group justify="space-between">
                      <Badge color="teal" variant="light">
                        Kunde: {ticket.partyName}
                      </Badge>
                      <Button size="xs" onClick={() => setActive(stepIndexOf('customer') + 1)}>
                        Weiter
                      </Button>
                    </Group>
                  )}
                </Stack>
              </Stepper.Step>
            ) : (
              <Stepper.Step key="assignee" label="Bearbeiter" description="wer arbeitet daran">
                <Stack gap="xs" mt="sm">
                  <Text size="sm">
                    Das Ticket hat keinen Bearbeiter. Weclapp verlangt einen, sobald es nicht mehr
                    „Noch nicht zugewiesen" ist.
                  </Text>
                  <Select
                    label="Bearbeiter"
                    data={technicianOptions}
                    value={assigneeId}
                    onChange={setAssigneeId}
                    searchable
                    comboboxProps={{ withinPortal: true, zIndex: 1000 }}
                    description={
                      mayAssignOthers ? undefined : 'Techniker können Tickets nur sich selbst zuweisen.'
                    }
                  />
                  {reasonNeeded && (
                    <Textarea
                      label="Begründung"
                      description={`Pflicht, wenn du einem anderen zuweist (mind. ${REASON_MIN} Zeichen).`}
                      value={reason}
                      onChange={(e) => setReason(e.currentTarget.value)}
                      autosize
                      minRows={2}
                    />
                  )}
                  <Group justify="flex-end">
                    <Button
                      size="xs"
                      disabled={!assigneeId || (reasonNeeded && reason.trim().length < REASON_MIN)}
                      onClick={() => setActive(stepIndexOf('assignee') + 1)}
                    >
                      Weiter
                    </Button>
                  </Group>
                </Stack>
              </Stepper.Step>
            ),
          )}
          <Stepper.Completed>
            <Stack gap="xs" mt="sm">
              <Text size="sm" fw={500}>
                Bereit.
              </Text>
              {steps.includes('customer') && (
                <Text size="sm">• Kunde: {ticket.partyName}</Text>
              )}
              {steps.includes('assignee') && assigneeId && (
                <Text size="sm">
                  • Bearbeiter: {technicianOptions.find((o) => o.value === assigneeId)?.label ?? assigneeId}
                </Text>
              )}
              {error && (
                <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
                  <Text size="xs" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {error}
                  </Text>
                </Alert>
              )}
              <Group justify="flex-end">
                <Button variant="subtle" onClick={onClose} disabled={running}>
                  Abbrechen
                </Button>
                <Button loading={running} onClick={() => void finish()}>
                  {finishLabel}
                </Button>
              </Group>
            </Stack>
          </Stepper.Completed>
        </Stepper>
        {active < finishIndex && (
          <Group justify="flex-start">
            <Button variant="subtle" size="xs" onClick={onClose}>
              Abbrechen
            </Button>
          </Group>
        )}
      </Stack>
    </Modal>
  );
}

/** Gewählten Bearbeiter setzen (Server wechselt dabei selbst von „Noch nicht zugewiesen" weg). */
async function applyAssigneeChoice(ticketId: string, choice: AssigneeChoice | null): Promise<void> {
  if (!choice) return;
  await api.updateWeclappTicket(ticketId, {
    responsibleUserId: choice.userId,
    ...(choice.reason ? { assignReason: choice.reason } : {}),
  });
}

function TimeEntryList({ ticketId }: { ticketId: string }) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['weclapp-cockpit', ticketId],
    queryFn: () => api.getWeclappTicketCockpit(ticketId),
    select: (d) => ({ timeEntries: d.timeEntries, pendingBookings: d.pendingTimeBookings ?? [], ticket: d.ticket }),
    staleTime: 30000,
  });
  const queryClient = useQueryClient();
  const [resolveFor, setResolveFor] = useState<string | null>(null);

  /** „Jetzt nachbuchen" — zusätzlich zum automatischen Lauf alle 10 Minuten. */
  const retryMutation = useMutation({
    mutationFn: (bookingId: string) => api.retryWeclappTimeBooking(ticketId, bookingId),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['weclapp-cockpit', ticketId] });
      notifications.show(
        res.status === 'SYNCED'
          ? { title: 'Nachgebucht', message: 'Die Zeit steht jetzt in Weclapp.', color: 'green' }
          : {
              title: 'Weclapp hat noch nicht angenommen',
              message: res.warnings[0] ?? res.lastError ?? 'Die Buchung wartet weiter.',
              color: 'yellow',
              autoClose: 10000,
            },
      );
    },
    onError: (e: Error) => notifications.show({ title: 'Nachbuchen fehlgeschlagen', message: e.message, color: 'red' }),
  });

  if (isLoading) {
    return (
      <Stack gap="xs" mt="xs">
        <Skeleton height={50} radius="sm" />
        <Skeleton height={50} radius="sm" />
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert color="red" variant="light" mt="xs">
        Fehler beim Laden der Zeiteinträge.{' '}
        <Button size="compact-xs" variant="subtle" onClick={() => refetch()}>
          Erneut versuchen
        </Button>
        {error instanceof Error && error.message && (
          <Text size="xs" c="red.7" mt={4} style={{ wordBreak: 'break-word' }}>
            {error.message}
          </Text>
        )}
      </Alert>
    );
  }

  const entries = data?.timeEntries ?? [];
  // Nur Wartelisten-Zeilen, deren Leistungsnachweis wirklich noch aussteht — hängen nur
  // noch die optionalen Kommentare, ist die Zeit längst in Weclapp (steht in `entries`).
  const pending = (data?.pendingBookings ?? []).filter((b) => b.timeEntryPending);
  const gaps = ticketGaps(data?.ticket, { customer: true, assignee: true });

  if (entries.length === 0 && pending.length === 0) {
    return (
      <Text size="sm" c="dimmed" mt="xs">
        Keine gebuchten Zeiten vorhanden
      </Text>
    );
  }

  return (
    <Stack gap="xs" mt="xs">
      {pending.map((b) => (
        <div
          key={b.id}
          style={{
            background: 'var(--mantine-color-dark-6)',
            borderRadius: 6,
            padding: '0.5rem 0.75rem',
            borderLeft: `3px solid var(--mantine-color-${b.status === 'ERROR' ? 'red' : 'yellow'}-6)`,
          }}
        >
          <Group gap="xs" mb={4} wrap="wrap">
            <IconClockPause size={12} color={`var(--mantine-color-${b.status === 'ERROR' ? 'red' : 'yellow'}-4)`} />
            <Text size="xs" fw={600} c={b.status === 'ERROR' ? 'red.4' : 'yellow.4'}>
              {Math.round(b.hours * 100) / 100}h
            </Text>
            {b.technicianName && (
              <Text size="xs" c="dimmed">
                {b.technicianName}
              </Text>
            )}
            <Text size="xs" c="dimmed">
              {formatDate(b.startDate ?? b.createdAt)}
            </Text>
            <Tooltip
              label={
                b.status === 'ERROR'
                  ? `Automatisches Nachbuchen aufgegeben (${b.attempts} Versuche) — ein Admin muss unter Sync-Verwaltung nachholen.${b.lastError ? ` Letzter Fehler: ${b.lastError}` : ''}`
                  : `Weclapp hat die Buchung abgelehnt — sie ist im Portal gespeichert und wird automatisch nachgebucht (alle 10 Min geprüft).${b.lastError ? ` Letzter Fehler: ${b.lastError}` : ''}`
              }
              multiline
              maw={360}
            >
              <Badge size="xs" variant="light" color={b.status === 'ERROR' ? 'red' : 'yellow'}>
                {b.status === 'ERROR' ? 'Nachbuchen fehlgeschlagen' : 'Wartet auf Weclapp'}
              </Badge>
            </Tooltip>
            {(b.commentInternalPending || b.commentPublicPending) && (
              <Badge size="xs" variant="outline" color="gray">
                {[
                  b.commentInternalPending ? 'interner Kommentar' : null,
                  b.commentPublicPending ? 'öffentlicher Kommentar' : null,
                ]
                  .filter(Boolean)
                  .join(' + ')}{' '}
                folgt mit
              </Badge>
            )}
          </Group>
          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
            {b.comment}
          </Text>
          {b.diagnosisTitle && (
            <Text size="xs" c={b.status === 'ERROR' ? 'red.4' : 'yellow.4'} mt={4}>
              Grund: {b.diagnosisTitle}
            </Text>
          )}
          <Group gap="xs" mt={6}>
            <Button
              size="compact-xs"
              variant="light"
              leftSection={<IconRefresh size={12} />}
              loading={retryMutation.isPending && retryMutation.variables === b.id}
              onClick={() => retryMutation.mutate(b.id)}
            >
              Jetzt nachbuchen
            </Button>
            {gaps.length > 0 && (
              <Button
                size="compact-xs"
                variant="filled"
                color="orange"
                leftSection={<IconListCheck size={12} />}
                onClick={() => setResolveFor(b.id)}
              >
                Konflikte auflösen
              </Button>
            )}
          </Group>
        </div>
      ))}
      {data?.ticket && (
        <TicketGapsModal
          opened={resolveFor !== null}
          onClose={() => setResolveFor(null)}
          ticket={data.ticket}
          gaps={gaps}
          title="Konflikte auflösen"
          intro="Weclapp nimmt die Buchung erst an, wenn am Ticket alles da ist. Was fehlt, fragen wir der Reihe nach ab — danach wird sofort nachgebucht."
          finishLabel="Übernehmen und nachbuchen"
          onFinish={async (assignee) => {
            await applyAssigneeChoice(ticketId, assignee);
            // Alle wartenden Buchungen dieses Tickets, nicht nur die angeklickte: Die Ursache
            // war dieselbe.
            const results = [];
            for (const p of pending) {
              results.push(await api.retryWeclappTimeBooking(ticketId, p.id));
            }
            queryClient.invalidateQueries({ queryKey: ['weclapp-cockpit', ticketId] });
            const open = results.filter((r) => r.status !== 'SYNCED');
            if (open.length > 0) {
              throw new Error(
                `Weclapp hat noch nicht angenommen: ${open[0]!.warnings[0] ?? open[0]!.lastError ?? 'unbekannter Grund'}`,
              );
            }
            notifications.show({ title: 'Nachgebucht', message: 'Die Zeit steht jetzt in Weclapp.', color: 'green' });
          }}
        />
      )}
      {entries.map((e: WeclappPerformanceRecord) => (
        <div
          key={e.id}
          style={{
            background: 'var(--mantine-color-dark-6)',
            borderRadius: 6,
            padding: '0.5rem 0.75rem',
            borderLeft: '3px solid var(--mantine-color-teal-6)',
          }}
        >
          <Group gap="xs" mb={4}>
            <IconClockPlay size={12} color="var(--mantine-color-teal-4)" />
            <Text size="xs" fw={600} c="teal.4">
              {e.recordedQuantity != null ? `${e.recordedQuantity}h` : '–'}
            </Text>
            {e.userName && (
              <Text size="xs" c="dimmed">
                {e.userName}
              </Text>
            )}
            <Text size="xs" c="dimmed">
              {formatDate(e.startDate ?? e.createdDate)}
            </Text>
          </Group>
          {e.comment && (/<[a-z!/][^>]*>/i.test(e.comment) || /&(?:nbsp|amp|lt|gt|quot|#\d+);/i.test(e.comment) ? (
            <EmailDescription html={e.comment} />
          ) : (
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {e.comment}
            </Text>
          ))}
        </div>
      ))}
    </Stack>
  );
}

function candidateLabel(c: { customerName: string; customerNumber: string | null }): string {
  return c.customerNumber ? `${c.customerName} (${c.customerNumber})` : c.customerName;
}

/**
 * Grobe Client-Seite der Server-Stopliste (ticketSenderMatch.isSharedSenderAddress):
 * System-/Anbieter-Absender, die an viele Kunden schreiben — als Kontakt gespeichert würden
 * sie das Absender-Matching vergiften. Der Server prüft verbindlich, das hier ist Vorwarnung.
 */
function isSharedSenderEmail(email: string): boolean {
  const lower = email.trim().toLowerCase();
  if (!lower) return false;
  return [
    /no-?reply@/, /donotreply@/, /do-not-reply@/, /postmaster@/, /mailer-?daemon/,
    /notification[s]?@/, /alert[s]?@/,
    /@sophos\.com$/, /@microsoft\.com$/, /@sendcloud\./, /@amazonses\.com$/, /@google\.com$/,
    /@([a-z0-9-]+\.)*uptimia\.com$/, /@([a-z0-9-]+\.)*uptimerobot\.com$/,
  ].some((p) => p.test(lower));
}

/** „max@pure-spa.de" → „Pure Spa" — grobe Vorbefüllung, der Techniker korrigiert. */
function guessCompanyFromEmail(email: string): string | undefined {
  const domain = email.split('@')[1]?.trim().toLowerCase();
  if (!domain) return undefined;
  const base = domain.split('.')[0];
  if (!base || ['gmail', 'outlook', 'web', 'gmx', 'yahoo', 'icloud', 'hotmail', 't-online'].includes(base)) {
    return undefined;
  }
  return base
    .split(/[-_]/)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join(' ');
}

/**
 * „Kunde zuordnen" — sichtbar v. a. bei Tickets ohne Kundenzuordnung (KUNDE FEHLT).
 * Extrahiert die Absender-Mail, schlägt passende Kunden vor (Mail/Domain), legt optional
 * einen lokalen Kontakt an und setzt am Weclapp-Ticket NUR die Party (nie contactId).
 */
function CustomerMatchPanel({
  ticket,
  onAssigned,
}: {
  ticket: WeclappTicket;
  /** Nach erfolgreicher Kundenzuordnung (z. B. Popover schließen — Toast bestätigt). */
  onAssigned?: () => void;
}) {
  const queryClient = useQueryClient();
  const [senderEmail, setSenderEmail] = useState('');
  const [senderTouched, setSenderTouched] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null);
  const [contactName, setContactName] = useState('');
  // Default AUS: Automatik-Absender (noreply@…) sollen nicht versehentlich Kontakte werden.
  const [createContact, setCreateContact] = useState(false);
  // Zuordnung optional als Regel speichern: künftige Mails mit gleichem Absender /
  // Empfänger / Betreff-Teil ordnet das System dann automatisch diesem Kunden zu.
  const [saveAsRule, setSaveAsRule] = useState(false);
  const [ruleUseSender, setRuleUseSender] = useState(true);
  const [ruleSubject, setRuleSubject] = useState('');
  const [ruleRecipient, setRuleRecipient] = useState<string | null>(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(customerSearch, 350);
  const [suggestionDismissed, setSuggestionDismissed] = useState(false);
  /** Bei bereits zugeordnetem Kunden bleibt das Formular eingeklappt, bis „Ändern". */
  const [reassignOpen, setReassignOpen] = useState(false);

  const candidatesQuery = useQuery({
    queryKey: ['weclapp-sender-candidates', ticket.id],
    queryFn: () => api.getWeclappTicketSenderCandidates(ticket.id),
    staleTime: 60_000,
  });

  const matchQuery = useQuery({
    queryKey: ['weclapp-customer-match', ticket.id],
    queryFn: () => api.getWeclappTicketCustomerMatch(ticket.id),
    staleTime: 30_000,
  });
  const match = matchQuery.data?.match ?? null;

  const invalidateAll = () => {
    // ['weclapp-cockpit'] ist die Query, die den Ticketkopf im Flyout speist (partyName →
    // „Kunde fehlt"-Banner). Ohne diese Invalidierung bleibt das Flyout nach erfolgreicher
    // Zuordnung auf dem alten Stand stehen und die Zuordnung sieht fehlgeschlagen aus.
    queryClient.invalidateQueries({ queryKey: ['weclapp-cockpit', ticket.id] });
    queryClient.invalidateQueries({ queryKey: ['weclapp-ticket', ticket.id] });
    queryClient.invalidateQueries({ queryKey: ['weclapp-sender-candidates', ticket.id] });
    queryClient.invalidateQueries({ queryKey: ['my-weclapp-tickets'] });
    queryClient.invalidateQueries({ queryKey: ['weclapp-tickets'] });
    queryClient.invalidateQueries({ queryKey: ['weclapp-dispatch-board'] });
    queryClient.invalidateQueries({ queryKey: ['weclapp-customer-match', ticket.id] });
    // „Ohne Kunde"-Tab & Boards sofort nachziehen — sonst zeigt die Liste das Ticket
    // trotz erfolgreicher Zuordnung bis zu 15 s weiter als KUNDE FEHLT.
    queryClient.invalidateQueries({ queryKey: ['all-weclapp-tickets'] });
    queryClient.invalidateQueries({ queryKey: ['team-weclapp-tickets'] });
    queryClient.invalidateQueries({ queryKey: ['kanban'] });
  };

  const runKianMutation = useMutation({
    mutationFn: () => api.runWeclappTicketCustomerMatch(ticket.id),
    onSuccess: ({ outcome }) => {
      setSuggestionDismissed(false);
      invalidateAll();
      const msg =
        outcome.status === 'AUTO_ASSIGNED'
          ? `Kunde zugeordnet: ${outcome.customerName ?? ''}`
          : outcome.status === 'SUGGESTED'
            ? `Vorschlag: ${outcome.customerName ?? ''}`
            : 'Kein eindeutiger Kunde — bitte manuell zuordnen';
      // Bei Auto-Zuordnung schließt das Popover nach kurzem Moment von selbst —
      // die grüne Bestätigung ist kurz sichtbar, der Toast oben rechts bleibt.
      if (outcome.status === 'AUTO_ASSIGNED') {
        window.setTimeout(() => onAssigned?.(), 1500);
      }
      notifications.show({
        title: 'KIAN-Prüfung abgeschlossen',
        message: msg,
        color: outcome.status === 'NEEDS_REVIEW' ? 'yellow' : 'green',
      });
    },
    onError: (e: Error) => notifications.show({ title: 'KIAN-Fehler', message: e.message, color: 'red' }),
  });

  const confirmKianMutation = useMutation({
    mutationFn: () => api.confirmWeclappTicketCustomerMatch(ticket.id, { createContact: true }),
    onSuccess: (res) => {
      invalidateAll();
      notifications.show({ title: 'Vorschlag übernommen', message: res.customerName, color: 'green' });
    },
    onError: (e: Error) => notifications.show({ title: 'Fehler', message: e.message, color: 'red' }),
  });

  // Absender-Mail einmalig vorbefüllen
  useEffect(() => {
    if (!senderTouched && candidatesQuery.data?.primaryEmail) {
      setSenderEmail(candidatesQuery.data.primaryEmail);
    }
  }, [candidatesQuery.data?.primaryEmail, senderTouched]);

  const searchQuery = useQuery({
    queryKey: ['hub-customer-search', debouncedSearch],
    queryFn: () => api.searchHubCustomers(debouncedSearch, 1, 20),
    enabled: debouncedSearch.trim().length >= 2,
    staleTime: 60_000,
  });

  const candidates = candidatesQuery.data?.candidates ?? [];

  const selectData = useMemo(() => {
    const map = new Map<string, { value: string; label: string }>();
    for (const c of candidates) map.set(c.customerId, { value: c.customerId, label: candidateLabel(c) });
    for (const c of searchQuery.data?.customers ?? []) {
      if (!map.has(c.id)) {
        map.set(c.id, { value: c.id, label: c.customerNumber ? `${c.name} (${c.customerNumber})` : c.name });
      }
    }
    if (selectedCustomerId && selectedLabel && !map.has(selectedCustomerId)) {
      map.set(selectedCustomerId, { value: selectedCustomerId, label: selectedLabel });
    }
    return [...map.values()];
  }, [candidates, searchQuery.data, selectedCustomerId, selectedLabel]);

  const assignMutation = useMutation({
    mutationFn: () => {
      if (!selectedCustomerId) throw new Error('Bitte einen Kunden auswählen');
      return api.assignWeclappTicketCustomer(ticket.id, {
        customerId: selectedCustomerId,
        senderEmail: senderEmail.trim() || undefined,
        contactName: contactName.trim() || undefined,
        createContact,
      });
    },
    onSuccess: (res) => {
      invalidateAll();
      notifications.show({
        title: 'Kunde zugeordnet',
        message: `${res.customerName}${res.contactId ? ' · Kontakt lokal gespeichert' : ''}`,
        color: 'green',
      });
      // Regel NACH erfolgreicher Zuordnung anlegen — schlägt die Zuordnung fehl,
      // entsteht auch keine Regel.
      const senderPattern = saveAsRule && ruleUseSender ? senderEmail.trim().toLowerCase() : '';
      const subjectContains = saveAsRule ? ruleSubject.trim() : '';
      const recipientPattern = saveAsRule ? (ruleRecipient ?? '') : '';
      if (saveAsRule && selectedCustomerId && (senderPattern || subjectContains || recipientPattern)) {
        api
          .createTicketCustomerRule({
            senderPattern: senderPattern || undefined,
            subjectContains: subjectContains || undefined,
            recipientPattern: recipientPattern || undefined,
            customerId: selectedCustomerId,
            applyToExisting: true,
          })
          .then(() =>
            notifications.show({
              title: 'Zuordnungsregel gespeichert',
              message:
                'Künftige passende Tickets werden automatisch zugeordnet — bestehende offene zieht das System im Hintergrund nach.',
              color: 'teal',
            })
          )
          .catch((e: Error) =>
            notifications.show({ title: 'Regel konnte nicht gespeichert werden', message: e.message, color: 'red' })
          );
      }
      onAssigned?.();
    },
    onError: (e: Error) => notifications.show({ title: 'Fehler', message: e.message, color: 'red' }),
  });

  const pickCandidate = (c: WeclappCustomerCandidate) => {
    setSelectedCustomerId(c.customerId);
    setSelectedLabel(candidateLabel(c));
    if (!senderTouched && c.matchedEmail && !c.matchedEmail.startsWith('@')) {
      setSenderEmail(c.matchedEmail);
    }
  };

  // Ist ein Kunde zugeordnet, ist das Zuordnungs-Formular Ballast: der häufige Fall
  // („passt") braucht nur die Bestätigung, der seltene („falsch zugeordnet") einen Klick.
  const customerMissing = isCustomerMissing(ticket.partyName);
  const showAssignForm = customerMissing || reassignOpen;

  return (
    <Stack gap="sm" mt="sm">
      {customerMissing ? (
        <Alert color="orange" variant="light" icon={<IconAlertTriangle size={16} />}>
          Dieses Ticket hat noch keinen Kunden. Absender prüfen und Kunde zuordnen.
        </Alert>
      ) : (
        <Alert color="teal" variant="light" icon={<IconCheck size={16} />}>
          <Group justify="space-between" wrap="nowrap" gap="sm">
            {/* minWidth: 0, damit lange Kundennamen umbrechen statt den Button
                aus dem Popover zu schieben (sonst wird „Ändern" abgeschnitten). */}
            <Text size="sm" style={{ flex: 1, minWidth: 0 }}>
              Kunde:{' '}
              <b>
                <CustomerNameLink
                  name={ticket.partyName ?? ''}
                  weclappCustomerId={ticket.partyId}
                />
              </b>
            </Text>
            {!reassignOpen && (
              <Button size="compact-xs" variant="subtle" style={{ flexShrink: 0 }} onClick={() => setReassignOpen(true)}>
                Ändern
              </Button>
            )}
          </Group>
        </Alert>
      )}

      {/* KIAN hat automatisch zugeordnet */}
      {match?.status === 'AUTO_ASSIGNED' && match.assignedBy === 'KIAN' && (
        <Alert color="grape" variant="light" icon={<IconSparkles size={16} />}>
          <Text size="sm" fw={600}>KIAN hat den Kunden zugeordnet</Text>
          <Text size="xs" c="dimmed">
            {match.assignedCustomerName ?? ''}
            {typeof match.confidence === 'number' ? ` · Sicherheit ${Math.round(match.confidence * 100)}%` : ''}
          </Text>
          {match.kianReasoning && (
            <Text size="xs" c="dimmed" mt={4}>{match.kianReasoning}</Text>
          )}
        </Alert>
      )}

      {/* KIAN-Vorschlag (noch nicht übernommen) */}
      {match?.status === 'SUGGESTED' && !suggestionDismissed && (
        <Alert color="blue" variant="light" icon={<IconSparkles size={16} />}>
          <Stack gap={6}>
            <Text size="sm">
              <b>KIAN-Vorschlag:</b> {match.suggestedCustomerName ?? 'Kunde'}
              {typeof match.confidence === 'number' ? ` (Sicherheit ${Math.round(match.confidence * 100)}%)` : ''}
            </Text>
            {match.kianReasoning && <Text size="xs" c="dimmed">{match.kianReasoning}</Text>}
            <Group gap="xs">
              <Button
                size="compact-xs"
                color="teal"
                leftSection={<IconCheck size={12} />}
                loading={confirmKianMutation.isPending}
                onClick={() => confirmKianMutation.mutate()}
              >
                Übernehmen
              </Button>
              <Button
                size="compact-xs"
                variant="subtle"
                color="gray"
                onClick={() => setSuggestionDismissed(true)}
              >
                Ablehnen
              </Button>
            </Group>
          </Stack>
        </Alert>
      )}

      {/* KIAN-Prüfung anstoßen */}
      {customerMissing && (
        <Button
          size="xs"
          variant="light"
          color="grape"
          leftSection={<IconSparkles size={14} />}
          loading={runKianMutation.isPending}
          onClick={() => runKianMutation.mutate()}
        >
          Kunde von KIAN prüfen lassen
        </Button>
      )}

      {showAssignForm && (
        <>
      <TextInput
        label="Kunden-E-Mail (Kontakt)"
        description="Beste erkannte Kunden-Adresse — kann Absender oder An/CC-Empfänger sein (z. B. Tenant-Adresse). Wird optional als lokaler Kontakt gespeichert, nie am Weclapp-Ticket gesetzt."
        value={senderEmail}
        onChange={(e) => {
          setSenderTouched(true);
          setSenderEmail(e.currentTarget.value);
        }}
        placeholder="kontakt@firma.de"
        leftSection={<IconAt size={14} />}
        size="sm"
      />

      {/* Weitere erkannte Adressen als Schnellauswahl */}
      {(candidatesQuery.data?.senderEmails?.length ?? 0) > 1 && (
        <Group gap={4}>
          <Text size="xs" c="dimmed">Erkannt:</Text>
          {candidatesQuery.data!.senderEmails.slice(0, 5).map((e) => (
            <Badge
              key={e.email}
              size="sm"
              variant={e.email === senderEmail ? 'filled' : 'light'}
              color={e.isBounce ? 'red' : e.source === 'from' ? 'blue' : 'gray'}
              style={{ cursor: 'pointer', textTransform: 'none' }}
              onClick={() => {
                setSenderTouched(true);
                setSenderEmail(e.email);
              }}
              title={e.isBounce ? 'Bounce-/System-Absender — vermutlich nicht der Kunde' : e.source}
            >
              {e.email}
            </Badge>
          ))}
        </Group>
      )}

      {/* Quelle der Mail immer sichtbar machen: Von + Empfänger (An/CC). Die Mail ging
          nicht zwingend an support@fixbyte.it; kundenspezifische Empfänger sind oft das
          bessere Signal. Leere Empfänger werden benannt statt still ausgeblendet. */}
      {candidatesQuery.data && (
        <Stack gap={4}>
          {ticket.senderEmail && (
            <Group gap={4}>
              <Text size="xs" c="dimmed">Von:</Text>
              <Badge size="sm" variant="outline" color="gray" style={{ textTransform: 'none' }}>
                {ticket.senderEmail}
              </Badge>
            </Group>
          )}
          <Group gap={4}>
            <Text size="xs" c="dimmed">Empfänger (An/CC):</Text>
            {(candidatesQuery.data.recipients?.length ?? 0) > 0 ? (
              candidatesQuery.data.recipients.slice(0, 8).map((r) => (
                <Badge key={r} size="sm" variant="outline" color="gray" style={{ textTransform: 'none' }}>
                  {r}
                </Badge>
              ))
            ) : (
              <Text size="xs" c="dimmed" fs="italic">
                keine bekannt (Mail-Archiv leer oder nicht gespiegelt)
              </Text>
            )}
          </Group>
        </Stack>
      )}

      {/* Kunden-Vorschläge aus Mail/Domain */}
      {candidates.length > 0 && (
        <Stack gap={4}>
          <Text size="xs" c="dimmed">Vorschläge:</Text>
          <Group gap={6}>
            {candidates.slice(0, 6).map((c) => (
              <Button
                key={c.customerId}
                size="compact-xs"
                variant={selectedCustomerId === c.customerId ? 'filled' : 'light'}
                color={
                  c.matchMethod === 'EMAIL_EXACT'
                    ? 'teal'
                    : c.matchMethod === 'HISTORY'
                      ? 'orange'
                      : c.matchMethod === 'NAME_DOMAIN'
                        ? 'violet'
                        : 'blue'
                }
                leftSection={<IconBuilding size={12} />}
                onClick={() => pickCandidate(c)}
                title={
                  c.matchMethod === 'EMAIL_EXACT'
                    ? 'Exakter Mail-Treffer'
                    : c.matchMethod === 'HISTORY'
                      ? `Verlauf: ${c.matchedEmail}`
                      : c.matchMethod === 'NAME_DOMAIN'
                        ? 'Kundenname steckt in der Mail-Domain (z. B. Microsoft-Tenant) — bitte prüfen'
                        : 'Domain-Treffer'
                }
              >
                {candidateLabel(c)}
                {c.matchMethod === 'EMAIL_DOMAIN'
                  ? ' · Domain'
                  : c.matchMethod === 'NAME_DOMAIN'
                    ? ' · Namensähnlichkeit'
                    : c.matchMethod === 'HISTORY'
                      ? ' · Verlauf'
                      : ''}
                {!c.weclappPartyId ? ' · ⚠' : ''}
              </Button>
            ))}
          </Group>
        </Stack>
      )}

      <Select
        label="Kunde"
        placeholder={candidatesQuery.isLoading ? 'Absender wird geprüft…' : 'Kunde suchen (min. 2 Zeichen)…'}
        data={selectData}
        value={selectedCustomerId}
        onChange={(v) => {
          setSelectedCustomerId(v);
          setSelectedLabel(selectData.find((o) => o.value === v)?.label ?? null);
        }}
        searchable
        searchValue={customerSearch}
        onSearchChange={setCustomerSearch}
        nothingFoundMessage={searchQuery.isFetching ? 'Suche…' : 'Kein Kunde gefunden'}
        leftSection={<IconBuilding size={14} />}
        size="sm"
        clearable
        comboboxProps={{ withinPortal: true, zIndex: 1000 }}
      />

      <Switch
        label="Absender als lokalen Kontakt speichern"
        description="Gespeicherte Kontakt-Mails ordnen künftige Mails dieses Absenders automatisch diesem Kunden zu — nur für kundenspezifische Adressen sinnvoll."
        checked={createContact}
        onChange={(e) => setCreateContact(e.currentTarget.checked)}
        size="sm"
      />
      {createContact && isSharedSenderEmail(senderEmail) && (
        <Alert color="orange" variant="light" p="xs" icon={<IconAlertTriangle size={14} />}>
          <Text size="xs">
            <b>{senderEmail.trim()}</b> sieht nach einer System-/Anbieter-Adresse aus
            (no-reply, Sophos, Microsoft & Co.) — solche Absender schreiben an viele Kunden
            und werden deshalb <b>nicht</b> als Kontakt gespeichert. Trage stattdessen eine
            echte kundenspezifische Adresse ein.
          </Text>
        </Alert>
      )}
      {createContact && (
        <TextInput
          label="Kontaktname (optional)"
          value={contactName}
          onChange={(e) => setContactName(e.currentTarget.value)}
          placeholder="Max Mustermann"
          leftSection={<IconUserPlus size={14} />}
          size="sm"
        />
      )}

      <Switch
        label="Als Zuordnungsregel speichern — künftige passende Mails automatisch diesem Kunden"
        checked={saveAsRule}
        onChange={(e) => {
          const on = e.currentTarget.checked;
          setSaveAsRule(on);
          // Betreff vorbefüllen (kürzbar) — bei Automatik-Mails steht der Kunde oft genau dort.
          if (on && !ruleSubject.trim() && ticket.subject) setRuleSubject(ticket.subject);
        }}
        size="sm"
        color="violet"
      />
      {saveAsRule && (
        <Stack gap="xs" pl="sm" style={{ borderLeft: '2px solid var(--mantine-color-violet-4)' }}>
          <Switch
            size="xs"
            label={`Absender muss passen: ${senderEmail.trim() || '(keine Absender-Mail)'}`}
            checked={ruleUseSender}
            onChange={(e) => setRuleUseSender(e.currentTarget.checked)}
          />
          <TextInput
            label="Betreff enthält (optional)"
            description="Für wiederkehrende Automatik-Mails, bei denen der Kunde im Betreff steht."
            value={ruleSubject}
            onChange={(e) => setRuleSubject(e.currentTarget.value)}
            placeholder="z. B. Vitalounge Hotel"
            size="sm"
          />
          {(candidatesQuery.data?.recipients?.length ?? 0) > 0 && (
            <Select
              label="Empfänger muss passen (optional)"
              description="An-/CC-Adresse dieser Mail als Kriterium — z. B. kundenspezifische Postfächer."
              data={candidatesQuery.data!.recipients}
              value={ruleRecipient}
              onChange={setRuleRecipient}
              clearable
              size="sm"
              comboboxProps={{ withinPortal: true, zIndex: 1000 }}
            />
          )}
          <Text size="xs" c="dimmed">
            Alle gesetzten Kriterien müssen zutreffen. Ganze Domains: im Absenderfeld nur
            „@domain.de" stehen lassen. Verwaltung unter Admin → Kunden-Triage.
          </Text>
        </Stack>
      )}

      <Group gap="sm">
        <Button
          leftSection={<IconCheck size={16} />}
          loading={assignMutation.isPending}
          disabled={!selectedCustomerId}
          onClick={() => assignMutation.mutate()}
        >
          {customerMissing ? 'Kunde zuordnen' : 'Zuordnung ändern'}
        </Button>
        {!customerMissing && (
          <Button variant="subtle" onClick={() => setReassignOpen(false)}>
            Abbrechen
          </Button>
        )}
      </Group>

      {/* Existiert der Kunde noch gar nicht, führt der Weg über den Freigabe-Workflow —
          vorbefüllt mit Absender-Mail und Domain-Vermutung aus diesem Ticket. */}
      {customerMissing && (
        <Group gap="xs" mt={4}>
          <Text size="xs" c="dimmed">Kunde existiert noch nicht?</Text>
          <ProposeCustomerButton
            size="compact-xs"
            prefill={{
              companyName: guessCompanyFromEmail(senderEmail),
              email: senderEmail.trim() || undefined,
              notes: `Aus Ticket #${ticket.ticketNumber}: ${ticket.subject ?? ''}`.trim(),
            }}
          />
        </Group>
      )}
        </>
      )}
    </Stack>
  );
}

function AssignmentForm({
  ticket,
  onHandover,
  onAssigned,
}: {
  ticket: WeclappTicket;
  /** Öffnet den Übergeben-Dialog (Empfänger bestätigt). Fehlt, wenn bereits eine Übergabe läuft. */
  onHandover?: () => void;
  /** Nach erfolgreicher Zuweisung/Pool-Rückgabe (z. B. Popover schließen — Toast bestätigt). */
  onAssigned?: () => void;
}) {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [assignTarget, setAssignTarget] = useState<string | null>(null);

  const myWeclappId = user?.weclappUserId ?? null;
  // Techniker ohne weclapp-Benutzer: `assigneeRef` (local:…) statt Haupt-Benutzer.
  const currentAssigneeId = ticket.assigneeRef ?? ticket.responsibleUserId ?? null;
  const isAssignedToMe =
    !!myWeclappId && !!currentAssigneeId && String(currentAssigneeId) === String(myWeclappId);
  const { colorOf } = usePersonColors();

  // Zuweisungsregel (identisch serverseitig erzwungen, siehe PATCH /weclapp/tickets/:id):
  //   an sich selbst  jeder, ohne Begründung
  //   an andere       ADMIN ohne Begründung; TEAMLEAD mit Begründung; Techniker gar nicht
  //   in den Pool     jeder, mit Begründung
  // Das Ausblenden hier ist reine Benutzerführung — verbindlich ist der Server.
  const role = user?.role ?? 'TECHNICIAN';
  const mayAssignOthers = role === 'ADMIN' || role === 'TEAMLEAD';
  const othersNeedReason = role === 'TEAMLEAD';

  // Offener Begründungs-Dialog: `kind` unterscheidet, welche Aktion bestätigt wird.
  const [reasonPrompt, setReasonPrompt] = useState<{
    kind: 'release' | 'assign';
    userId: string | null;
    label: string;
  } | null>(null);
  const [reasonText, setReasonText] = useState('');
  const REASON_MIN = 10; // identisch zu ASSIGN_REASON_MIN im Backend

  // Nur zur NAMENS-Auflösung beliebiger Weclapp-IDs (aktueller Bearbeiter kann auch ein
  // Nicht-Portal-Benutzer sein). Die AUSWAHL-Liste kommt aus den Portal-Technikern unten.
  const usersQuery = useQuery({
    queryKey: ['weclapp-assignable-users'],
    queryFn: () => api.getWeclappAssignableUsers(),
    staleTime: 5 * 60 * 1000,
  });

  // Auswahlliste = aktive Portal-Techniker mit Weclapp-Verknüpfung — dieselbe Quelle wie
  // „Buchung als" und „Erwähnen". Die rohe Weclapp-Userliste enthält API-/System- und
  // Alt-Accounts, die hier nie zugewiesen werden sollen.
  const portalTechQuery = useQuery({
    queryKey: ['portal-technicians'],
    queryFn: () => api.getPortalTechnicians(),
    staleTime: 120_000,
  });

  const invalidateAfterChange = () => {
    queryClient.invalidateQueries({ queryKey: ['weclapp-cockpit', ticket.id] });
    queryClient.invalidateQueries({ queryKey: ['weclapp-ticket', ticket.id] });
    queryClient.invalidateQueries({ queryKey: ['my-weclapp-tickets'] });
    queryClient.invalidateQueries({ queryKey: ['weclapp-tickets'] });
    queryClient.invalidateQueries({ queryKey: ['team-tickets'] });
    queryClient.invalidateQueries({ queryKey: ['weclapp-dispatch-board'] });
  };

  // Ergebnis der letzten Zuweisung — bleibt sichtbar, bis das Flyout geschlossen wird.
  //
  // Vorher gab es nur einen kurzlebigen Toast (mit leerer message). Das war nicht
  // nachvollziehbar: der Toast meldete Erfolg, während das „Aktuell"-Badge noch „Pool –
  // nicht zugewiesen" zeigte, weil der Refetch noch lief. Beides zusammen ließ offen, was
  // jetzt eigentlich gilt.
  //
  // Deshalb wird hier nicht bloß Erfolg BEHAUPTET, sondern gegen den Serverzustand
  // GEPRÜFT: `expectedUserId` wird mit dem tatsächlich zurückgelesenen
  // `ticket.responsibleUserId` verglichen (siehe assignOutcome unten).
  const [lastAssign, setLastAssign] = useState<{
    expectedUserId: string | null;
    label: string;
    at: Date;
  } | null>(null);
  const [assignError, setAssignError] = useState<{ summary: string; detail?: string } | null>(
    null,
  );

  // Läuft der Refetch des Cockpits noch? Solange ja, ist ein abweichender Stand normal.
  const cockpitRefetching = useIsFetching({ queryKey: ['weclapp-cockpit', ticket.id] }) > 0;

  const assignMutation = useMutation({
    mutationFn: (v: { userId: string | null; reason?: string }) =>
      api.updateWeclappTicket(ticket.id, {
        responsibleUserId: v.userId,
        // Der Server verlangt die Begründung rollen-/aktionsabhängig; hier wird sie im
        // passenden Feld mitgeschickt, damit die Fehlermeldung bei Bedarf eindeutig ist.
        ...(v.reason && v.userId === null ? { releaseReason: v.reason } : {}),
        ...(v.reason && v.userId !== null ? { assignReason: v.reason } : {}),
      }),
    onSuccess: (data, v) => {
      const label =
        v.userId === null
          ? 'Pool (kein Bearbeiter)'
          : (userOptions.find((o) => o.value === v.userId)?.label ?? v.userId);
      setAssignError(null);
      setLastAssign({ expectedUserId: v.userId, label, at: new Date() });
      invalidateAfterChange();
      setAssignTarget(null);
      setReasonPrompt(null);
      showTicketUpdateNotice(
        data,
        v.userId ? 'Ticket zugewiesen' : 'Ticket in Pool zurückgegeben',
        v.userId ? label : 'Das Ticket hat jetzt keinen Bearbeiter mehr.',
      );
      onAssigned?.();
    },
    onError: (e: Error) => {
      setLastAssign(null);
      // Lesbare Anweisung und technischen Wortlaut getrennt halten. Der Wortlaut bleibt
      // dauerhaft in der Oberfläche — er ist die Grundlage für die Fehlersuche und war
      // vorher nur als flüchtiger Toast zu sehen.
      const api = e instanceof ApiError ? e : null;
      setAssignError({
        summary: api?.summary ?? e.message,
        detail: api?.detail,
      });
      notifications.show({ title: 'Fehler', message: e.message, color: 'red' });
    },
  });

  // Welcher Knopf gerade arbeitet: Alle drei Wege (übernehmen, zuweisen, Pool) laufen über
  // dieselbe Mutation. Gesperrt werden alle, drehen darf sich nur der geklickte — sonst sieht
  // es aus, als liefen zwei Zuweisungen gleichzeitig.
  const pendingAssignUserId = assignMutation.isPending
    ? (assignMutation.variables?.userId ?? null)
    : undefined;

  // Drei Zustände statt „irgendwas ist passiert":
  //   pending   — Zuweisung gesendet, Rückmeldung vom Server steht noch aus
  //   confirmed — Server liefert genau den erwarteten Bearbeiter zurück
  //   mismatch  — Refetch ist durch, Server zeigt aber etwas anderes
  const assignOutcome: 'pending' | 'confirmed' | 'mismatch' | null = !lastAssign
    ? null
    : String(currentAssigneeId ?? '') === String(lastAssign.expectedUserId ?? '')
      ? 'confirmed'
      : assignMutation.isPending || cockpitRefetching
        ? 'pending'
        : 'mismatch';

  // Eigene Person als erste Option, dann alle anderen (nicht der aktuelle Bearbeiter)
  const portalTechs = (portalTechQuery.data?.technicians ?? []).filter((t) => t.weclappUserId);
  const userOptions: { value: string; label: string }[] = [];
  if (myWeclappId && !isAssignedToMe) {
    const me = portalTechs.find((t) => String(t.weclappUserId) === String(myWeclappId));
    userOptions.push({
      value: myWeclappId,
      label: me ? `${me.displayName} (ich)` : 'Mir zuweisen',
    });
  }
  portalTechs
    .filter(
      (t) =>
        String(t.weclappUserId) !== String(currentAssigneeId ?? '') &&
        String(t.weclappUserId) !== String(myWeclappId ?? '')
    )
    .forEach((t) => userOptions.push({ value: t.weclappUserId as string, label: t.displayName }));

  // Name auflösen: bevorzugt responsibleUserName, sonst über die Benutzerliste
  // (responsibleUserName fehlt bei manchen Tickets → sonst würde die rohe ID angezeigt)
  const resolvedAssigneeName = currentAssigneeId
    ? ticket.responsibleUserName ??
      usersQuery.data?.users.find((u) => String(u.id) === String(currentAssigneeId))?.name ??
      currentAssigneeId
    : null;

  return (
    <Stack gap="xs" mt="sm">
      <Group gap="xs" wrap="wrap">
        <Text size="sm" c="dimmed">Aktuell:</Text>
        {currentAssigneeId ? (
          <Badge
            size="sm"
            variant="light"
            color={colorOf(currentAssigneeId, ticket.responsibleUserName)}
            style={personRingStyle(colorOf(currentAssigneeId, ticket.responsibleUserName), { width: 1.5 })}
          >
            {resolvedAssigneeName}
            {isAssignedToMe ? ' (du)' : ''}
          </Badge>
        ) : (
          <Badge size="sm" variant="light" color="orange">
            Pool – nicht zugewiesen
          </Badge>
        )}
      </Group>

      {!myWeclappId && (
        <Alert color="yellow" variant="light">
          Keine Weclapp User-ID hinterlegt — bitte Admin kontaktieren.
        </Alert>
      )}

      {/* Ergebnis der letzten Zuweisung — bewusst persistent statt nur als Toast, und
          gegen den zurückgelesenen Serverzustand geprüft statt bloß behauptet. */}
      {assignOutcome === 'confirmed' && lastAssign && (
        <Alert color="teal" variant="light" p="xs" icon={<IconCheck size={16} />}>
          <Text size="sm">
            Zuweisung bestätigt: <strong>{lastAssign.label}</strong>
          </Text>
          <Text size="xs" c="dimmed">
            In Weclapp gesetzt um {lastAssign.at.toLocaleTimeString('de-DE')}
          </Text>
        </Alert>
      )}

      {assignOutcome === 'pending' && lastAssign && (
        <Alert color="blue" variant="light" p="xs" icon={<Loader size={14} />}>
          <Text size="sm">
            Zuweisung an <strong>{lastAssign.label}</strong> gesendet — warte auf Bestätigung
            aus Weclapp…
          </Text>
        </Alert>
      )}

      {assignOutcome === 'mismatch' && lastAssign && (
        <Alert color="orange" variant="light" p="xs" icon={<IconAlertTriangle size={16} />}>
          <Text size="sm">
            Zuweisung an <strong>{lastAssign.label}</strong> wurde gesendet, Weclapp meldet
            aber weiterhin{' '}
            <strong>{resolvedAssigneeName ?? 'keinen Bearbeiter'}</strong>.
          </Text>
          <Text size="xs" c="dimmed">
            Bitte das Ticket neu laden. Bleibt es dabei, wurde die Änderung in Weclapp nicht
            übernommen.
          </Text>
        </Alert>
      )}

      {assignError && (
        <Alert color="red" variant="light" p="xs" icon={<IconAlertTriangle size={16} />}>
          <Text size="sm">Zuweisung fehlgeschlagen: {assignError.summary}</Text>
          {/* Technischer Wortlaut dauerhaft sichtbar und markierbar — ohne ihn lässt sich
              ein Weclapp-Validierungsfehler nicht nachvollziehen oder weitergeben. */}
          {assignError.detail && (
            <Text
              size="xs"
              c="dimmed"
              mt={6}
              style={{
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                userSelect: 'text',
              }}
            >
              {assignError.detail}
            </Text>
          )}
        </Alert>
      )}

      {/* Selbst übernehmen — für jede Rolle der direkte Weg, ohne Begründung. */}
      {!isAssignedToMe && myWeclappId && (
        <Button
          size="xs"
          color="teal"
          leftSection={<IconUserCheck size={14} />}
          loading={pendingAssignUserId === myWeclappId}
          disabled={assignMutation.isPending}
          onClick={() => assignMutation.mutate({ userId: myWeclappId })}
        >
          Ticket übernehmen
        </Button>
      )}

      {/* Zuweisung an ANDERE nur für Admin/Teamlead. Techniker nutzen die Übergabe, damit
          der Empfänger aktiv bestätigt und niemand unbemerkt Arbeit zugeschoben bekommt. */}
      {mayAssignOthers ? (
        <Group gap="xs" align="flex-end" wrap="nowrap">
          <Select
            label="Anderer Person zuweisen"
            description={
              othersNeedReason ? 'Disposition — Begründung erforderlich' : undefined
            }
            placeholder={portalTechQuery.isLoading ? 'Lade Benutzer…' : 'Techniker wählen…'}
            data={userOptions.filter((o) => o.value !== myWeclappId)}
            value={assignTarget}
            onChange={setAssignTarget}
            searchable
            clearable
            disabled={portalTechQuery.isLoading}
            style={{ flex: 1 }}
            size="sm"
            leftSection={<IconUsers size={14} />}
            comboboxProps={{ withinPortal: true, zIndex: 1000 }}
          />
          <Button
            size="xs"
            color="teal"
            loading={
              pendingAssignUserId != null && pendingAssignUserId !== myWeclappId
            }
            disabled={!assignTarget || assignMutation.isPending}
            onClick={() => {
              if (!assignTarget) return;
              const label =
                userOptions.find((o) => o.value === assignTarget)?.label ?? assignTarget;
              if (othersNeedReason) {
                setReasonText('');
                setReasonPrompt({ kind: 'assign', userId: assignTarget, label });
              } else {
                assignMutation.mutate({ userId: assignTarget });
              }
            }}
          >
            Zuweisen
          </Button>
        </Group>
      ) : null}

      {/* Übergeben — für ALLE Rollen der Weg mit Bestätigung: der Empfänger nimmt die
          Übernahme aktiv an. Admin/Teamlead haben damit beide Wege (direkt zuweisen
          ODER übergeben), Techniker geben Tickets ausschließlich so weiter. */}
      {onHandover ? (
        <Stack gap={4}>
          <Button
            size="xs"
            variant="light"
            color="orange"
            leftSection={<IconTransfer size={14} />}
            onClick={onHandover}
          >
            An andere Person übergeben
          </Button>
          <Text size="xs" c="dimmed">
            {mayAssignOthers
              ? 'Übergabe statt Direkt-Zuweisung: der Empfänger muss die Übernahme aktiv annehmen.'
              : 'Der Empfänger muss die Übernahme aktiv annehmen.'}
          </Text>
        </Stack>
      ) : (
        !mayAssignOthers && (
          <Text size="xs" c="dimmed">
            Weitergabe an eine andere Person läuft über <strong>Übergeben</strong> — der
            Empfänger muss sie bestätigen.
          </Text>
        )
      )}

      {currentAssigneeId && (
        <Button
          size="xs"
          variant="subtle"
          color="gray"
          leftSection={<IconUserOff size={13} />}
          loading={pendingAssignUserId === null}
          disabled={assignMutation.isPending}
          onClick={() => {
            setReasonText('');
            setReasonPrompt({ kind: 'release', userId: null, label: 'Pool' });
          }}
        >
          In Pool zurückgeben
        </Button>
      )}

      {/* Begründungs-Dialog für Pool-Rückgabe (alle) und Teamlead-Zuweisung.
          Die Begründung wird als interner Kommentar am Ticket festgehalten — nur dort, wo
          sie echte Information trägt. Bei „selbst übernehmen" entsteht bewusst kein
          Kommentar: wer wann übernommen hat, steht ohnehin im Verlauf. */}
      <Modal
        opened={reasonPrompt !== null}
        onClose={() => setReasonPrompt(null)}
        title={
          reasonPrompt?.kind === 'release'
            ? 'Ticket in den Pool zurückgeben'
            : `Ticket zuweisen an ${reasonPrompt?.label ?? ''}`
        }
        zIndex={700}
        centered
      >
        <Stack gap="sm">
          <Textarea
            label="Begründung"
            description={`Wird als interner Kommentar im Ticket festgehalten (mind. ${REASON_MIN} Zeichen).`}
            placeholder={
              reasonPrompt?.kind === 'release'
                ? 'z. B. Kunde hat das Anliegen zurückgezogen — zurück in den Pool zur Neubewertung.'
                : 'z. B. Sascha übernimmt, weil er den Kunden und die Firewall-Konfiguration kennt.'
            }
            value={reasonText}
            onChange={(e) => setReasonText(e.currentTarget.value)}
            minRows={3}
            autosize
            required
          />
          <Group justify="flex-end">
            <Button variant="subtle" color="gray" onClick={() => setReasonPrompt(null)}>
              Abbrechen
            </Button>
            <Button
              color="teal"
              loading={assignMutation.isPending}
              disabled={reasonText.trim().length < REASON_MIN || assignMutation.isPending}
              onClick={() =>
                reasonPrompt &&
                assignMutation.mutate({
                  userId: reasonPrompt.userId,
                  reason: reasonText.trim(),
                })
              }
            >
              {reasonPrompt?.kind === 'release' ? 'Zurückgeben' : 'Zuweisen'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

/**
 * Status direkt im Ticketkopf ändern — inklusive Schnell-Schließen mit optionalem
 * internem Kommentar, ohne den Umweg über den „Bearbeiten"-Abschnitt.
 */
/**
 * Interner Anstupser an den Bearbeiter — Pendant zu den Nudge-Buttons im
 * Kundenportal: interner Kommentar + Glocke/Push/Mail an den Zugewiesenen.
 * Der Kommentar steht sofort im Verlauf (Spiegel zuerst, dann weclapp) — deshalb
 * danach das Cockpit neu laden, sonst sähe es aus, als fehle er.
 */
type NudgeKind = 'REMINDER' | 'STATUS' | 'URGENT' | 'CUSTOM';

const NUDGE_KIND_OPTIONS: { value: NudgeKind; label: string }[] = [
  { value: 'CUSTOM', label: 'Nur mein Text' },
  { value: 'REMINDER', label: '⏰ Erinnern' },
  { value: 'STATUS', label: '❓ Status' },
  { value: 'URGENT', label: '🔥 Dringend' },
];

function TicketNudgeMenu({ ticket }: { ticket: WeclappTicket }) {
  const queryClient = useQueryClient();
  const [textOpen, setTextOpen] = useState(false);
  const [textKind, setTextKind] = useState<NudgeKind>('CUSTOM');
  const [text, setText] = useState('');
  const nudgeMutation = useMutation({
    mutationFn: (data: { kind: NudgeKind; note?: string }) => api.nudgeWeclappTicket(ticket.id, data),
    onSuccess: (res) => {
      void queryClient.invalidateQueries({ queryKey: ['weclapp-cockpit', ticket.id] });
      setTextOpen(false);
      setText('');
      setTextKind('CUSTOM');
      const where =
        res.note === 'PENDING'
          ? ' Der interne Kommentar steht im Ticket und wird nach Weclapp übertragen, sobald es antwortet.'
          : res.note === 'FAILED'
            ? ' Ob der interne Kommentar in Weclapp angekommen ist, ist unklar — bitte dort nachsehen.'
            : ' Der interne Kommentar steht im Ticket.';
      notifications.show({
        title: 'Anstupser gesendet',
        message: `${res.notified} wurde per Glocke, Push und Mail benachrichtigt.${where}`,
        color: res.note === 'FAILED' ? 'yellow' : 'green',
      });
    },
    onError: (e: Error) =>
      notifications.show({ title: 'Anstupser fehlgeschlagen', message: e.message, color: 'red' }),
  });

  const trimmed = text.trim();
  const canSendText = trimmed.length > 0 && trimmed.length <= 1000 && !nudgeMutation.isPending;
  const sendText = () => {
    if (!canSendText) return;
    nudgeMutation.mutate({ kind: textKind, note: trimmed });
  };

  return (
    <>
      <Menu withinPortal zIndex={1100} position="bottom-end" shadow="md">
        <Menu.Target>
          <Tooltip
            label={
              ticket.responsibleUserName
                ? 'Bearbeiter anstupsen — interner Kommentar im Ticket + Glocke, Push und Mail'
                : 'Kein Bearbeiter zugewiesen — Anstupsen geht erst nach der Zuweisung'
            }
          >
            <Button
              size="compact-xs"
              variant="light"
              color="orange"
              leftSection={<IconBellRinging size={13} />}
              loading={nudgeMutation.isPending}
              style={ticket.responsibleUserId ? undefined : { opacity: 0.5 }}
            >
              Anstupsen
            </Button>
          </Tooltip>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>
            {ticket.responsibleUserName
              ? `Anstupsen: ${ticket.responsibleUserName}`
              : 'Kein Bearbeiter — erst zuweisen'}
          </Menu.Label>
          <Menu.Item disabled={!ticket.responsibleUserId} onClick={() => nudgeMutation.mutate({ kind: 'REMINDER' })}>
            ⏰ An das Ticket erinnern
          </Menu.Item>
          <Menu.Item disabled={!ticket.responsibleUserId} onClick={() => nudgeMutation.mutate({ kind: 'STATUS' })}>
            ❓ Nach Status fragen
          </Menu.Item>
          <Menu.Item
            color="red"
            disabled={!ticket.responsibleUserId}
            onClick={() => nudgeMutation.mutate({ kind: 'URGENT' })}
          >
            🔥 Dringend — bitte priorisieren
          </Menu.Item>
          <Menu.Divider />
          <Menu.Item disabled={!ticket.responsibleUserId} onClick={() => setTextOpen(true)}>
            ✍️ Eigener Text …
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
      <Modal
        opened={textOpen}
        onClose={() => {
          if (!nudgeMutation.isPending) setTextOpen(false);
        }}
        title={`Anstupsen: ${ticket.responsibleUserName ?? 'Bearbeiter'}`}
        zIndex={1200}
        centered
      >
        <Stack gap="sm">
          <Textarea
            data-autofocus
            label="Nachricht"
            placeholder="z. B. Kunde hat angerufen, bitte heute noch zurückmelden."
            autosize
            minRows={3}
            maxRows={10}
            value={text}
            onChange={(e) => {
              const __value = e.currentTarget.value;
              setText(__value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                sendText();
              }
            }}
            error={trimmed.length > 1000 ? 'Höchstens 1.000 Zeichen.' : undefined}
            description={`${trimmed.length}/1.000 · Strg/⌘ + Enter sendet`}
          />
          <div>
            <Text size="xs" c="dimmed" mb={4}>
              Baustein davor (optional)
            </Text>
            <SegmentedControl
              size="xs"
              fullWidth
              value={textKind}
              onChange={(v) => setTextKind(v as NudgeKind)}
              data={NUDGE_KIND_OPTIONS}
            />
          </div>
          <Text size="xs" c="dimmed">
            Steht als interner Kommentar im Ticket, dazu Glocke, Push und Mail an den Bearbeiter.
          </Text>
          <Group justify="flex-end" gap="xs">
            <Button variant="default" size="xs" onClick={() => setTextOpen(false)} disabled={nudgeMutation.isPending}>
              Abbrechen
            </Button>
            <Button
              size="xs"
              color="orange"
              leftSection={<IconBellRinging size={13} />}
              onClick={sendText}
              disabled={!canSendText}
              loading={nudgeMutation.isPending}
            >
              Anstupsen
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

/** Ergebnis eines Tickets in der Massen-Schließen-Aktion. */
interface BulkCloseResult {
  id: string;
  ticketNumber: string;
  ok: boolean;
  error?: string;
  /** z. B. „Kunde zugeordnet: X" — automatische Zuordnung vor dem Schließen. */
  note?: string;
}

/** Kleine Kunden-Suche zum Ändern des Zwilling-Vorschlags im Massen-Schließen. */
function TwinCustomerPicker({
  onPick,
}: {
  onPick: (c: { customerId: string; customerName: string }) => void;
}) {
  const [opened, setOpened] = useState(false);
  const [q, setQ] = useState('');
  const [dq] = useDebouncedValue(q, 300);
  const { data } = useQuery({
    queryKey: ['twin-customer-search', dq],
    queryFn: () => api.searchHubCustomers(dq, 1, 20),
    enabled: opened && dq.trim().length >= 2,
    staleTime: 60_000,
  });
  const results = data?.customers ?? [];
  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      withinPortal
      zIndex={1200}
      width={320}
      position="bottom-start"
      closeOnClickOutside={false}
    >
      <Popover.Target>
        <Button size="compact-xs" variant="subtle" onClick={() => setOpened((o) => !o)}>
          ändern
        </Button>
      </Popover.Target>
      <Popover.Dropdown p="xs">
        <Stack gap={6}>
          <Group gap={4} wrap="nowrap">
            <TextInput
              size="xs"
              placeholder="Kunde suchen (min. 2 Zeichen)…"
              value={q}
              onChange={(e) => setQ(e.currentTarget.value)}
              style={{ flex: 1 }}
              autoFocus
            />
            <ActionIcon size="sm" variant="subtle" color="gray" onClick={() => setOpened(false)} aria-label="Schließen">
              <IconX size={12} />
            </ActionIcon>
          </Group>
          <ScrollArea.Autosize mah={180}>
            <Stack gap={2}>
              {results.map((c) => (
                <UnstyledButton
                  key={c.id}
                  onClick={() => {
                    onPick({ customerId: c.id, customerName: c.name });
                    setOpened(false);
                    setQ('');
                  }}
                >
                  <Text size="xs">
                    {c.name}
                    {c.customerNumber ? ` (${c.customerNumber})` : ''}
                  </Text>
                </UnstyledButton>
              ))}
              {dq.trim().length >= 2 && results.length === 0 && (
                <Text size="xs" c="dimmed">Kein Treffer</Text>
              )}
            </Stack>
          </ScrollArea.Autosize>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

/** „vor 3 Std." / „vor 12 Tagen" — kompaktes Alter für die Zwillings-Liste. */
function formatAgeShort(iso: string): string {
  const hours = Math.max(0, (Date.now() - new Date(iso).getTime()) / 3_600_000);
  if (hours < 24) return `vor ${Math.round(hours)} Std.`;
  const days = Math.round(hours / 24);
  if (days < 60) return `vor ${days} Tag${days === 1 ? '' : 'en'}`;
  return `vor ${Math.round(days / 30)} Monaten`;
}

/** Schnellbausteine für die häufigsten Abschluss-Gründe im Schließen-Dialog. */
export const CLOSE_COMMENT_PRESETS = [
  'Reine Info-Mail, kein Handlungsbedarf',
  'Telefonisch mit dem Kunden geklärt',
  'Duplikat eines anderen Tickets',
  'Automatische System-Mail, keine Aktion nötig',
  'Vom Kunden bestätigt — erledigt',
];

function StatusQuickMenu({ ticket }: { ticket: WeclappTicket }) {
  const queryClient = useQueryClient();
  const [closeOpen, setCloseOpen] = useState(false);
  const [closeComment, setCloseComment] = useState('');
  // EINE Idempotenz-Referenz pro geöffnetem Dialog: schlägt der Statuswechsel nach
  // bereits gepostetem Kommentar fehl (z. B. Bearbeiter-Pflicht), postet der zweite
  // Versuch den Kommentar nicht erneut — vorher entstanden Doppel-Kommentare.
  const closeExtRef = useRef<string>(crypto.randomUUID());
  useEffect(() => {
    if (closeOpen) closeExtRef.current = crypto.randomUUID();
  }, [closeOpen]);

  // Massen-Schließen: offene Tickets mit identischem Betreff + gleichem Ausgangszustand
  // (Status/Zuweisung) als abwählbare Vorschläge — z. B. wiederkehrende Info-Mails.
  const sameStateQuery = useQuery({
    queryKey: ['weclapp-same-state', ticket.id],
    queryFn: () => api.getWeclappSameStateTickets(ticket.id),
    enabled: closeOpen,
    staleTime: 30_000,
  });
  const sameStateTickets = sameStateQuery.data?.tickets ?? [];
  const [bulkSelected, setBulkSelected] = useState<Set<string>>(new Set());
  /** Ergebnis-Übersicht nach der Massen-Aktion (✓/✗ pro Ticket). */
  const [bulkReport, setBulkReport] = useState<BulkCloseResult[] | null>(null);
  useEffect(() => {
    // Beim Öffnen alle Vorschläge vorausgewählt — der Anwendungsfall ist ja gerade
    // „alle auf einmal weg"; einzelne lassen sich abwählen.
    if (closeOpen) setBulkSelected(new Set(sameStateTickets.map((t) => t.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeOpen, sameStateQuery.data]);
  // Kunden-Wahl pro Zwilling ohne Kunde — vorbelegt mit dem Server-Vorschlag
  // (identische Quelle → Kunde des Ausgangstickets, sonst KIAN-Kandidat), änderbar.
  const [twinCustomer, setTwinCustomer] = useState<
    Map<string, { customerId: string; customerName: string } | null>
  >(new Map());
  useEffect(() => {
    if (!closeOpen) return;
    const next = new Map<string, { customerId: string; customerName: string } | null>();
    for (const t of sameStateTickets) {
      if (isCustomerMissing(t.partyName)) {
        next.set(
          t.id,
          t.suggestion
            ? { customerId: t.suggestion.customerId, customerName: t.suggestion.customerName }
            : null,
        );
      }
    }
    setTwinCustomer(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closeOpen, sameStateQuery.data]);
  // Zwillings-Sektion als Konstante — sie erscheint sowohl im Schließen-Formular als
  // auch im Kundenzuordnungs-Schritt (Tickets ohne Kunde), damit der Massen-Vorschlag
  // nie unsichtbar bleibt. Die Auswahl (bulkSelected) überlebt den Zweig-Wechsel.
  const sameStateSection = sameStateTickets.length > 0 && (
    <Paper withBorder p="xs" radius="sm">
      <Group justify="space-between" mb={2}>
        <Text size="xs" fw={600}>
          {sameStateTickets.length} weitere Tickets mit identischem Betreff und Status
        </Text>
        <Button
          size="compact-xs"
          variant="subtle"
          onClick={() =>
            setBulkSelected((prev) =>
              prev.size === sameStateTickets.length
                ? new Set()
                : new Set(sameStateTickets.map((t) => t.id))
            )
          }
        >
          {bulkSelected.size === sameStateTickets.length ? 'Keine' : 'Alle'}
        </Button>
      </Group>
      {/* Betreff einmal im Kopf — er ist bei allen Treffern per Definition identisch */}
      <Text size="xs" c="dimmed" mb={6} style={{ overflowWrap: 'anywhere' }}>
        „{sameStateTickets[0]?.subject ?? ticket.subject}"
      </Text>
      <ScrollArea.Autosize mah={260}>
        <Stack gap={6}>
          {sameStateTickets.map((t) => (
            <Stack key={t.id} gap={2}>
              <Group gap={6} wrap="nowrap" justify="space-between">
                <Switch
                  size="xs"
                  style={{ flex: 1, minWidth: 0 }}
                  checked={bulkSelected.has(t.id)}
                  onChange={(e) => {
                    const on = e.currentTarget.checked;
                    setBulkSelected((prev) => {
                      const next = new Set(prev);
                      if (on) next.add(t.id);
                      else next.delete(t.id);
                      return next;
                    });
                  }}
                  label={
                    <Group gap={6} wrap="wrap">
                      <Text size="xs" ff="monospace">#{t.ticketNumber ?? t.id}</Text>
                      <Text size="xs" c={t.partyName ? undefined : 'orange'}>
                        {t.partyName ?? 'Kunde fehlt'}
                      </Text>
                      {t.status && (
                        <Text size="xs" style={{ color: weclappStatusColorVar(t.status, undefined, 4) }}>
                          {t.status}
                        </Text>
                      )}
                      {t.createdDate && (
                        <Text size="xs" c="dimmed">
                          {new Date(t.createdDate).toLocaleDateString('de-DE')} ·{' '}
                          {formatAgeShort(t.createdDate)}
                        </Text>
                      )}
                    </Group>
                  }
                />
                {/* Schnell-Durchsicht: Von/An/CC + Inhalts-Preview per Hover */}
                <Tooltip
                  multiline
                  w={380}
                  label={
                    <Stack gap={2}>
                      <Text size="xs">Von: {t.senderEmail ?? '—'}</Text>
                      <Text size="xs">An: {t.toRecipients?.join(', ') || '—'}</Text>
                      {(t.ccRecipients?.length ?? 0) > 0 && (
                        <Text size="xs">CC: {t.ccRecipients.join(', ')}</Text>
                      )}
                      {t.descriptionPreview && (
                        <Text size="xs" c="dimmed" lineClamp={8} style={{ whiteSpace: 'pre-wrap' }}>
                          {t.descriptionPreview}
                        </Text>
                      )}
                    </Stack>
                  }
                >
                  <ActionIcon variant="subtle" color="gray" size="sm" style={{ flexShrink: 0 }} aria-label="Details">
                    <IconInfoCircle size={13} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label="Ticket in neuem Tab ansehen">
                  <ActionIcon
                    component="a"
                    href={`/t/${t.ticketNumber ?? t.id}`}
                    target="_blank"
                    rel="noreferrer"
                    variant="subtle"
                    color="gray"
                    size="sm"
                    style={{ flexShrink: 0 }}
                    aria-label={`Ticket ${t.ticketNumber ?? t.id} öffnen`}
                  >
                    <IconExternalLink size={13} />
                  </ActionIcon>
                </Tooltip>
              </Group>
              {/* Kunde fehlt → Vorschlag anzeigen (identische Quelle bzw. KIAN) + änderbar */}
              {isCustomerMissing(t.partyName) && bulkSelected.has(t.id) && (
                <Group gap={6} pl={34} wrap="wrap">
                  {(() => {
                    const choice = twinCustomer.get(t.id) ?? null;
                    if (!choice) {
                      return (
                        <Badge size="xs" variant="light" color="red">
                          Kunde fehlt — wird übersprungen
                        </Badge>
                      );
                    }
                    const isSuggested = t.suggestion && choice.customerId === t.suggestion.customerId;
                    return (
                      <Badge
                        size="xs"
                        variant="light"
                        color={isSuggested && t.suggestion!.source === 'IDENTICAL' ? 'teal' : 'violet'}
                      >
                        Kunde: {choice.customerName}
                        {isSuggested
                          ? t.suggestion!.source === 'IDENTICAL'
                            ? ' · identische Quelle'
                            : ' · KIAN-Vorschlag'
                          : ' · manuell'}
                      </Badge>
                    );
                  })()}
                  <TwinCustomerPicker
                    onPick={(c) => setTwinCustomer((prev) => new Map(prev).set(t.id, c))}
                  />
                </Group>
              )}
            </Stack>
          ))}
        </Stack>
      </ScrollArea.Autosize>
      {bulkSelected.size > 0 && (
        <Text size="xs" c="orange" mt={6}>
          {bulkSelected.size} Ticket{bulkSelected.size === 1 ? '' : 's'} werden mit
          demselben Kommentar und Status mitgeschlossen.
        </Text>
      )}
    </Paper>
  );

  const { user: statusUser } = useAuthStore();
  // Weclapp verlangt für interne Status wie CLOSED einen zugewiesenen Bearbeiter
  // („assigned user required"). Hat das Ticket keinen, wird beim Schließen der
  // schließende Techniker mitgesetzt — sonst schlägt der Statuswechsel mit 400 fehl.
  const assigneeFix =
    !ticket.responsibleUserId && statusUser?.weclappUserId
      ? { responsibleUserId: statusUser.weclappUserId }
      : {};

  const { data: metaData } = useQuery({
    queryKey: ['weclapp-metadata'],
    queryFn: () => api.getWeclappMetadata(),
    staleTime: 5 * 60 * 1000,
  });
  const statuses = metaData?.statuses ?? [];
  const closedStatus = statuses.find((s) =>
    /\b(geschlossen|abgeschlossen|erledigt|closed|cancelled|storniert)\b/i.test(s.name)
  );
  // Weclapp verlangt für alle intern geschlossenen Status (CLOSED/FIXED) einen
  // Bearbeiter — auch für grau eingefärbte wie „Duplikat" oder „Spam". Die reine
  // Farb-Heuristik (grün/teal/cyan) übersah genau diese und ließ den Statuswechsel
  // mit Weclapp-400 „assigned user required" scheitern.
  //
  // Und nicht nur für geschlossene: JEDER Status außer „Noch nicht zugewiesen" und Pool
  // verlangt einen („assigned user required for internal ticket status: IN_PROGRESS",
  // v1.360.2). Ist der interne Status bekannt, entscheidet er; sonst die alte Heuristik.
  const needsAssignee = (s: (typeof statuses)[number]) => {
    const internal = (s.internalTicketStatus ?? s.systemStatus)?.toUpperCase();
    if (internal) return internal !== 'UNASSIGNED' && internal !== 'POOL';
    return (
      s.openClosed?.effective === 'closed' ||
      ['green', 'teal', 'cyan'].includes(weclappStatusColor(s.name, s.systemStatus ?? s.internalTicketStatus))
    );
  };
  /** Status, für den gerade der Bearbeiter abgefragt wird (TicketGapsModal). */
  const [statusNeedingAssignee, setStatusNeedingAssignee] = useState<(typeof statuses)[number] | null>(null);
  /**
   * Statusmails, Regel 5: Das Ticket war schon einmal erledigt — vor dem Wechsel fragen, ob der Kunde
   * die weclapp-Statusmail bekommt (vorbelegt mit dem Vorschlag des Servers).
   */
  const [mailAsk, setMailAsk] = useState<{
    statusId: string;
    statusName: string;
    assignee: AssigneeChoice | null;
    preview: StatusMailPreview;
    notify: boolean;
  } | null>(null);
  const [closeNotify, setCloseNotify] = useState<boolean | null>(null);
  const { data: closeMailPreview } = useQuery({
    queryKey: ['weclapp-status-mail', ticket.id, closedStatus?.id],
    queryFn: () => api.getWeclappStatusMailPreview(ticket.id, closedStatus!.id),
    enabled: closeOpen && !!closedStatus,
    staleTime: 0,
  });
  useEffect(() => {
    if (closeOpen) setCloseNotify(null);
  }, [closeOpen]);
  const closeNotifyValue = closeMailPreview?.offerChoice ? (closeNotify ?? closeMailPreview.decision.notify) : undefined;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['weclapp-cockpit', ticket.id] });
    queryClient.invalidateQueries({ queryKey: ['weclapp-ticket', ticket.id] });
    queryClient.invalidateQueries({ queryKey: ['weclapp-tickets'] });
    queryClient.invalidateQueries({ queryKey: ['my-weclapp-tickets'] });
    queryClient.invalidateQueries({ queryKey: ['weclapp-dispatch-board'] });
    // Listen-Ansichten (Alle/Ohne Kunde/Team, Kanban) sofort nachziehen — der Spiegel
    // ist nach dem PATCH bereits synchron aktualisiert, nur der Query-Cache hielt
    // sonst bis zu 15 s den alten Stand („Ticket geschlossen, Liste zeigt es offen").
    queryClient.invalidateQueries({ queryKey: ['all-weclapp-tickets'] });
    queryClient.invalidateQueries({ queryKey: ['team-weclapp-tickets'] });
    queryClient.invalidateQueries({ queryKey: ['kanban'] });
  };

  const statusMutation = useMutation({
    // Abschluss-artige Status (gelöst/geschlossen/bestätigt) verlangen in Weclapp
    // einen Bearbeiter — ohne einen wird der Statuswechsel abgelehnt (400).
    // Bearbeiter und Status gehen im SELBEN PUT: Weclapp lehnt den Status ohne Bearbeiter ab
    // und den Bearbeiter unter „Noch nicht zugewiesen" ebenso.
    mutationFn: (v: string | { statusId: string; assignee: AssigneeChoice | null; notifyCustomer?: boolean }) => {
      const { statusId, assignee, notifyCustomer } =
        typeof v === 'string' ? { statusId: v, assignee: null, notifyCustomer: undefined } : v;
      const target = statuses.find((s) => s.id === statusId);
      const closing = target ? needsAssignee(target) : false;
      return api.updateWeclappTicket(ticket.id, {
        statusId,
        ...(assignee
          ? { responsibleUserId: assignee.userId, ...(assignee.reason ? { assignReason: assignee.reason } : {}) }
          : closing
            ? assigneeFix
            : {}),
        ...(notifyCustomer !== undefined ? { notifyCustomer } : {}),
      });
    },
    // Der Wechsel läuft über die Weclapp-API und dauert 1–3 s — ohne Hinweis sieht
    // das nach „hängt" aus. Ladehinweis sofort, dieselbe Notification wird bei
    // Erfolg/Fehler aktualisiert.
    onMutate: (v) => {
      const statusId = typeof v === 'string' ? v : v.statusId;
      notifications.show({
        id: `weclapp-status-${ticket.id}`,
        loading: true,
        title: 'Status wird in Weclapp gesetzt…',
        message: statuses.find((s) => s.id === statusId)?.name ?? '',
        autoClose: false,
        withCloseButton: false,
      });
    },
    onSuccess: (result, v) => {
      const statusId = typeof v === 'string' ? v : v.statusId;
      invalidate();
      // Der Ladehinweis kündigt Weclapp an; ist es nicht erreichbar, muss die Endmeldung das
      // richtigstellen — der Status steht dann zunächst nur im Portal.
      const waiting = result?.pending === true;
      notifications.update({
        id: `weclapp-status-${ticket.id}`,
        loading: false,
        title: waiting ? 'Im Portal gesetzt — Weclapp folgt' : 'Status geändert',
        message: waiting
          ? (result?.message ?? '')
          : `${statuses.find((s) => s.id === statusId)?.name ?? ''}${
              // Statusmails: sagen, wenn der Kunde bewusst keine Mail bekam (Korrektur, Häkchen aus).
              result?.statusMail && !result.statusMail.notify
                ? ' · ohne Statusmail an den Kunden'
                : result?.statusMail?.templatesDisabled
                  ? ' · Mailvorlagen am Ticket aus — keine Statusmail'
                  : ''
            }`,
        color: waiting ? 'yellow' : 'green',
        autoClose: waiting ? 9000 : 2500,
        withCloseButton: true,
      });
    },
    onError: (e: Error) =>
      notifications.update({
        id: `weclapp-status-${ticket.id}`,
        loading: false,
        title: 'Statuswechsel fehlgeschlagen',
        message: e.message,
        color: 'red',
        autoClose: 8000,
        withCloseButton: true,
      }),
  });

  /**
   * Statuswechsel aus dem Menü bzw. nach der Bearbeiter-Abfrage. War das Ticket schon einmal erledigt
   * und geht es wieder auf gelöst/geschlossen, erst das Häkchen „Kunde benachrichtigen“ zeigen.
   * Ist der Vorschlag nicht abrufbar, entscheidet der Server allein (Regeln 1–4).
   */
  const changeStatus = async (s: (typeof statuses)[number], assignee: AssigneeChoice | null) => {
    const preview = await api.getWeclappStatusMailPreview(ticket.id, s.id).catch(() => null);
    if (preview?.offerChoice) {
      setMailAsk({ statusId: s.id, statusName: s.name, assignee, preview, notify: preview.decision.notify });
      return;
    }
    await statusMutation.mutateAsync({ statusId: s.id, assignee });
  };

  // KIAN formuliert einen Abschluss-Kommentar aus Ticket + Verlauf (postet nichts).
  const closeDraftMutation = useMutation({
    mutationFn: () =>
      api.draftWeclappTicketComment(ticket.id, {
        draft: closeComment.trim() || undefined,
        internal: true,
      }),
    onSuccess: (res) => setCloseComment(res.text),
    onError: (e: Error) =>
      notifications.show({ title: 'KIAN-Entwurf fehlgeschlagen', message: e.message, color: 'red' }),
  });

  const closeMutation = useMutation({
    mutationFn: async () => {
      if (!closedStatus) throw new Error('Kein Abschluss-Status in Weclapp gefunden');
      // Vorab-Prüfung statt Weclapp-400: CLOSED verlangt einen Bearbeiter, und ohne
      // eigene Weclapp-Verknüpfung kann das Portal den Schließenden nicht eintragen.
      if (!ticket.responsibleUserId && !statusUser?.weclappUserId) {
        throw new Error(
          'Weclapp verlangt für den Abschluss einen Bearbeiter. Bitte zuerst über ZUWEISEN einen Techniker setzen — oder deine eigene Weclapp-Verknüpfung in der Benutzerverwaltung hinterlegen.',
        );
      }
      if (closeComment.trim()) {
        notifications.update({
          id: `weclapp-close-${ticket.id}`,
          loading: true,
          title: 'Ticket wird geschlossen…',
          message: 'Schritt 1/2: Abschluss-Kommentar wird gepostet',
          autoClose: false,
          withCloseButton: false,
        });
        await api.addWeclappTicketComment(ticket.id, {
          comment: closeComment.trim(),
          internal: true,
          externalReference: closeExtRef.current,
        });
      }
      notifications.update({
        id: `weclapp-close-${ticket.id}`,
        loading: true,
        title: 'Ticket wird geschlossen…',
        message: closeComment.trim() ? 'Schritt 2/2: Status wird in Weclapp gesetzt' : 'Status wird in Weclapp gesetzt',
        autoClose: false,
        withCloseButton: false,
      });
      await api.updateWeclappTicket(ticket.id, {
        statusId: closedStatus.id,
        ...assigneeFix,
        ...(closeNotifyValue !== undefined ? { notifyCustomer: closeNotifyValue } : {}),
      });

      // Massen-Aktion: ausgewählte Betreff-Zwillinge sequentiell mit demselben Vorgang
      // schließen (gleicher Kommentar, gleicher Zielstatus, Bearbeiter-Fix pro Ticket).
      // Einzelfehler brechen den Rest nicht ab — Zusammenfassung am Ende.
      const bulkIds = sameStateTickets.filter((t) => bulkSelected.has(t.id)).map((t) => t.id);
      const bulkResults: BulkCloseResult[] = [];
      for (let i = 0; i < bulkIds.length; i++) {
        const id = bulkIds[i]!;
        const row = sameStateTickets.find((t) => t.id === id);
        notifications.update({
          id: `weclapp-close-${ticket.id}`,
          loading: true,
          title: 'Gleiche Tickets werden geschlossen…',
          message: `${i + 1}/${bulkIds.length} (#${row?.ticketNumber ?? id})`,
          autoClose: false,
          withCloseButton: false,
        });
        try {
          // Kundenpflicht: Zwilling ohne Kunde bekommt erst die getroffene Wahl
          // (übernommen/KIAN/manuell) zugeordnet — ohne Wahl wird NICHT geschlossen.
          let note: string | undefined;
          if (row && isCustomerMissing(row.partyName)) {
            const choice = twinCustomer.get(id) ?? null;
            if (!choice) {
              bulkResults.push({
                id,
                ticketNumber: row.ticketNumber ?? id,
                ok: false,
                error: 'Übersprungen — Kunde fehlt und keine Zuordnung gewählt',
              });
              continue;
            }
            await api.assignWeclappTicketCustomer(id, {
              customerId: choice.customerId,
              senderEmail: row.senderEmail ?? undefined,
            });
            note = `Kunde zugeordnet: ${choice.customerName}`;
          }
          if (closeComment.trim()) {
            await api.addWeclappTicketComment(id, {
              comment: closeComment.trim(),
              internal: true,
              externalReference: crypto.randomUUID(),
            });
          }
          await api.updateWeclappTicket(id, { statusId: closedStatus.id, ...assigneeFix });
          bulkResults.push({ id, ticketNumber: row?.ticketNumber ?? id, ok: true, note });
        } catch (err) {
          bulkResults.push({
            id,
            ticketNumber: row?.ticketNumber ?? id,
            ok: false,
            error: err instanceof Error ? err.message : 'Unbekannter Fehler',
          });
        }
      }
      return { bulkResults };
    },
    onMutate: () => {
      // Modal sofort schließen — der Fortschritt läuft oben rechts als Notification
      // weiter (auch die Massen-Aktion), man kann direkt weiterarbeiten. Die
      // Ergebnis-Übersicht öffnet sich am Ende von selbst.
      setCloseOpen(false);
      notifications.show({
        id: `weclapp-close-${ticket.id}`,
        loading: true,
        title: 'Ticket wird geschlossen…',
        message: '',
        autoClose: false,
        withCloseButton: false,
      });
    },
    onSuccess: (res) => {
      invalidate();
      queryClient.invalidateQueries({ queryKey: ['weclapp-cockpit', ticket.id] });
      setCloseOpen(false);
      setCloseComment('');
      const results = res?.bulkResults ?? [];
      const failed = results.filter((r) => !r.ok);
      // Ergebnis nicht mehr automatisch aufreißen: die Notification zeigt „x/x" und
      // öffnet die Übersicht erst auf Klick — man kann ungestört weiterarbeiten.
      notifications.update({
        id: `weclapp-close-${ticket.id}`,
        loading: false,
        title:
          results.length > 0
            ? `Ticket + ${results.length - failed.length}/${results.length} gleiche geschlossen`
            : 'Ticket geschlossen',
        message:
          results.length > 0 ? (
            <Group gap="xs">
              {failed.length > 0 && (
                <Text size="sm" c="red.5" fw={600}>
                  {failed.length} fehlgeschlagen
                </Text>
              )}
              <Button
                size="compact-xs"
                variant="light"
                onClick={() => {
                  notifications.hide(`weclapp-close-${ticket.id}`);
                  setBulkReport(results);
                }}
              >
                Übersicht öffnen
              </Button>
            </Group>
          ) : (
            closedStatus?.name ?? ''
          ),
        color: failed.length > 0 ? 'yellow' : 'green',
        // Mit Massen-Ergebnis stehen lassen, bis der Nutzer klickt oder wegklickt.
        autoClose: results.length > 0 ? false : 2500,
        withCloseButton: true,
      });
    },
    onError: (e: Error) =>
      notifications.update({
        id: `weclapp-close-${ticket.id}`,
        loading: false,
        title: 'Schließen fehlgeschlagen',
        message: e.message,
        color: 'red',
        autoClose: 8000,
        withCloseButton: true,
      }),
  });

  return (
    <>
      <Menu withinPortal zIndex={1100} position="bottom-start" shadow="md">
        <Menu.Target>
          <Tooltip label="Status ändern">
            <Badge
              size="sm"
              variant="light"
              color={weclappStatusColor(ticket.status)}
              style={{ cursor: 'pointer' }}
              rightSection={<IconChevronDown size={10} />}
            >
              {statusMutation.isPending || closeMutation.isPending ? '…' : ticket.status}
            </Badge>
          </Tooltip>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>Status ändern</Menu.Label>
          {statuses.map((s) => (
            <Menu.Item
              key={s.id}
              // Eintragsfarbe = Statusfarbe: das Dropdown zeigt den Lebenszyklus als Skala
              color={weclappStatusColor(s.name, s.systemStatus ?? s.internalTicketStatus)}
              leftSection={
                s.name === ticket.status ? (
                  <IconCheck size={12} />
                ) : (
                  <span
                    style={{
                      display: 'inline-block',
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      background: weclappStatusColorVar(s.name, s.systemStatus ?? s.internalTicketStatus),
                    }}
                  />
                )
              }
              disabled={s.name === ticket.status || statusMutation.isPending}
              onClick={() => {
                // Vorab-Prüfung statt API-Fehler: abschlussartige Status verlangen in
                // Weclapp einen Bearbeiter — und unsere Regel einen Kunden. Die
                // Kundenpflicht gilt bewusst nur für Lösungs-Status (grün/teal/cyan),
                // nicht für Entsorgungs-Status wie Duplikat/Spam — die haben oft
                // gar keinen Kunden.
                const resolving = ['green', 'teal', 'cyan'].includes(
                  weclappStatusColor(s.name, s.systemStatus ?? s.internalTicketStatus),
                );
                if (resolving && isCustomerMissing(ticket.partyName)) {
                  notifications.show({
                    title: 'Kunde fehlt',
                    message: `„${s.name}" braucht eine Kundenzuordnung — der Zuordnungs-Dialog öffnet sich.`,
                    color: 'orange',
                    autoClose: 5000,
                  });
                  setCloseOpen(true);
                  return;
                }
                // Kein Bearbeiter, aber der Status verlangt einen: fragen, wer es wird
                // (vorbelegt: ich) — statt still sich selbst einzutragen oder an Weclapps
                // 400 zu scheitern.
                if (needsAssignee(s) && !ticket.responsibleUserId) {
                  setStatusNeedingAssignee(s);
                  return;
                }
                // Fehler zeigt die Mutation selbst (onError).
                void changeStatus(s, null).catch(() => undefined);
              }}
            >
              {s.name}
            </Menu.Item>
          ))}
          {closedStatus && (
            <>
              <Menu.Divider />
              <Menu.Item color="teal" leftSection={<IconCheck size={12} />} onClick={() => setCloseOpen(true)}>
                Schließen (mit/ohne Kommentar)…
              </Menu.Item>
            </>
          )}
        </Menu.Dropdown>
      </Menu>

      {/* Schließen ist die häufigste Statusaktion — direkter Button statt Weg übers Menü.
          Bei bereits geschlossenen/gelösten Tickets wäre er nur Rauschen. */}
      {closedStatus && !['green', 'teal', 'gray'].includes(weclappStatusColor(ticket.status)) && (
        <Button
          size="compact-xs"
          variant="light"
          color="green"
          leftSection={<IconCheck size={12} />}
          loading={closeMutation.isPending}
          onClick={() => setCloseOpen(true)}
        >
          Schließen
        </Button>
      )}

      <TicketGapsModal
        opened={statusNeedingAssignee !== null}
        onClose={() => setStatusNeedingAssignee(null)}
        ticket={ticket}
        gaps={['assignee']}
        title={`Status „${statusNeedingAssignee?.name ?? ''}" setzen`}
        intro="Für diesen Status verlangt Weclapp einen Bearbeiter, und das Ticket hat noch keinen. Wer soll es werden?"
        finishLabel="Bearbeiter setzen und Status ändern"
        onFinish={async (assignee) => {
          if (!statusNeedingAssignee) return;
          await changeStatus(statusNeedingAssignee, assignee);
        }}
      />

      <Modal
        opened={mailAsk !== null}
        onClose={() => setMailAsk(null)}
        title={`Status „${mailAsk?.statusName ?? ''}" setzen`}
        zIndex={700}
        centered
      >
        {mailAsk && (
          <Stack gap="sm">
            <StatusMailCheckbox
              preview={mailAsk.preview}
              checked={mailAsk.notify}
              onChange={(notify) => setMailAsk((prev) => (prev ? { ...prev, notify } : prev))}
            />
            <Group justify="flex-end" gap="sm">
              <Button variant="subtle" onClick={() => setMailAsk(null)}>
                Abbrechen
              </Button>
              <Button
                loading={statusMutation.isPending}
                onClick={() => {
                  const ask = mailAsk;
                  setMailAsk(null);
                  statusMutation.mutate({ statusId: ask.statusId, assignee: ask.assignee, notifyCustomer: ask.notify });
                }}
              >
                Status setzen
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      <Modal
        opened={closeOpen}
        onClose={() => setCloseOpen(false)}
        title="Ticket schließen"
        zIndex={700}
        centered
        size={isCustomerMissing(ticket.partyName) ? 'lg' : 'md'}
      >
        {/* Ohne Kundenzuordnung wird nicht geschlossen: erst zuordnen (sonst fehlt das
            Ticket in Serviceberichten und Auswertungen), dann erscheint der Abschluss. */}
        {isCustomerMissing(ticket.partyName) ? (
          <Stack gap="sm">
            <Alert color="orange" variant="light" icon={<IconAlertTriangle size={16} />}>
              Dieses Ticket hat noch keinen Kunden. Bitte zuerst zuordnen — danach kannst du
              hier direkt schließen.
            </Alert>
            <CustomerMatchPanel ticket={ticket} />
            {/* Zwillinge schon hier zeigen — sonst wirkt es, als gäbe es keinen
                Massen-Vorschlag, obwohl er nach der Zuordnung greift. */}
            {sameStateSection && (
              <>
                {sameStateSection}
                <Text size="xs" c="dimmed">
                  Nach der Kundenzuordnung werden die ausgewählten Tickets im selben Vorgang
                  mitgeschlossen.
                </Text>
              </>
            )}
          </Stack>
        ) : (
        <Stack gap="sm">
          {/* Bearbeiter-Voraussetzung sichtbar machen, BEVOR der Klick in den Fehler läuft. */}
          {!ticket.responsibleUserId && !statusUser?.weclappUserId && (
            <Alert color="orange" variant="light" icon={<IconAlertTriangle size={16} />}>
              Kein Bearbeiter zugewiesen — Weclapp lehnt den Abschluss dann ab. Bitte zuerst
              über ZUWEISEN einen Techniker setzen oder deine eigene Weclapp-Verknüpfung in
              der Benutzerverwaltung hinterlegen.
            </Alert>
          )}
          {/* Schnellbausteine für die häufigsten Abschluss-Gründe — Klick füllt das Feld. */}
          <Group gap={6} wrap="wrap">
            {CLOSE_COMMENT_PRESETS.map((preset) => (
              <Button
                key={preset}
                size="compact-xs"
                variant={closeComment === preset ? 'filled' : 'light'}
                color="gray"
                onClick={() => setCloseComment(preset)}
              >
                {preset}
              </Button>
            ))}
            <Button
              size="compact-xs"
              variant="light"
              color="grape"
              leftSection={<IconSparkles size={12} />}
              loading={closeDraftMutation.isPending}
              onClick={() => closeDraftMutation.mutate()}
            >
              KIAN vorschlagen
            </Button>
          </Group>
          <Textarea
            label="Interner Abschluss-Kommentar (optional)"
            placeholder="Was wurde gemacht / warum wird geschlossen?"
            value={closeComment}
            onChange={(e) => setCloseComment(e.currentTarget.value)}
            autosize
            minRows={3}
          />

          {/* Statusmails, Regel 5: schon einmal erledigt → Häkchen „Kunde benachrichtigen“. */}
          {closeMailPreview?.offerChoice && closeNotifyValue !== undefined && (
            <StatusMailCheckbox preview={closeMailPreview} checked={closeNotifyValue} onChange={setCloseNotify} />
          )}

          {/* Betreff-Zwillinge: gleicher Betreff + gleicher Ausgangszustand → im selben
              Vorgang mitschließen (vorausgewählt, einzeln abwählbar). */}
          {sameStateSection}
          <Group justify="flex-end" gap="sm">
            <Button variant="subtle" onClick={() => setCloseOpen(false)}>
              Abbrechen
            </Button>
            <Button color="teal" loading={closeMutation.isPending} onClick={() => closeMutation.mutate()}>
              {closeComment.trim() ? 'Kommentieren & schließen' : 'Ohne Kommentar schließen'}
            </Button>
          </Group>
        </Stack>
        )}
      </Modal>
      {/* Ergebnis-Übersicht nach der Massen-Aktion: was hat geklappt, was nicht. */}
      <Modal
        opened={bulkReport !== null}
        onClose={() => setBulkReport(null)}
        title="Ergebnis der Massen-Aktion"
        zIndex={750}
        centered
      >
        {bulkReport && (
          <Stack gap="sm">
            <Group gap="xs">
              <Badge color="green" variant="light">
                {bulkReport.filter((r) => r.ok).length} geschlossen
              </Badge>
              {bulkReport.some((r) => !r.ok) && (
                <Badge color="red" variant="light">
                  {bulkReport.filter((r) => !r.ok).length} fehlgeschlagen
                </Badge>
              )}
            </Group>
            <ScrollArea.Autosize mah={320}>
              <Stack gap={4}>
                {[...bulkReport].sort((a, b) => Number(a.ok) - Number(b.ok)).map((r) => (
                  <Group key={r.id} gap={6} wrap="nowrap" align="flex-start">
                    {r.ok ? (
                      <IconCheck size={14} style={{ color: 'var(--mantine-color-green-5)', flexShrink: 0, marginTop: 2 }} />
                    ) : (
                      <IconAlertTriangle size={14} style={{ color: 'var(--mantine-color-red-5)', flexShrink: 0, marginTop: 2 }} />
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Text size="xs" ff="monospace" component="a" href={`/t/${r.ticketNumber}`} target="_blank" rel="noreferrer" c={r.ok ? undefined : 'red'} style={{ textDecoration: 'underline dotted' }}>
                        #{r.ticketNumber}
                      </Text>
                      {!r.ok && r.error && (
                        <Text size="xs" c="dimmed" style={{ overflowWrap: 'anywhere' }}>
                          {r.error}
                        </Text>
                      )}
                      {r.ok && r.note && (
                        <Text size="xs" c="teal.5" style={{ overflowWrap: 'anywhere' }}>
                          {r.note}
                        </Text>
                      )}
                    </div>
                  </Group>
                ))}
              </Stack>
            </ScrollArea.Autosize>
            {bulkReport.some((r) => !r.ok) && (
              <Text size="xs" c="dimmed">
                Fehlgeschlagene Tickets sind unverändert offen — über den Link einzeln prüfen
                und schließen.
              </Text>
            )}
            <Group justify="flex-end">
              <Button size="xs" onClick={() => setBulkReport(null)}>Verstanden</Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </>
  );
}

function EditTicketForm({
  ticket,
  autoOptimizeDescription,
  onCancel,
  onAskCustomer,
}: {
  ticket: WeclappTicket;
  /** Zähler aus der Lese-Ansicht: erhöht sich, wenn KIAN direkt loslegen soll. */
  autoOptimizeDescription?: number;
  /** Bearbeiten abbrechen — schließt das Formular, ohne etwas zu speichern. */
  onCancel?: () => void;
  /** Offene Punkte als Kommentar-Entwurf an den Kunden übergeben. */
  onAskCustomer?: (html: string) => void;
}) {
  const queryClient = useQueryClient();
  const [subject, setSubject] = useState(ticket.subject);
  const [description, setDescription] = useState(ticket.description ?? '');
  const [descriptionDirty, setDescriptionDirty] = useState(false);
  const [statusId, setStatusId] = useState<string | null>(null);
  const [priorityId, setPriorityId] = useState<string | null>(null);
  const [categoryId, setCategoryId] = useState<string | null>(null);
  // Stand VOR der ersten KIAN-Optimierung — für „Zurück" (pro Feld).
  const [kianPrevSubject, setKianPrevSubject] = useState<string | null>(null);
  const [kianPrevDescription, setKianPrevDescription] = useState<string | null>(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  /**
   * Was KIAN für das ITIL-Format nicht aus dem Material holen konnte, plus sein
   * Vorschlag, wie der Techniker es klärt. Bewusst NICHT in der Beschreibung: dort
   * stünde es später im Leistungsnachweis. Bewerten muss der Techniker selbst.
   */
  const [kianFollowUp, setKianFollowUp] = useState<{
    openPoints: string[];
    urgency: 'low' | 'medium' | 'high' | 'urgent' | null;
    suggestedChannel: 'call' | 'whatsapp' | 'comment' | null;
    channelReason: string | null;
  } | null>(null);

  // Re-sync form when a different ticket is opened
  useEffect(() => {
    setSubject(ticket.subject);
    setDescription(ticket.description ?? '');
    setDescriptionDirty(false);
    setStatusId(null);
    setPriorityId(null);
    setCategoryId(null);
    setKianPrevSubject(null);
    setKianPrevDescription(null);
  }, [ticket.id]);

  const optimizeSubjectMutation = useMutation({
    mutationFn: () =>
      api.optimizeWeclappTicketText(ticket.id, {
        field: 'subject',
        subject,
        descriptionHtml: description || undefined,
      }),
    onSuccess: (res) => {
      setKianPrevSubject((prev) => prev ?? subject);
      setSubject(res.text);
      queryClient.invalidateQueries({ queryKey: ['kian-assist-usage'] });
      notifications.show({
        title: 'Betreff optimiert',
        message: kianNotice(
          `Vorschlag eingefügt${
            res.quota ? ` · ${res.quota.used}/${res.quota.limit} heute` : ''
          }`,
          res.kianUsage,
        ),
        color: 'grape',
      });
    },
    onError: (e: Error) => {
      queryClient.invalidateQueries({ queryKey: ['kian-assist-usage'] });
      notifications.show({ title: 'KIAN-Optimierung fehlgeschlagen', message: e.message, color: 'red' });
    },
  });

  const optimizeDescriptionMutation = useMutation({
    mutationFn: () =>
      api.optimizeWeclappTicketText(ticket.id, {
        field: 'description',
        subject: subject || undefined,
        descriptionHtml: description,
      }),
    onSuccess: (res) => {
      if (!res.html) return;
      setKianPrevDescription((prev) => prev ?? description);
      setDescription(res.html);
      setDescriptionDirty(true);
      setKianFollowUp(
        res.openPoints && res.openPoints.length > 0
          ? {
              openPoints: res.openPoints,
              urgency: res.urgency ?? null,
              suggestedChannel: res.suggestedChannel ?? null,
              channelReason: res.channelReason ?? null,
            }
          : null,
      );
      queryClient.invalidateQueries({ queryKey: ['kian-assist-usage'] });
      // KIAN liefert einen Entwurf, kein Ergebnis — geprüft und gespeichert wird von Hand.
      notifications.show({
        title: 'KIAN-Vorschlag eingefügt',
        message: kianNotice(
          'Bitte prüfen und offene Punkte klären — gespeichert wird erst mit „Speichern".' +
            (res.quota ? ` (${res.quota.used}/${res.quota.limit} heute)` : ''),
          res.kianUsage,
        ),
        color: 'grape',
      });
    },
    onError: (e: Error) => {
      queryClient.invalidateQueries({ queryKey: ['kian-assist-usage'] });
      notifications.show({ title: 'KIAN-Optimierung fehlgeschlagen', message: e.message, color: 'red' });
    },
  });

  // Klick auf „KIAN: ITIL-Format" in der Lese-Ansicht: das Formular öffnet sich und
  // startet die Optimierung sofort. Der Zählerstand wird gemerkt, damit ein Re-Render
  // (z. B. nachgeladene Live-Daten) nicht ein zweites Mal auslöst.
  const handledAutoOptimize = useRef(0);
  useEffect(() => {
    if (!autoOptimizeDescription || autoOptimizeDescription === handledAutoOptimize.current) return;
    handledAutoOptimize.current = autoOptimizeDescription;
    optimizeDescriptionMutation.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOptimizeDescription]);

  const { data: metaData } = useQuery({
    queryKey: ['weclapp-metadata'],
    queryFn: () => api.getWeclappMetadata(),
    staleTime: 5 * 60 * 1000,
  });

  const mutation = useMutation({
    mutationFn: () => {
      const updates: {
        subject?: string;
        description?: string;
        statusId?: string;
        priorityId?: string;
        categoryId?: string;
        kianOptimized?: { subject?: boolean; description?: boolean };
      } = {};
      if (subject.trim() !== ticket.subject) updates.subject = subject.trim();
      if (descriptionDirty) updates.description = description;
      if (statusId) updates.statusId = statusId;
      if (priorityId) updates.priorityId = priorityId;
      if (categoryId) updates.categoryId = categoryId;
      if (Object.keys(updates).length === 0) throw new Error('Keine Änderungen');
      // `kianPrev*` steht genau dann, wenn KIAN das Feld geschrieben hat und der
      // Vorschlag NICHT über „Zurück" verworfen wurde — daraus baut der Server den
      // KIAN-Hinweis in der Beschreibung und die interne Original-Notiz.
      if (kianPrevSubject !== null || kianPrevDescription !== null) {
        updates.kianOptimized = {
          subject: kianPrevSubject !== null,
          description: kianPrevDescription !== null,
        };
      }
      return api.updateWeclappTicket(ticket.id, updates);
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['weclapp-ticket', ticket.id] });
      queryClient.invalidateQueries({ queryKey: ['weclapp-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['my-weclapp-tickets'] });
      // Dispatch-Board spiegelt Status/Priorität sonst erst nach dem Refetch
      queryClient.invalidateQueries({ queryKey: ['weclapp-dispatch-board'] });
      showTicketUpdateNotice(result);
      setDescriptionDirty(false);
      setStatusId(null);
      setPriorityId(null);
      setCategoryId(null);
      // Gespeichert ist gespeichert: der „Zurück"-Stand gehört zum verworfenen
      // Entwurf, nicht zum Ticket — sonst würde ein späterer Klick den bereits
      // dokumentierten KIAN-Text stillschweigend wieder überschreiben.
      setKianPrevSubject(null);
      setKianPrevDescription(null);
    },
    onError: (e: Error) =>
      e.message === 'Keine Änderungen'
        ? notifications.show({
            title: 'Keine Änderungen',
            message: 'Es wurden keine Felder geändert.',
            color: 'blue',
          })
        : notifications.show({ title: 'Fehler', message: e.message, color: 'red' }),
  });

  const statusOptions = (metaData?.statuses ?? []).map((s) => ({ value: s.id, label: s.name }));
  const priorityOptions = (metaData?.priorities ?? []).map((p) => ({ value: p.id, label: p.name }));
  const categoryOptions = (metaData?.categories ?? []).map((c) => ({ value: c.id, label: c.name }));

  const hasChanges =
    subject.trim() !== ticket.subject || descriptionDirty || !!statusId || !!priorityId || !!categoryId;

  // Abbrechen verwirft alles Ungespeicherte — inklusive eines gerade erzeugten
  // KIAN-Vorschlags. Bei offenen Änderungen deshalb erst nachfragen.
  const handleCancelClick = () => {
    if (hasChanges) setCancelConfirmOpen(true);
    else onCancel?.();
  };

  /**
   * Rückfrage-Wege für die offenen Punkte. Wen wir fragen, entscheidet der Server:
   * er löst den MELDER auf (Ticket-Kontakt → Absender-Adresse → Name). Findet er
   * ihn nicht, gibt es bewusst KEINEN Ersatz — früher wurde hier „der erste Kontakt
   * mit irgendeiner Telefonnummer" vorgeschlagen, was bei Mail-Tickets regelmäßig
   * die falsche Person traf. Stattdessen bietet die UI dann zwei benannte Wege an:
   * den Haupt-Ansprechpartner anrufen ODER den Melder schriftlich erreichen.
   */
  const { data: followUpContacts } = useQuery({
    queryKey: ['weclapp-followup-contacts', ticket.id],
    queryFn: () => api.getTicketFollowUpContacts(ticket.id),
    enabled: !!kianFollowUp,
    staleTime: 5 * 60 * 1000,
  });
  const reporter = followUpContacts?.reporter ?? null;
  /** Anrufen nur beim Melder — die Haupt-APs bekommen eigene, benannte Buttons. */
  const callNumber = reporter?.mobilePhone || reporter?.phone || null;
  /** Haupt-APs mit Rufnummer, nur ohne eindeutigen Melder — jeder einzeln, es können mehrere sein. */
  const primaryCallTargets = reporter
    ? []
    : (followUpContacts?.primaryContacts ?? []).flatMap((contact) => {
        const phone = contact.mobilePhone || contact.phone;
        return phone ? [{ contact, phone }] : [];
      });
  /** Melder-Adresse für die schriftliche Rückfrage — auch ohne Kontakt-Treffer. */
  const reporterEmail = reporter?.email ?? followUpContacts?.reporterEmail ?? null;
  const reporterLabel =
    reporter?.name ?? followUpContacts?.reporterName ?? reporterEmail ?? null;
  /**
   * WhatsApp läuft über unsere Chatwerk-Inbox, nicht über wa.me: geantwortet wird aus
   * der FixByte-Nummer, und der Verlauf bleibt im Portal. Deshalb wird hier nur
   * nachgeschlagen, ob es zu Kunde/Rufnummer schon eine Konversation gibt — eine neue
   * eröffnen kann WhatsApp-seitig weder Chatwerk noch das Portal.
   */
  // Alle bekannten Rufnummern des Melders — Weclapp UND portal-intern gepflegte.
  const reporterPhoneParam = (followUpContacts?.reporterPhones ?? []).join(',') || undefined;
  const { data: chatLookup } = useQuery({
    queryKey: ['chat-lookup', ticket.partyId, reporterPhoneParam ?? null, ticket.ticketNumber],
    queryFn: () =>
      api.lookupChatConversation({
        weclappPartyId: ticket.partyId,
        // Nummern des MELDERS — treffen die Person; die Kunden-Ebene im Backend ist
        // nur der Fallback und liefert dann irgendeinen Chat der Firma.
        phone: reporterPhoneParam,
        ticketNumber: ticket.ticketNumber,
      }),
    // Auch ohne Kunden-/Nummern-Treffer abfragen: die Einladung auf unsere eigene
    // Nummer hängt nicht am Kunden und ist gerade dann wichtig, wenn es noch keinen
    // Chat gibt.
    enabled: !!kianFollowUp,
    staleTime: 60 * 1000,
  });
  const chatConversation = chatLookup?.conversation ?? null;
  const whatsappInvite = chatLookup?.whatsappInvite ?? null;

  /**
   * QR-Code zur Einladung — im Browser erzeugt und als data:-URI eingebettet, damit
   * der Kommentar kein öffentlich erreichbares Bild braucht. Mailprogramme zeigen
   * data:-Bilder oft nicht an; der Link daneben trägt die Einladung deshalb allein,
   * der QR ist die Zugabe für alle, die das Ticket am Rechner lesen.
   */
  const [inviteQrDataUrl, setInviteQrDataUrl] = useState<string | null>(null);
  useEffect(() => {
    const link = whatsappInvite?.link;
    if (!link) {
      setInviteQrDataUrl(null);
      return;
    }
    let cancelled = false;
    QRCode.toDataURL(link, { width: 320, margin: 1 })
      .then((url) => {
        if (!cancelled) setInviteQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setInviteQrDataUrl(null);
      });
    return () => {
      cancelled = true;
    };
  }, [whatsappInvite?.link]);

  const escapeText = (v: string) =>
    v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

  /**
   * Hat der MELDER schon einen WhatsApp-Chat mit uns? Ein über die Kundenebene
   * gefundener Chat (matchedBy 'customer') gehört einer anderen Person — für ihn
   * ist die Einladung genauso nötig wie ganz ohne Chat.
   */
  const reporterHasChat = chatConversation?.matchedBy === 'phone';

  /**
   * Entwurf für die Rückfrage — der Techniker formuliert im Composer weiter.
   *
   * Besteht noch kein WhatsApp-Chat, hängt die Einladung auf unsere Nummer mit dran
   * (Link + QR zum Scannen): eröffnen kann die Konversation nur der Kunde, und
   * genau dazu ist das hier die Gelegenheit. Der QR liegt als öffentliches Bild auf
   * unserer Domain, damit ihn auch das Mailprogramm des Kunden laden kann.
   */
  const askCustomerHtml = (points: string[]) =>
    [
      '<p>Guten Tag,</p>',
      '<p>für die weitere Bearbeitung fehlen uns noch folgende Angaben:</p>',
      `<ul>${points.map((p) => `<li>${escapeText(p)}</li>`).join('')}</ul>`,
      '<p>Können Sie uns das kurz zurückmelden? Eine Antwort auf diese Nachricht genügt.</p>',
      ...(whatsappInvite && !reporterHasChat
        ? [
            '<p>Schneller geht es per WhatsApp: ',
            `<a href="${whatsappInvite.link}">Chat mit uns öffnen</a>`,
            inviteQrDataUrl ? ' — oder diesen QR-Code mit dem Handy scannen:</p>' : '.</p>',
            ...(inviteQrDataUrl
              ? [`<p><img src="${inviteQrDataUrl}" alt="WhatsApp-QR-Code" width="140" height="140" /></p>`]
              : []),
          ]
        : []),
    ].join('');

  const urgencyLabel: Record<'low' | 'medium' | 'high' | 'urgent', string> = {
    low: 'niedrige',
    medium: 'mittlere',
    high: 'hohe',
    urgent: 'sehr hohe',
  };
  const channelLabel: Record<'call' | 'whatsapp' | 'comment', string> = {
    call: 'anrufen',
    whatsapp: 'per WhatsApp fragen',
    comment: 'per Ticket-Kommentar fragen',
  };

  return (
    <Stack gap="xs" mt="sm">
      <TextInput
        label="Betreff"
        value={subject}
        onChange={(e) => setSubject(e.currentTarget.value)}
        size="sm"
        rightSectionWidth={kianPrevSubject !== null ? 62 : 34}
        rightSection={
          <Group gap={2} wrap="nowrap">
            {kianPrevSubject !== null && (
              <Tooltip label="Original wiederherstellen" withArrow>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  onClick={() => {
                    setSubject(kianPrevSubject);
                    setKianPrevSubject(null);
                  }}
                >
                  <IconArrowBackUp size={16} />
                </ActionIcon>
              </Tooltip>
            )}
            <Tooltip label="Betreff mit KIAN optimieren" withArrow>
              <ActionIcon
                variant="subtle"
                color="grape"
                size="sm"
                loading={optimizeSubjectMutation.isPending}
                disabled={!subject.trim()}
                onClick={() => optimizeSubjectMutation.mutate()}
              >
                <IconSparkles size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        }
      />
      <div>
        <Group justify="space-between" mb={4}>
          <Text size="xs" fw={500}>Beschreibung</Text>
          <Group gap={6}>
            <KianAssistUsageHint />
            {kianPrevDescription !== null && (
              <Button
                size="compact-xs"
                variant="subtle"
                color="gray"
                leftSection={<IconArrowBackUp size={13} />}
                onClick={() => {
                  setDescription(kianPrevDescription);
                  setKianPrevDescription(null);
                }}
              >
                Zurück
              </Button>
            )}
            <Tooltip
              label="Beschreibung aus Kunde, Betreff, Ticket-Mails und Anhängen ins ITIL-Format bringen — auch wenn noch keine Beschreibung da ist"
              withArrow
              multiline
              w={280}
            >
              <Button
                size="compact-xs"
                variant="light"
                color="grape"
                leftSection={<IconSparkles size={13} />}
                loading={optimizeDescriptionMutation.isPending}
                onClick={() => optimizeDescriptionMutation.mutate()}
              >
                KIAN: ITIL-Format
              </Button>
            </Tooltip>
          </Group>
        </Group>
        <WysiwygEditor
          value={description}
          onChange={(html) => { setDescription(html); setDescriptionDirty(true); }}
          minHeight={140}
        />
        {kianFollowUp && (
          <Alert color="yellow" p="xs" mt={6} icon={<IconAlertTriangle size={16} />}>
            <Stack gap={6}>
              <Text size="xs" fw={600}>
                Für das ITIL-Format fehlt noch — bitte selbst klären und ergänzen:
              </Text>
              <ul style={{ margin: 0, paddingLeft: '1.1rem' }}>
                {kianFollowUp.openPoints.map((point) => (
                  <li key={point}>
                    <Text size="xs">{point}</Text>
                  </li>
                ))}
              </ul>
              {kianFollowUp.suggestedChannel && (
                <Text size="xs" c="dimmed">
                  KIAN schätzt {urgencyLabel[kianFollowUp.urgency ?? 'low']} Dringlichkeit und würde{' '}
                  {channelLabel[kianFollowUp.suggestedChannel]}.
                  {kianFollowUp.channelReason ? ` ${kianFollowUp.channelReason}` : ''}
                  {kianFollowUp.suggestedChannel === 'whatsapp' && !reporterHasChat
                    ? whatsappInvite
                      ? ' Mit dem Melder gibt es allerdings noch keinen Chat — eröffnen kann ihn nur er selbst. Die schriftliche Rückfrage enthält dafür Link und QR-Code auf unsere WhatsApp-Nummer.'
                      : ' Mit dem Melder gibt es allerdings keinen Chat in der Chat-Inbox — WhatsApp-Gespräche kann nur der Kunde eröffnen.'
                    : ''}{' '}
                  Die Einschätzung triffst du selbst.
                </Text>
              )}
              {/* Wen fragen wir? Melder-Auflösung offenlegen statt still zu raten. */}
              <Text size="xs" c="dimmed">
                {reporter ? (
                  <>
                    Melder: <b>{reporter.name}</b>
                    {reporter.matchedBy === 'senderEmail' && reporter.email
                      ? ` (Absender ${reporter.email})`
                      : reporter.matchedBy === 'name'
                        ? ' (über den Namen zugeordnet — bitte kurz prüfen)'
                        : ''}
                  </>
                ) : (
                  <>
                    Melder nicht eindeutig zugeordnet
                    {reporterLabel ? ` — laut Ticket „${reporterLabel}"` : ''}. Deshalb kein
                    Anruf-Vorschlag: entweder einen Haupt-Ansprechpartner anrufen oder dem Melder
                    schriftlich antworten.
                  </>
                )}
              </Text>
              <Group gap={4}>
                {callNumber && (
                  // Wählen wie bisher — danach fragt der Dialog nach dem Ergebnis
                  // und hängt es als INTERNEN Vermerk ans Ticket. Kunde und Kontakt
                  // löst der Server über die Rufnummer auf.
                  <CallButton
                    phone={callNumber}
                    contactName={reporter?.name}
                    ticket={{ provider: 'weclapp', id: ticket.id, number: ticket.ticketNumber }}
                    originType="ticket"
                    originId={ticket.id}
                  >
                    {(dial) => (
                      <Button
                        size="compact-xs"
                        variant={kianFollowUp.suggestedChannel === 'call' ? 'filled' : 'light'}
                        color="teal"
                        leftSection={<IconPhone size={13} />}
                        onClick={dial}
                      >
                        Melder anrufen{reporter?.name ? ` (${reporter.name})` : ''}
                      </Button>
                    )}
                  </CallButton>
                )}
                {primaryCallTargets.map(({ contact, phone }) => (
                  <Tooltip
                    key={contact.id}
                    label={`${contact.name} ist im ServiceHub als Haupt-Ansprechpartner des Kunden gepflegt — nicht der Melder dieses Tickets.`}
                    withArrow
                    multiline
                    w={260}
                  >
                    <CallButton
                      phone={phone}
                      contactName={contact.name}
                      ticket={{ provider: 'weclapp', id: ticket.id, number: ticket.ticketNumber }}
                      originType="ticket"
                      originId={ticket.id}
                    >
                      {(dial) => (
                        <Button
                          size="compact-xs"
                          variant="light"
                          color="teal"
                          leftSection={<IconPhone size={13} />}
                          onClick={dial}
                        >
                          Haupt-AP anrufen ({contact.name})
                        </Button>
                      )}
                    </CallButton>
                  </Tooltip>
                ))}
                {/* Weder Melder noch Haupt-AP? Dann alle gepflegten Wege zur
                    Auswahl — Zentrale, Durchwahlen, Mitarbeiter. Vorher blieb an
                    dieser Stelle nur, die Nummer von Hand herauszusuchen. */}
                {ticket.partyId && (
                  <CustomerCallButton
                    weclappCustomerId={ticket.partyId}
                    customerName={ticket.partyName ?? undefined}
                    ticket={{ provider: 'weclapp', id: ticket.id, number: ticket.ticketNumber }}
                    originType="ticket"
                    originId={ticket.id}
                    label="Anderen Weg wählen"
                  />
                )}
                {chatConversation && (
                  <Tooltip
                    label={
                      chatConversation.matchedBy === 'customer'
                        ? `Chat mit ${chatConversation.contactName ?? 'dem Kunden'} — das ist der jüngste Chat dieses Kunden, nicht zwingend der Melder dieses Tickets.`
                        : chatConversation.replyWindowOpen
                          ? `Öffnet den Chat mit ${chatConversation.contactName ?? 'dem Kunden'} in der Chat-Inbox`
                          : `Chat mit ${chatConversation.contactName ?? 'dem Kunden'} — 24-h-Antwortfenster abgelaufen, freie Nachrichten sind erst nach einer Kundennachricht wieder möglich`
                    }
                    withArrow
                    multiline
                    w={260}
                  >
                    <Button
                      size="compact-xs"
                      variant={
                        kianFollowUp.suggestedChannel === 'whatsapp' &&
                        chatConversation.matchedBy === 'phone'
                          ? 'filled'
                          : 'light'
                      }
                      color="green"
                      leftSection={<IconBrandWhatsapp size={13} />}
                      component={Link}
                      to={`/chats?open=${encodeURIComponent(chatConversation.id)}`}
                    >
                      {/* Name immer sichtbar: sonst landet man im Chat einer anderen Person. */}
                      Chat{chatConversation.contactName ? `: ${chatConversation.contactName}` : ' öffnen'}
                      {chatConversation.matchedBy === 'customer' ? ' (nicht der Melder)' : ''}
                      {chatConversation.replyWindowOpen ? '' : ' — Fenster zu'}
                    </Button>
                  </Tooltip>
                )}
                {onAskCustomer && (
                  <Tooltip
                    label={
                      reporterEmail
                        ? `Geht als Ticket-Kommentar per Mail an den Melder (${reporterEmail})${whatsappInvite && !chatConversation ? ' — inklusive WhatsApp-Link und QR-Code, damit er den Chat eröffnen kann' : ''}.`
                        : 'Geht als Ticket-Kommentar an die am Ticket hinterlegten Empfänger.'
                    }
                    withArrow
                    multiline
                    w={280}
                  >
                    <Button
                      size="compact-xs"
                      variant={
                        kianFollowUp.suggestedChannel === 'comment' || !reporter ? 'filled' : 'light'
                      }
                      color="blue"
                      leftSection={<IconMessagePlus size={13} />}
                      onClick={() => onAskCustomer(askCustomerHtml(kianFollowUp.openPoints))}
                    >
                      Melder schriftlich fragen{reporterLabel ? ` (${reporterLabel})` : ''}
                    </Button>
                  </Tooltip>
                )}
                <Button
                  size="compact-xs"
                  variant="subtle"
                  color="gray"
                  onClick={() => setKianFollowUp(null)}
                >
                  Erledigt
                </Button>
              </Group>
            </Stack>
          </Alert>
        )}
      </div>
      <Group gap="sm">
        <Select
          label="Status"
          placeholder={ticket.status}
          data={statusOptions}
          value={statusId}
          onChange={setStatusId}
          clearable
          style={{ flex: 1 }}
          size="sm"
          comboboxProps={{ withinPortal: true, zIndex: 1000 }}
        />
        <Select
          label="Priorität"
          placeholder={ticket.priority ?? '–'}
          data={priorityOptions}
          value={priorityId}
          onChange={setPriorityId}
          clearable
          style={{ flex: 1 }}
          size="sm"
          comboboxProps={{ withinPortal: true, zIndex: 1000 }}
        />
        <Select
          label="Kategorie"
          placeholder={ticket.ticketCategoryName ?? '–'}
          data={categoryOptions}
          value={categoryId}
          onChange={setCategoryId}
          clearable
          searchable
          style={{ flex: 1 }}
          size="sm"
          comboboxProps={{ withinPortal: true, zIndex: 1000 }}
        />
      </Group>
      <Group justify="space-between" align="center">
        <Text size="xs" c="dimmed" style={{ flex: 1 }}>
          Bei Änderung von Betreff/Beschreibung wird das Original automatisch als interne
          Notiz gesichert und unten in der Beschreibung ein Bearbeitungshinweis ergänzt —
          bei KIAN-Optimierung inklusive Vermerk, dass KIAN den Text geschrieben hat.
        </Text>
        <Group gap="xs">
          {onCancel && (
            <Button
              size="xs"
              variant="default"
              leftSection={<IconX size={14} />}
              onClick={handleCancelClick}
            >
              Abbrechen
            </Button>
          )}
          <Button
            size="xs"
            leftSection={<IconEdit size={14} />}
            loading={mutation.isPending}
            disabled={!hasChanges}
            onClick={() => mutation.mutate()}
          >
            Speichern
          </Button>
        </Group>
      </Group>

      {/* Rückfrage vor dem Verwerfen — ein KIAN-Vorschlag ist schnell erzeugt, aber
          nach dem Schließen des Formulars weg. */}
      <Modal
        opened={cancelConfirmOpen}
        onClose={() => setCancelConfirmOpen(false)}
        title="Bearbeitung abbrechen?"
        size="sm"
        zIndex={800}
        centered
      >
        <Stack gap="sm">
          <Text size="sm">
            Alle ungespeicherten Änderungen an Betreff und Beschreibung gehen verloren —
            auch ein gerade erzeugter KIAN-Vorschlag. Das Ticket selbst bleibt unverändert.
          </Text>
          <Group justify="flex-end" gap="xs">
            <Button variant="default" size="xs" onClick={() => setCancelConfirmOpen(false)}>
              Weiter bearbeiten
            </Button>
            <Button
              color="red"
              size="xs"
              onClick={() => {
                setCancelConfirmOpen(false);
                onCancel?.();
              }}
            >
              Verwerfen
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

function formatBytes(bytes?: number): string {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Virenscan-Kennzeichnung: ClamAV lokal + VirusTotal-Hash-Lookup (nur Hashes). */
function AttachmentScanBadge({ scan }: { scan?: AttachmentScanStatus }) {
  if (!scan) return null;

  if (scan.blocked) {
    return (
      <Tooltip
        label={`Virenscanner-Befund: ${scan.clamavSignature ?? 'VirusTotal-Treffer'} — Download gesperrt`}
        withArrow
      >
        <Badge size="xs" color="red" variant="filled" leftSection={<IconShieldX size={10} />} style={{ flexShrink: 0 }}>
          Virus
        </Badge>
      </Tooltip>
    );
  }

  const clamLabel =
    scan.clamavStatus === 'CLEAN'
      ? 'ClamAV: sauber'
      : scan.clamavStatus === 'PENDING'
        ? 'ClamAV: Scan ausstehend'
        : scan.clamavStatus === 'TOO_LARGE'
          ? 'ClamAV: zu groß für den Scan (>200 MB)'
          : scan.clamavStatus === 'SKIPPED'
            ? 'ClamAV: Scanner nicht konfiguriert'
            : 'ClamAV: Scan-Fehler';
  const vtLabel =
    scan.vtStatus === 'CLEAN'
      ? `VirusTotal: 0/${scan.vtTotal ?? '?'} Engines (Hash-Lookup)`
      : scan.vtStatus === 'SUSPICIOUS'
        ? `VirusTotal: verdächtig (${scan.vtPositives ?? '?'}/${scan.vtTotal ?? '?'} Engines)`
        : scan.vtStatus === 'UNKNOWN'
          ? 'VirusTotal: Hash unbekannt (frische Datei, kein Befund)'
          : scan.vtStatus === 'PENDING'
            ? 'VirusTotal: Hash-Lookup ausstehend'
            : scan.vtStatus === 'SKIPPED'
              ? 'VirusTotal: deaktiviert (kein API-Key)'
              : 'VirusTotal: Lookup-Fehler';

  const clamOk = scan.clamavStatus === 'CLEAN';
  const suspicious = scan.vtStatus === 'SUSPICIOUS';
  const pending = scan.clamavStatus === 'PENDING';
  const color = suspicious ? 'yellow' : clamOk ? 'teal' : 'gray';
  const label = suspicious ? 'VT ?' : clamOk ? 'geprüft' : pending ? 'Scan…' : 'ungescannt';
  const icon = clamOk && !suspicious ? <IconShieldCheck size={10} /> : <IconShieldQuestion size={10} />;

  return (
    <Tooltip label={`${clamLabel} · ${vtLabel}`} withArrow multiline w={300}>
      <Badge size="xs" color={color} variant="light" leftSection={icon} style={{ flexShrink: 0, cursor: 'help' }}>
        {label}
      </Badge>
    </Tooltip>
  );
}

function AttachmentsList({ documents, ticketId }: { documents: WeclappDocumentRef[]; ticketId: string }) {
  /** Lightbox: angeklicktes Bild in voller Größe im Modal statt Download/neuem Tab. */
  const [preview, setPreview] = useState<{ url: string; name: string } | null>(null);
  // Drag & Drop: ohne eigene Handler übernimmt der Browser den Drop und öffnet die
  // Datei als neuen Tab. Der Zähler fängt verschachtelte dragenter/dragleave ab.
  const [dragActive, setDragActive] = useState(false);
  const dragCounter = useRef(0);
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Anhänge kommen gebündelt aus dem Cockpit (Ticket-Ebene + E-Mail-Anhänge der Kommentare).
  // Gleicher Query-Key wie die Listen → kein zusätzlicher Request (Dedup).
  const { data: cockpitDocs } = useQuery({
    queryKey: ['weclapp-cockpit', ticketId],
    queryFn: () => api.getWeclappTicketCockpit(ticketId),
    select: (d) => d.documents,
    staleTime: 30000,
  });
  // „Keine Anhänge" und „Anhänge nicht ladbar" sahen bisher identisch aus — wer
  // das liest, glaubt, es gäbe nichts, und sucht gar nicht erst weiter.
  const { data: docsError } = useQuery({
    queryKey: ['weclapp-cockpit', ticketId],
    queryFn: () => api.getWeclappTicketCockpit(ticketId),
    select: (d) => d.documentsError ?? null,
    staleTime: 30000,
  });

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result as string).split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      return api.uploadWeclappAttachment(ticketId, { base64, filename: file.name, mimeType: file.type });
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['weclapp-ticket', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['weclapp-cockpit', ticketId] });
      notifications.show(
        res.pending
          ? { color: 'yellow', message: 'Anhang hängt am Ticket — weclapp bekommt ihn, sobald es das Ticket hat bzw. wieder antwortet.' }
          : { color: 'green', message: 'Anhang hochgeladen' },
      );
    },
    onError: (e) => {
      notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Upload fehlgeschlagen' });
    },
  });

  const seenIds = new Set<string>();
  const allDocs: WeclappDocumentRef[] = [];
  for (const doc of [...documents, ...(cockpitDocs ?? [])]) {
    if (doc && !seenIds.has(doc.id)) {
      seenIds.add(doc.id);
      allDocs.push(doc);
    }
  }

  // Virenscan-Verdikte (ClamAV + VT-Hash). Der Abruf plant ungescannte Anhänge
  // serverseitig automatisch ein — solange etwas aussteht, wird nachgepollt.
  const docIdsKey = allDocs.map((d) => d.id).sort().join(',');
  const { data: scanData } = useQuery({
    queryKey: ['attachment-scan-status', ticketId, docIdsKey],
    queryFn: () =>
      api.getAttachmentScanStatus(
        allDocs.map((d) => ({ docId: d.id, fileName: d.fileName ?? d.name ?? null, ticketId }))
      ),
    enabled: allDocs.length > 0,
    staleTime: 30_000,
    refetchInterval: (query) => {
      const statuses = query.state.data?.statuses;
      if (!statuses) return 15_000;
      const pending = Object.values(statuses).some(
        (s) => s.clamavStatus === 'PENDING' || s.vtStatus === 'PENDING'
      );
      return pending ? 15_000 : false;
    },
  });
  const scanStatuses = scanData?.statuses ?? {};

  // Office-Vorschau: Anhang nach Scan-Gate in die SharePoint-Preview-Site heben
  // und in Office for the web öffnen. Popup VOR dem Request öffnen — sonst
  // blockt der Browser das window.open nach dem await.
  const { user: currentUser } = useAuthStore();
  const officeViewMutation = useMutation({
    mutationFn: async ({ docId }: { docId: string }) => api.openAttachmentOfficeView(ticketId, docId),
    onSuccess: (data) => {
      window.open(data.webUrl, '_blank', 'noopener');
    },
    onError: (e) => {
      const isNotConfigured = e instanceof ApiError && e.code === 'not_configured';
      const base = e instanceof ApiError ? e.summary || e.message : e instanceof Error ? e.message : 'Fehler';
      const adminHint =
        isNotConfigured && currentUser?.role === 'ADMIN'
          ? ' → Einstellungen → Microsoft 365 → „Office-Vorschau einrichten" (Gerätecode, legt Berechtigungen + Site automatisch an).'
          : '';
      notifications.show({
        color: isNotConfigured ? 'yellow' : 'red',
        title: 'Office-Vorschau',
        message: `${base}${adminHint}`,
        autoClose: isNotConfigured ? 12000 : 6000,
      });
    },
  });

  return (
    <Stack
      gap={4}
      mt="xs"
      onDragEnter={(e) => {
        if (!e.dataTransfer?.types.includes('Files')) return;
        e.preventDefault();
        dragCounter.current += 1;
        setDragActive(true);
      }}
      onDragOver={(e) => {
        if (!e.dataTransfer?.types.includes('Files')) return;
        e.preventDefault();
      }}
      onDragLeave={(e) => {
        if (!e.dataTransfer?.types.includes('Files')) return;
        dragCounter.current = Math.max(0, dragCounter.current - 1);
        if (dragCounter.current === 0) setDragActive(false);
      }}
      onDrop={(e) => {
        if (!e.dataTransfer?.types.includes('Files')) return;
        e.preventDefault();
        dragCounter.current = 0;
        setDragActive(false);
        Array.from(e.dataTransfer.files).forEach((file) => uploadMutation.mutate(file));
      }}
      style={
        dragActive
          ? {
              outline: '2px dashed var(--mantine-color-orange-5)',
              outlineOffset: 4,
              borderRadius: 6,
            }
          : undefined
      }
    >
      {allDocs.length === 0 && (
        <Text size="sm" c="dimmed">
          {docsError ? 'Anhänge konnten nicht geladen werden' : 'Keine Anhänge'}
        </Text>
      )}
      {allDocs.map((doc) => {
        // mediaType fehlt bei manchen Weclapp-Dokumenten (z. B. Inline-Bildern aus
        // Mails) — zusätzlich an der Dateiendung erkennen, sonst gibt es keine Vorschau.
        const displayName = doc.fileName || doc.name || doc.id;
        const isImage =
          (doc.mediaType ?? '').startsWith('image/') ||
          /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(doc.fileName || doc.name || '');
        const url = api.getWeclappAttachmentDownloadUrl(ticketId, doc.id);
        return (
          <div key={doc.id}>
            <Group gap="xs" wrap="nowrap">
              {isImage
                ? <IconPhoto size={14} style={{ flexShrink: 0, color: 'var(--mantine-color-blue-4)' }} />
                : <IconFileText size={14} style={{ flexShrink: 0, color: 'var(--mantine-color-gray-5)' }} />}
              <Text
                size="sm"
                style={{
                  flex: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  ...(isImage ? { cursor: 'pointer', textDecoration: 'underline dotted' } : {}),
                }}
                onClick={isImage ? () => setPreview({ url, name: displayName }) : undefined}
                title={isImage ? 'Vorschau öffnen' : undefined}
              >
                {displayName}
              </Text>
              {doc.pendingSync && (
                <Tooltip label="Hängt im ServiceHub am Ticket — weclapp bekommt die Datei, sobald es das Ticket hat bzw. wieder antwortet" withArrow multiline w={260}>
                  <Badge size="xs" color="yellow" variant="light" style={{ flexShrink: 0 }}>
                    wartet auf weclapp
                  </Badge>
                </Tooltip>
              )}
              <AttachmentScanBadge scan={scanStatuses[doc.id]} />
              {/\.(xlsx?|docx?|pptx?|csv)$/i.test(doc.fileName || doc.name || '') &&
                !scanStatuses[doc.id]?.blocked && (
                  <Tooltip label="In Office öffnen (Virenscan, dann Ansicht im eigenen M365-Tenant)" withArrow>
                    <ActionIcon
                      variant="subtle"
                      color="teal"
                      size="lg"
                      style={{ flexShrink: 0 }}
                      loading={
                        officeViewMutation.isPending && officeViewMutation.variables?.docId === doc.id
                      }
                      onClick={() => officeViewMutation.mutate({ docId: doc.id })}
                      aria-label={`In Office öffnen: ${doc.fileName || doc.name || ''}`}
                    >
                      <IconEye size={18} />
                    </ActionIcon>
                  </Tooltip>
                )}
              {doc.fileSize ? (
                <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                  {formatBytes(doc.fileSize)}
                </Text>
              ) : null}
              {scanStatuses[doc.id]?.blocked ? (
                <ActionIcon
                  variant="subtle"
                  color="red"
                  size="lg"
                  style={{ flexShrink: 0 }}
                  disabled
                  aria-label="Download gesperrt (Virenscanner-Befund)"
                >
                  <IconShieldX size={18} />
                </ActionIcon>
              ) : (
                <ActionIcon
                  component="a"
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  variant="subtle"
                  color="blue"
                  size="lg"
                  style={{ flexShrink: 0 }}
                  aria-label={`Download ${doc.fileName || doc.name || ''}`}
                >
                  <IconDownload size={18} />
                </ActionIcon>
              )}
            </Group>
            {isImage && !scanStatuses[doc.id]?.blocked && (
              <img
                src={url}
                alt={displayName}
                loading="lazy"
                onClick={() => setPreview({ url, name: displayName })}
                style={{
                  display: 'block',
                  marginTop: 4,
                  maxWidth: '100%',
                  maxHeight: 180,
                  borderRadius: 6,
                  border: '1px solid var(--mantine-color-dark-4)',
                  cursor: 'zoom-in',
                }}
              />
            )}
          </div>
        );
      })}
      <input
        ref={fileInputRef}
        type="file"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => {
          Array.from(e.target.files ?? []).forEach((file) => uploadMutation.mutate(file));
          e.target.value = '';
        }}
      />
      <Button
        size="xs"
        variant="light"
        leftSection={<IconPaperclip size={13} />}
        mt={4}
        loading={uploadMutation.isPending}
        onClick={() => fileInputRef.current?.click()}
      >
        Anhang hochladen
      </Button>

      <Modal
        opened={preview !== null}
        onClose={() => setPreview(null)}
        title={preview?.name}
        size="xl"
        zIndex={800}
        centered
      >
        {preview && (
          <Stack gap="sm" align="center">
            <img
              src={preview.url}
              alt={preview.name}
              style={{ maxWidth: '100%', maxHeight: '75vh', borderRadius: 6 }}
            />
            <Button
              component="a"
              href={preview.url}
              target="_blank"
              rel="noreferrer"
              size="xs"
              variant="light"
              leftSection={<IconDownload size={13} />}
            >
              In Originalgröße öffnen / herunterladen
            </Button>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}

// ============================================
// HISTORY
// ============================================

const WECLAPP_FIELD_LABELS: Record<string, string> = {
  ticketStatusId: 'Status',
  responsibleUserId: 'Bearbeiter',
  ticketNumber: 'Ticketnummer',
  subject: 'Betreff',
  description: 'Beschreibung',
  priorityId: 'Priorität',
  categoryId: 'Kategorie',
  partyId: 'Kunde',
  contactId: 'Kontakt',
  dueDate: 'Fälligkeitsdatum',
  assignedUserId: 'Zugewiesen an',
};

function displayFieldName(attribute: string): string {
  return WECLAPP_FIELD_LABELS[attribute] ?? attribute;
}

/** Herkunfts-Badge pro Verlaufseintrag: wo wurde die Aktion ausgeführt? */
const HISTORY_ORIGIN_BADGES: Record<
  NonNullable<WeclappTicketHistoryEntry['origin']>,
  { label: string; color: string }
> = {
  portal: { label: 'Portal', color: 'grape' },
  customer: { label: 'Kunden-Portal', color: 'cyan' },
  kian: { label: 'KIAN', color: 'violet' },
  weclapp: { label: 'Weclapp', color: 'blue' },
};

function HistoryEntryCard({
  entry,
  onUseTimestamp,
}: {
  entry: WeclappTicketHistoryEntry;
  /** Zeitstempel per Klick als Startzeit in die Zeitbuchung übernehmen. */
  onUseTimestamp?: (d: Date) => void;
}) {
  const date = new Date(entry.timestamp);
  const dateStr = date.toLocaleString('de-DE', {
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
  const dateValid = !isNaN(date.getTime());

  // Ältere Server-Antworten kennen `origin` noch nicht → wie bisher aus `source` ableiten.
  const origin = entry.origin ?? (entry.source === 'portal' ? 'portal' : undefined);
  const originBadge = origin ? HISTORY_ORIGIN_BADGES[origin] : undefined;
  const isPortal = origin === 'portal' || origin === 'customer' || origin === 'kian';

  return (
    <div
      style={{
        background: 'var(--mantine-color-dark-6)',
        borderRadius: 6,
        padding: '0.5rem 0.75rem',
        borderLeft: isPortal
          ? '3px solid var(--mantine-color-grape-6)'
          : '3px solid var(--mantine-color-gray-6)',
      }}
    >
      <Group gap="xs" mb={4} wrap="nowrap">
        {onUseTimestamp && dateValid ? (
          <Tooltip label="Als Startzeit in die Zeitbuchung übernehmen" withArrow>
            <Text
              size="xs"
              c="dimmed"
              style={{
                flexShrink: 0,
                cursor: 'pointer',
                textDecoration: 'underline dotted',
                textUnderlineOffset: 3,
              }}
              onClick={() => onUseTimestamp(date)}
            >
              {dateStr}
            </Text>
          </Tooltip>
        ) : (
          <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>{dateStr}</Text>
        )}
        {entry.actorName && (
          <Text size="xs" fw={600} c={isPortal ? 'grape.4' : 'gray.3'} truncate>
            {entry.actorName}
          </Text>
        )}
        {originBadge && (
          <Badge size="xs" color={originBadge.color} variant="light" style={{ flexShrink: 0 }}>
            {originBadge.label}
          </Badge>
        )}
      </Group>

      {entry.note && (
        <Text size="sm" c="dimmed">{entry.note}</Text>
      )}

      {entry.changes && entry.changes.length > 0 && (
        <Stack gap={2} mt={entry.note ? 4 : 0}>
          {entry.changes.map((c, i) => (
            <Group key={i} gap={4} wrap="nowrap">
              <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>{displayFieldName(c.attribute)}:</Text>
              {(c.oldDisplayValue ?? c.oldValue) && (
                <>
                  <Text size="xs" c="red.4" style={{ textDecoration: 'line-through' }} truncate>
                    {c.oldDisplayValue ?? c.oldValue}
                  </Text>
                  <Text size="xs" c="dimmed">→</Text>
                </>
              )}
              <Text size="xs" c="green.4" truncate>
                {c.newDisplayValue ?? c.newValue ?? '–'}
              </Text>
            </Group>
          ))}
        </Stack>
      )}
    </div>
  );
}

function HistoryList({
  ticketId,
  onUseTimestamp,
}: {
  ticketId: string;
  onUseTimestamp?: (d: Date) => void;
}) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['weclapp-history', ticketId],
    queryFn: () => api.getWeclappTicketHistory(ticketId),
    staleTime: 60_000,
  });

  if (isLoading) {
    return (
      <Stack gap="xs" mt="xs">
        <Skeleton height={50} radius="sm" />
        <Skeleton height={50} radius="sm" />
        <Skeleton height={50} radius="sm" />
      </Stack>
    );
  }

  if (error) {
    return (
      <Alert color="red" variant="light" mt="xs">
        Fehler beim Laden des Verlaufs.{' '}
        <Button size="compact-xs" variant="subtle" onClick={() => refetch()}>
          Erneut versuchen
        </Button>
      </Alert>
    );
  }

  const history = data?.history ?? [];

  if (history.length === 0) {
    return (
      <Text size="sm" c="dimmed" mt="xs">
        Kein Verlauf vorhanden
      </Text>
    );
  }

  return (
    <Stack gap="xs" mt="xs">
      {history.map((entry) => (
        <HistoryEntryCard key={entry.id} entry={entry} onUseTimestamp={onUseTimestamp} />
      ))}
    </Stack>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

/**
 * Freiraum rechts in der ersten Kopfzeile für das Schließen-X. Das X steht absolut und
 * ragt nur in die erste Zeile hinein — die Zeilen darunter behalten die volle Breite.
 */
const HEADER_CLOSE_CLEARANCE = '2.25rem';

export function WeclappTicketCockpit({ ticketId, onClose, zIndex = 500 }: WeclappTicketCockpitProps) {
  // Escape / Klick daneben darf angefangene Eingaben (Kommentar, Zeiteintrag) nicht
  // stumm verwerfen — vorher nachfragen. Formulare registrieren sich im Draft-Store.
  // WICHTIG: Der Selector muss das stabile `drafts`-Objekt liefern. `Object.values(...)`
  // im Selector erzeugt bei jedem Aufruf ein neues Array — Zustand v5 sieht damit in
  // jedem Render eine „Änderung" und React bricht mit Error #185 (Endlos-Loop) ab.
  const unsavedDrafts = useUnsavedDrafts((s) => s.drafts);
  const personColors = usePersonColors();
  /** Labels der offenen Entwürfe, solange die Rückfrage steht — null = keine Rückfrage. */
  const [pendingCloseLabels, setPendingCloseLabels] = useState<string[] | null>(null);
  const guardedClose = () => {
    const draftLabels = [...new Set(Object.values(unsavedDrafts))];
    if (draftLabels.length > 0) {
      setPendingCloseLabels(draftLabels);
      return;
    }
    onClose();
  };
  // Auf Smartphones den Drawer voll ausfahren — sonst ist das Cockpit im Feld zu schmal.
  const isMobile = useMediaQuery('(max-width: 48em)');
  // Ab dieser Breite passen Ticket UND Analyse nebeneinander, ohne dass eine Spalte
  // unlesbar schmal wird.
  const isWideCockpit = useMediaQuery('(min-width: 90em)') ?? false;
  // Gibt es (noch) keine Analyse, existiert die rechte Spalte gar nicht — stattdessen
  // steht ein kompakter Starter (Analysieren/Deep + (i)-Hinweis) im Ticket-Kopf.
  // Identischer Query-Key wie im KianAnalysisPanel → geteilter Cache, kein Doppel-Request.
  const kianQuery = useQuery({
    queryKey: ['kian-analysis', ticketId, 'WECLAPP'] as const,
    queryFn: () => api.getKianAnalysis(ticketId!, 'WECLAPP'),
    enabled: !!ticketId,
    staleTime: 30_000,
  });
  const hasKianAnalysis = !!kianQuery.data?.analysis;
  const kianPaneWidth = 440;
  /** Nur für schmale Ansichten: welche der beiden Seiten gerade sichtbar ist. */
  const [pane, setPane] = useState<'ticket' | 'kian'>('ticket');
  const queryClient = useQueryClient();
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [descriptionOpen, setDescriptionOpen] = useState(true);
  // Liste + „Kommentar schreiben" teilen sich einen Abschnitt — offen als häufigste Feldaktion.
  const [commentListOpen, setCommentListOpen] = useState(true);
  // Gebuchte Zeiten + „Zeit buchen" teilen sich einen Abschnitt; das Buchungsformular
  // klappt erst per Button auf (gleiches Muster wie bei den Kommentaren).
  const [timesOpen, setTimesOpen] = useState(true);
  const [timeFormOpen, setTimeFormOpen] = useState(false);
  // Zeitstempel-Übernahme: Klick auf eine Zeit im Verlauf oder auf Erstellt/aktualisiert
  // im Kopf setzt die Startzeit der Zeitbuchung — Copy-Paste in ein datetime-Feld
  // funktioniert nicht, der Klick erledigt das direkt.
  const [timePrefill, setTimePrefill] = useState<Date | null>(null);
  /**
   * Vorbelegter Kommentar-Entwurf — gesetzt aus der KIAN-Optimierung, wenn der Techniker
   * die offenen Punkte per Ticket-Kommentar beim Kunden nachfragen will.
   */
  const [commentPrefill, setCommentPrefill] = useState<{ html: string; internal: boolean } | null>(
    null,
  );
  const askCustomerViaComment = (html: string) => {
    setCommentListOpen(true);
    // Neues Objekt, damit derselbe Text auch ein zweites Mal den Composer öffnet.
    setCommentPrefill({ html, internal: false });
  };
  const adoptTimestamp = (d: Date) => {
    setTimesOpen(true);
    setTimeFormOpen(true);
    // Neue Date-Instanz, damit der Effekt im Formular auch bei gleicher Zeit erneut feuert.
    setTimePrefill(new Date(d.getTime()));
    notifications.show({
      title: 'Startzeit übernommen',
      message: `Zeitbuchung beginnt ${d.toLocaleString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })} — Abschnitt „Zeiten".`,
      color: 'teal',
    });
  };
  // Beschreibung und Bearbeiten sind EIN Abschnitt — editMode schaltet zwischen
  // Lese-Ansicht und Bearbeitungsformular um.
  const [editMode, setEditMode] = useState(false);
  /**
   * Zähler statt Boolean: jeder Klick auf „KIAN: ITIL-Format" in der Lese-Ansicht
   * ist ein eigener Auftrag an das gerade geöffnete Formular. Der Vorschlag landet
   * dort — gespeichert wird weiterhin erst per Hand.
   */
  const [autoOptimizeDescription, setAutoOptimizeDescription] = useState(0);
  // Kunde und Zuweisung stehen nicht mehr doppelt (Kopf + Abschnittsliste), sondern
  // öffnen sich als Popover direkt an den Kopfzeilen-Elementen.
  const [customerPopoverOpen, setCustomerPopoverOpen] = useState(false);
  const [assignPopoverOpen, setAssignPopoverOpen] = useState(false);
  // Anhänge direkt sichtbar, damit Techniker Dokumente sofort sehen/herunterladen können.
  const [attachmentsOpen, setAttachmentsOpen] = useState(true);
  const [mailsOpen, setMailsOpen] = useState(false);
  const [reminderPopoverOpen, setReminderPopoverOpen] = useState(false);
  // Solange das Erinnerungs-Modal offen ist, darf das Popover nicht auf
  // Outside-Klicks schließen: das Modal hängt per Portal im <body>, jeder Klick
  // darin zählt sonst als „außerhalb" und reißt Popover samt Modal weg.
  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [liveTicket, setLiveTicket] = useState<WeclappTicket | null>(null);
  const [liveChanged, setLiveChanged] = useState(false);
  const [handoverModalOpen, setHandoverModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const { user: currentUser } = useAuthStore();
  const isAdmin = currentUser?.role === 'ADMIN';

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['weclapp-ticket', ticketId],
    queryFn: () => api.getWeclappTicket(ticketId!),
    enabled: !!ticketId,
    staleTime: 30000,
  });

  // Rückweg zum Chat: wurde dieses Ticket aus der Chat-Inbox erstellt, führt
  // ein Button direkt zur verknüpften WhatsApp-Konversation.
  const chatLinkQuery = useQuery({
    queryKey: ['chat-for-ticket', ticketId],
    queryFn: () => api.getChatForTicket(ticketId!),
    enabled: !!ticketId,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const chatConversationId = chatLinkQuery.data?.conversationId ?? null;

  /**
   * Wurde das Ticket gelöscht? Das Backend antwortet dann mit 404 + code
   * TICKET_TRASHED und sagt im Detailtext, wer es wann gelöscht hat — oder, wenn es endgültig
   * gelöscht ist, mit 410 + TICKET_DELETED und dem Grabstein („gelöscht am … von … — Begründung").
   */
  const trashedInfo = useMemo(() => {
    if (!(error instanceof ApiError) || (error.code !== 'TICKET_TRASHED' && error.code !== 'TICKET_DELETED')) return null;
    return {
      summary: error.summary,
      detail: error.detail ?? '',
      // Endgültig in Weclapp gelöschte Tickets lassen sich nicht mehr zurückholen —
      // der Text nennt diesen Fall explizit.
      restorable: error.code === 'TICKET_TRASHED' && !error.summary.includes('endgültig'),
    };
  }, [error]);

  const errorText = error instanceof Error ? error.message : null;

  /** Gelöschtes Ticket direkt aus dem Hinweis heraus zurückholen (Admin). */
  const restoreMutation = useMutation({
    mutationFn: () => api.restoreTrashedTickets([ticketId!]),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['weclapp-ticket', ticketId] });
      queryClient.invalidateQueries({ queryKey: ['all-weclapp-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['my-weclapp-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['ticket-trash'] });
      queryClient.invalidateQueries({ queryKey: ['ticket-trash-count'] });
      notifications.show({
        ...restoreNotice(result),
      });
      refetch();
    },
    onError: (err) => {
      notifications.show({
        title: 'Fehler',
        message: err instanceof Error ? err.message : 'Wiederherstellen fehlgeschlagen',
        color: 'red',
      });
    },
  });

  /**
   * Ticket in den Papierkorb verschieben (Admin). Löscht nichts in Weclapp — endgültig nur nach
   * 10 Jahren mit Vier-Augen-Freigabe, siehe TicketTrashPage.
   */
  const trashMutation = useMutation({
    mutationFn: () => api.trashTickets([ticketId!]),
    onSuccess: (result) => {
      setDeleteConfirmOpen(false);
      queryClient.invalidateQueries({ queryKey: ['all-weclapp-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['my-weclapp-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['weclapp-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['team-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['ticket-trash'] });
      queryClient.invalidateQueries({ queryKey: ['ticket-trash-count'] });
      notifications.show({
        title: 'In den Papierkorb verschoben',
        message: `${result.message} · wiederherstellbar, wird nie automatisch gelöscht`,
        color: 'orange',
      });
      onClose();
    },
    onError: (err) => {
      notifications.show({
        title: 'Fehler',
        message: err instanceof Error ? err.message : 'Löschen fehlgeschlagen',
        color: 'red',
      });
    },
  });

  /** Aus der Lese-Ansicht heraus: Bearbeiten öffnen UND die Optimierung anstoßen. */
  const handleOptimizeDescription = () => {
    if (!editMode) handleToggleEdit();
    setAutoOptimizeDescription((n) => n + 1);
  };

  const handleToggleEdit = () => {
    setEditMode((prev) => {
      const opening = !prev;
      if (opening && ticketId) {
        api.getWeclappTicketLive(ticketId).then((res) => {
          setLiveTicket(res.ticket);
          setLiveChanged(res.changed);
        }).catch(() => { /* silent: stale data is fine */ });
      }
      return opening;
    });
  };

  const ticket = data?.ticket;

  const { data: handoverData } = useQuery({
    queryKey: ['weclapp-handover-active', ticketId],
    queryFn: () => api.getWeclappActiveHandover(ticketId!),
    enabled: !!ticketId,
    staleTime: 15000,
  });
  const activeHandover = handoverData?.handover ?? null;

  const { data: remindersData } = useQuery({
    queryKey: ticketTodosKey('weclapp', ticketId ?? ''),
    queryFn: () => api.getTicketTodos('weclapp', ticketId!),
    enabled: !!ticketId,
    staleTime: 30000,
  });
  const activeReminderCount = (remindersData?.todos ?? []).length;

  useEffect(() => {
    setLiveTicket(null);
    setLiveChanged(false);
    setEditMode(false);
    setTimeFormOpen(false);
    setCustomerPopoverOpen(false);
    setAssignPopoverOpen(false);
    setHandoverModalOpen(false);
  }, [ticketId]);

  const kianWeclapp = useMemo(() => {
    if (!ticketId || !ticket) return null;
    const label =
      `Weclapp #${ticket.ticketNumber} – ${ticket.subject ?? ''}`.slice(0, 200);
    return {
      type: 'weclapp-ticket' as const,
      id: ticket.id,
      label,
      metadata: { party: ticket.partyName },
    };
  }, [ticketId, ticket]);

  useRegisterKianContext(kianWeclapp);

  const titleMutation = useMutation({
    mutationFn: () => api.updateWeclappTicket(ticket!.id, { subject: titleDraft.trim() }),
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: ['weclapp-ticket', ticketId] });
      void queryClient.invalidateQueries({ queryKey: ['weclapp-tickets'] });
      void queryClient.invalidateQueries({ queryKey: ['my-weclapp-tickets'] });
      setEditingTitle(false);
      showTicketUpdateNotice(result, 'Betreff aktualisiert');
    },
    onError: (e: Error) => notifications.show({ title: 'Fehler', message: e.message, color: 'red' }),
  });

  return (
    <Drawer
      opened={!!ticketId}
      onClose={guardedClose}
      position="right"
      size={isMobile ? '100%' : isWideCockpit ? (hasKianAnalysis ? 1180 : 1020) : 'lg'}
      withCloseButton={false}
      title={null}
      zIndex={zIndex}
      styles={{
        header: { display: 'none' },
        // Body als Flex-Spalte mit overflow:hidden: erst dadurch greift das
        // flex:1/minHeight:0 des Inhaltsbereichs und Ticket- und KIAN-Spalte
        // scrollen UNABHÄNGIG voneinander (vorher scrollte der ganze Body als
        // Einheit und die KIAN-Analyse wanderte mit dem Ticket mit). Bonus:
        // der Ticket-Kopf bleibt beim Scrollen stehen.
        body: { padding: 0, height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
        content: { display: 'flex', flexDirection: 'column', background: 'var(--mantine-color-dark-8)' },
      }}
    >
      {/* Header — flexShrink: 0, damit er im Flex-Body nicht zusammengedrückt wird */}
      <div
        style={{
          position: 'relative',
          flexShrink: 0,
          background: 'linear-gradient(135deg, var(--mantine-color-dark-7) 0%, var(--mantine-color-dark-8) 100%)',
          borderBottom: '1px solid var(--mantine-color-dark-5)',
          padding: '1rem 1.5rem',
        }}
      >
        {/* Schließen als X oben rechts, wie bei jedem Modal und im ConnectWise-Cockpit —
            und in jedem Zustand. Vorher stand links ein Pfeil „Zurück", der sich nicht als
            „Flyout schließen" las; beim Laden und bei einem Ladefehler fehlte er ganz, und
            am Handy (Flyout bildschirmfüllend, kein Klick daneben) gab es keinen sichtbaren
            Weg hinaus. */}
        <Tooltip label="Ansicht schließen (Esc)">
          <ActionIcon
            variant="subtle"
            color="gray"
            size="lg"
            onClick={guardedClose}
            aria-label="Ansicht schließen"
            style={{ position: 'absolute', top: '0.75rem', right: '0.75rem', zIndex: 2 }}
          >
            <IconX size={20} />
          </ActionIcon>
        </Tooltip>
        {isLoading ? (
          <Stack gap="xs">
            <Skeleton height={24} width={80} radius="sm" />
            <Skeleton height={24} style={{ maxWidth: 300 }} radius="sm" />
          </Stack>
        ) : trashedInfo ? (
          // Gelöschtes Ticket: kein „Fehler", sondern eine Auskunft. Erinnerungen,
          // Übergaben und Lesezeichen verlinken auf Ticket-IDs — ohne diesen Hinweis
          // stünde man vor einem unerklärlichen „nicht gefunden".
          <Stack gap="xs">
            <Group gap="xs" style={{ paddingRight: HEADER_CLOSE_CLEARANCE }}>
              <IconTrash size={18} color="var(--mantine-color-orange-4)" />
              <Text fw={600} c="orange.3">
                {trashedInfo.summary}
              </Text>
            </Group>
            {/* Eingerückt bis zum Text hinter dem Papierkorb-Symbol (18 px + Abstand). */}
            <Text size="sm" c="dimmed" style={{ paddingLeft: 'calc(18px + var(--mantine-spacing-xs))' }}>
              {trashedInfo.detail}
            </Text>
            {isAdmin && (
              <Group gap="xs" style={{ paddingLeft: 'calc(18px + var(--mantine-spacing-xs))' }}>
                {trashedInfo.restorable && (
                  <Button
                    size="xs"
                    color="green"
                    variant="light"
                    leftSection={<IconRestore size={14} />}
                    loading={restoreMutation.isPending}
                    onClick={() => restoreMutation.mutate()}
                  >
                    Wiederherstellen
                  </Button>
                )}
                <Button
                  size="xs"
                  variant="subtle"
                  color="gray"
                  component={Link}
                  to={`/tickets/trash?ticket=${encodeURIComponent(ticketId ?? '')}`}
                  onClick={guardedClose}
                >
                  Im Papierkorb anzeigen
                </Button>
              </Group>
            )}
          </Stack>
        ) : error ? (
          <Stack gap="xs">
            <Group gap="xs" style={{ paddingRight: HEADER_CLOSE_CLEARANCE }}>
              <IconAlertTriangle size={20} color="var(--mantine-color-red-4)" />
              <Text c="red.4">Fehler beim Laden des Tickets</Text>
            </Group>
            {/* Bisher wurde die Ursache komplett verschluckt — ohne sie ist jeder Fehler
                gleich aussagelos. */}
            {errorText && (
              <Text size="sm" c="dimmed">
                {errorText}
              </Text>
            )}
            <Button size="xs" variant="light" onClick={() => refetch()} leftSection={<IconRefresh size={14} />}>
              Erneut versuchen
            </Button>
          </Stack>
        ) : ticket ? (
          <Stack gap="xs">
            <Group gap="xs" align="flex-start" style={{ paddingRight: HEADER_CLOSE_CLEARANCE }}>
              <TicketShareBadge ticketNumber={ticket.ticketNumber} ticketId={ticket.id} />
              {editingTitle ? (
                <Group gap="xs" style={{ flex: 1 }} wrap="nowrap">
                  <TextInput
                    value={titleDraft}
                    onChange={(e) => setTitleDraft(e.currentTarget.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && titleDraft.trim()) titleMutation.mutate();
                      if (e.key === 'Escape') setEditingTitle(false);
                    }}
                    size="xs"
                    style={{ flex: 1 }}
                    autoFocus
                    disabled={titleMutation.isPending}
                  />
                  <ActionIcon size="sm" color="teal" variant="filled" onClick={() => titleMutation.mutate()} disabled={!titleDraft.trim()} loading={titleMutation.isPending} aria-label="Speichern">
                    <IconCheck size={13} />
                  </ActionIcon>
                  <ActionIcon size="sm" color="gray" variant="subtle" onClick={() => setEditingTitle(false)} disabled={titleMutation.isPending} aria-label="Abbrechen">
                    <IconX size={13} />
                  </ActionIcon>
                </Group>
              ) : (
                <Group gap={6} style={{ flex: 1 }} wrap="nowrap" align="flex-start">
                  <Text fw={600} size="lg" c="gray.1" style={{ lineHeight: 1.4 }}>
                    {ticket.subject}
                  </Text>
                  <ActionIcon variant="subtle" color="gray" size="xs" onClick={() => { setTitleDraft(ticket.subject ?? ''); setEditingTitle(true); }} aria-label="Betreff bearbeiten" style={{ opacity: 0.5, marginTop: 3, flexShrink: 0 }}>
                    <IconPencil size={12} />
                  </ActionIcon>
                </Group>
              )}
            </Group>
            {/* Kunde direkt im Kopf: Name verlinkt zum Kunden, der Stift (bzw. der
                „Kunde zuordnen"-Button ohne Kunde) öffnet die Zuordnung als Popover —
                der frühere Abschnitt „Kunde" in der Liste entfällt dadurch. */}
            <Group gap={6} align="center">
              {!isCustomerMissing(ticket.partyName) && (
                <Text size="sm" c="gray.4">
                  <CustomerNameLink
                    name={ticket.partyName ?? ''}
                    weclappCustomerId={ticket.partyId}
                  />
                  {ticket.contactName && ` • ${ticket.contactName}`}
                </Text>
              )}
              {!isCustomerMissing(ticket.partyName) && ticket.partyId && (
                <CustomerOpeningStatusBadge ident={{ weclappCustomerId: ticket.partyId }} size="sm" />
              )}
              <Popover
                opened={customerPopoverOpen}
                onChange={setCustomerPopoverOpen}
                position="bottom-start"
                width={480}
                shadow="md"
                withinPortal
                zIndex={zIndex + 100}
                // Die Kundenauswahl (Select) rendert ihr Dropdown im Portal — der Klick auf
                // einen Kunden zählte als „außerhalb" und schloss das Popover samt Auswahl.
                // Deshalb: nur explizit schließen (X, Escape, erneuter Chip-Klick, Erfolg).
                closeOnClickOutside={false}
                styles={{ dropdown: { maxWidth: 'calc(100vw - 24px)' } }}
              >
                <Popover.Target>
                  {isCustomerMissing(ticket.partyName) ? (
                    <Button
                      size="compact-xs"
                      color="orange"
                      leftSection={<IconBuilding size={12} />}
                      onClick={() => setCustomerPopoverOpen((o) => !o)}
                    >
                      Kunde zuordnen
                    </Button>
                  ) : (
                    <Tooltip label="Kundenzuordnung ändern">
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="xs"
                        onClick={() => setCustomerPopoverOpen((o) => !o)}
                        aria-label="Kundenzuordnung ändern"
                        style={{ opacity: 0.6 }}
                      >
                        <IconBuilding size={12} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                </Popover.Target>
                <Popover.Dropdown>
                  <Group justify="flex-end" mb={4}>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      onClick={() => setCustomerPopoverOpen(false)}
                      aria-label="Kundenzuordnung schließen"
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  </Group>
                  <ScrollArea.Autosize mah="min(70vh, 560px)" type="auto">
                    <CustomerMatchPanel ticket={ticket} onAssigned={() => setCustomerPopoverOpen(false)} />
                  </ScrollArea.Autosize>
                </Popover.Dropdown>
              </Popover>
            </Group>
            <Group gap="xs">
              <StatusQuickMenu ticket={ticket} />
              {ticket.priority && (
                <Badge size="sm" variant="light" color="orange">
                  {ticket.priority}
                </Badge>
              )}
              {/* Zuweisung ebenfalls im Kopf statt als Listen-Abschnitt: Badge anklicken
                  öffnet das Zuweisungsformular (übernehmen / zuweisen / Pool). */}
              <Popover
                opened={assignPopoverOpen}
                onChange={setAssignPopoverOpen}
                position="bottom-start"
                width={420}
                shadow="md"
                withinPortal
                zIndex={zIndex + 100}
                // Gleiches Portal-Problem wie beim Kunden-Popover: Klick in die
                // Techniker-Auswahl darf das Popover nicht schließen.
                closeOnClickOutside={false}
                styles={{ dropdown: { maxWidth: 'calc(100vw - 24px)' } }}
              >
                <Popover.Target>
                  <Badge
                    size="sm"
                    variant="light"
                    color={
                      ticket.responsibleUserName
                        ? personColors.colorOf(ticket.assigneeRef, ticket.responsibleUserId, ticket.responsibleUserName)
                        : 'orange'
                    }
                    leftSection={<IconUserCheck size={11} />}
                    onClick={() => setAssignPopoverOpen((o) => !o)}
                    style={{
                      cursor: 'pointer',
                      ...(ticket.responsibleUserName
                        ? personRingStyle(
                            personColors.colorOf(ticket.assigneeRef, ticket.responsibleUserId, ticket.responsibleUserName),
                            { width: 1.5 },
                          )
                        : undefined),
                    }}
                  >
                    {ticket.responsibleUserName ?? 'Zuweisen'}
                  </Badge>
                </Popover.Target>
                <Popover.Dropdown>
                  <Group justify="flex-end" mb={4}>
                    <ActionIcon
                      variant="subtle"
                      color="gray"
                      size="sm"
                      onClick={() => setAssignPopoverOpen(false)}
                      aria-label="Zuweisung schließen"
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  </Group>
                  <ScrollArea.Autosize mah="min(70vh, 560px)" type="auto">
                    <AssignmentForm
                      ticket={ticket}
                      onAssigned={() => setAssignPopoverOpen(false)}
                      onHandover={
                        !activeHandover
                          ? () => {
                              setAssignPopoverOpen(false);
                              setHandoverModalOpen(true);
                            }
                          : undefined
                      }
                    />
                  </ScrollArea.Autosize>
                </Popover.Dropdown>
              </Popover>
              {ticket.createdDate && (
                <Text size="xs" c="dimmed">
                  {/* Klick übernimmt den Zeitstempel als Startzeit in die Zeitbuchung. */}
                  <Tooltip label="Als Startzeit in die Zeitbuchung übernehmen" withArrow>
                    <span
                      style={{
                        cursor: 'pointer',
                        textDecoration: 'underline dotted',
                        textUnderlineOffset: 3,
                      }}
                      onClick={() => {
                        const d = parseTicketDateTime(ticket.createdDate!);
                        if (d) adoptTimestamp(d);
                      }}
                    >
                      Erstellt {formatTicketDateTime(ticket.createdDate)}
                    </span>
                  </Tooltip>
                  {ticket.lastModifiedDate ? (
                    <>
                      {' · '}
                      <Tooltip label="Als Startzeit in die Zeitbuchung übernehmen" withArrow>
                        <span
                          style={{
                            cursor: 'pointer',
                            textDecoration: 'underline dotted',
                            textUnderlineOffset: 3,
                          }}
                          onClick={() => {
                            const d = parseTicketDateTime(ticket.lastModifiedDate!);
                            if (d) adoptTimestamp(d);
                          }}
                        >
                          aktualisiert {formatTicketDateTime(ticket.lastModifiedDate)}
                        </span>
                      </Tooltip>
                    </>
                  ) : null}
                </Text>
              )}
            </Group>
            <Group gap="xs">
              {chatConversationId && (
                <Tooltip
                  label={`WhatsApp-Chat öffnen${chatLinkQuery.data?.contactName ? ` (${chatLinkQuery.data.contactName})` : ''}`}
                  withArrow
                >
                  <Button
                    size={isMobile ? 'xs' : 'compact-xs'}
                    variant="light"
                    color="green"
                    component={Link}
                    to={`/chats?open=${chatConversationId}`}
                    leftSection={<IconBrandWhatsapp size={12} />}
                  >
                    Zum Chat
                  </Button>
                </Tooltip>
              )}
              {/* Nur-ServiceHub-Tickets (`local-…`) gibt es in weclapp noch nicht — der Link führte ins Leere. */}
              {!ticket.id.startsWith('local-') && (
                <Button
                  size={isMobile ? 'xs' : 'compact-xs'}
                  variant="subtle"
                  color="gray"
                  component="a"
                  href={ticket.weclappUrl ?? `https://app.weclapp.com/app/ticket/${ticket.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  leftSection={<IconExternalLink size={12} />}
                >
                  In Weclapp öffnen
                </Button>
              )}
              {!activeHandover && (
                <Button
                  size={isMobile ? 'xs' : 'compact-xs'}
                  variant="light"
                  color="orange"
                  leftSection={<IconTransfer size={12} />}
                  onClick={() => setHandoverModalOpen(true)}
                >
                  Übergeben
                </Button>
              )}
              {/* Ohne vorhandene Analyse keine eigene KIAN-Spalte — nur dieser
                  kleine Starter mit (i)-Hinweis. Die Spalte erscheint mit Inhalt. */}
              {!hasKianAnalysis && ticketId && (
                <KianAnalysisPanel
                  ticketId={ticketId}
                  source="WECLAPP"
                  compact
                  ticketEnteredAt={
                    ticket.createdDate
                      ? new Date(parseInt(ticket.createdDate)).toISOString()
                      : undefined
                  }
                />
              )}
              {isAdmin && (
                <Tooltip label="Ticket löschen (Papierkorb, wiederherstellbar)">
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    size="md"
                    onClick={() => setDeleteConfirmOpen(true)}
                    aria-label="Ticket löschen"
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Tooltip>
              )}
              <Popover
                opened={reminderPopoverOpen}
                onChange={setReminderPopoverOpen}
                closeOnClickOutside={!reminderModalOpen}
                position="bottom-end"
                width={340}
                shadow="md"
                withinPortal
                zIndex={600}
                // Auf schmalen Screens nie über den Viewport hinauslaufen.
                styles={{ dropdown: { maxWidth: 'calc(100vw - 24px)' } }}
              >
                <Popover.Target>
                  <Indicator
                    label={activeReminderCount > 0 ? String(activeReminderCount) : undefined}
                    size={16}
                    color="grape"
                    disabled={activeReminderCount === 0}
                  >
                    <Tooltip label="Aufgaben & Erinnerungen zu diesem Ticket">
                      <ActionIcon
                        variant={activeReminderCount > 0 ? 'light' : 'subtle'}
                        color={activeReminderCount > 0 ? 'grape' : 'gray'}
                        size="md"
                        onClick={() => setReminderPopoverOpen((o) => !o)}
                        aria-label="Erinnerungen"
                      >
                        <IconBell size={16} />
                      </ActionIcon>
                    </Tooltip>
                  </Indicator>
                </Popover.Target>
                <Popover.Dropdown p={0}>
                  <WeclappTicketReminderPanel
                    onModalOpenChange={setReminderModalOpen}
                    weclappTicketId={ticket.id}
                    weclappTicketNumber={ticket.ticketNumber}
                    ticketSummary={ticket.subject}
                    companyName={isCustomerMissing(ticket.partyName) ? undefined : ticket.partyName}
                    partyId={isCustomerMissing(ticket.partyName) ? undefined : ticket.partyId}
                  />
                </Popover.Dropdown>
              </Popover>
              <TicketNudgeMenu ticket={ticket} />
            </Group>
          </Stack>
        ) : null}
      </div>

      {/* Body */}
      {ticket && (
        <HandoverModal
          opened={handoverModalOpen}
          onClose={() => setHandoverModalOpen(false)}
          source="WECLAPP"
          weclappTicketId={ticket.id}
          ticketSummary={ticket.subject ?? undefined}
        />
      )}

      {/* Rückfrage beim Schließen mit angefangenen Eingaben. Als Modal statt
          window.confirm: der Browser-Dialog sieht aus wie eine Systemwarnung, lässt sich
          nicht gestalten und wird in manchen Browsern nach mehrfachem Auslösen
          unterdrückt — dann verschwände der Entwurf wieder stillschweigend. */}
      <Modal
        opened={pendingCloseLabels !== null}
        onClose={() => setPendingCloseLabels(null)}
        title={<Text fw={600}>Eingaben verwerfen?</Text>}
        centered
        zIndex={zIndex + 200}
      >
        <Stack gap="md">
          <Text size="sm">
            Nicht gespeichert: <b>{(pendingCloseLabels ?? []).join(', ')}</b>. Beim Schließen geht der
            Inhalt verloren.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="subtle" color="gray" onClick={() => setPendingCloseLabels(null)}>
              Weiter bearbeiten
            </Button>
            <Button
              color="red"
              leftSection={<IconTrash size={16} />}
              onClick={() => {
                setPendingCloseLabels(null);
                onClose();
              }}
            >
              Verwerfen und schließen
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        title={<Text fw={600}>Ticket löschen?</Text>}
        centered
        zIndex={700}
      >
        <Stack gap="md">
          <Text size="sm">
            <b>#{ticket?.ticketNumber}</b> {ticket?.subject}
          </Text>
          <Text size="sm" c="dimmed">
            Das Ticket verschwindet sofort aus allen Portal-Ansichten und liegt mindestens 10 Jahre
            im Papierkorb. Gelöscht wird es nie automatisch — endgültig nur, wenn ein Admin es nach
            Ablauf der Frist beantragt und ein zweiter Admin bestätigt, oder einzeln durch einen Super-Admin.
          </Text>
          <Group justify="flex-end" gap="sm">
            <Button variant="subtle" color="gray" onClick={() => setDeleteConfirmOpen(false)}>
              Abbrechen
            </Button>
            <Button
              color="red"
              leftSection={<IconTrash size={16} />}
              loading={trashMutation.isPending}
              onClick={() => trashMutation.mutate()}
            >
              In den Papierkorb
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Auf breiten Bildschirmen steht die KIAN-Analyse in einer eigenen Spalte —
          sie ist der wertvollste Inhalt und lag vorher ganz unten in der Liste.
          Schmal/mobil wird stattdessen zwischen beiden Ansichten umgeschaltet. */}
      {ticket && !isWideCockpit && hasKianAnalysis && (
        <SegmentedControl
          fullWidth
          size="xs"
          m="xs"
          value={pane}
          onChange={(v) => setPane(v as 'ticket' | 'kian')}
          data={[
            { value: 'ticket', label: 'Ticket' },
            { value: 'kian', label: 'KIAN-Analyse' },
          ]}
        />
      )}

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
      <ScrollArea
        style={{ flex: 1, display: !ticket || isWideCockpit || pane === 'ticket' ? undefined : 'none' }}
        p="md"
      >
        {ticket && (
          <Stack gap={0}>
            {/* Handover Banner */}
            {activeHandover && (
              <div style={{ marginBottom: '0.5rem' }}>
                <HandoverBanner handover={activeHandover} ticketId={ticket.id} />
              </div>
            )}

            {/* Beschreibung — Lese-Ansicht und Bearbeiten (Betreff, Text, Status,
                Priorität) teilen sich einen Abschnitt; der Button schaltet um. */}
            <Section
              icon={<IconFileText size={16} />}
              title="Beschreibung"
              open={descriptionOpen}
              onToggle={() => setDescriptionOpen(!descriptionOpen)}
            >
              {editMode ? (
                <Stack gap="xs">
                  {liveChanged && (
                    <Alert color="yellow" p="xs" style={{ fontSize: 12 }}>
                      Ticket wurde seit dem letzten Sync geändert — aktuelle Daten geladen.
                    </Alert>
                  )}
                  <Group justify="flex-end">
                    <Button
                      size="compact-xs"
                      variant="subtle"
                      color="gray"
                      leftSection={<IconX size={12} />}
                      onClick={handleToggleEdit}
                    >
                      Bearbeiten beenden
                    </Button>
                  </Group>
                  <EditTicketForm
                    ticket={liveTicket ?? ticket}
                    autoOptimizeDescription={autoOptimizeDescription}
                    onCancel={handleToggleEdit}
                    onAskCustomer={askCustomerViaComment}
                  />
                </Stack>
              ) : (
                <Stack gap="sm">
                  {/* KIAN steht bewusst HIER und nicht erst im Bearbeiten-Formular:
                      gerade das leere Ticket („neuer ma") ist der Fall, in dem KIAN
                      aus Betreff, Mails und Anhängen überhaupt erst etwas macht. */}
                  <Group justify="flex-end" gap={6}>
                    <KianAssistUsageHint />
                    <Tooltip
                      label="Beschreibung aus Kunde, Betreff, Ticket-Mails und Anhängen ins ITIL-Format bringen — auch wenn noch keine Beschreibung da ist"
                      withArrow
                      multiline
                      w={280}
                    >
                      <Button
                        size="compact-xs"
                        variant="light"
                        color="grape"
                        leftSection={<IconSparkles size={13} />}
                        onClick={handleOptimizeDescription}
                      >
                        KIAN: ITIL-Format
                      </Button>
                    </Tooltip>
                  </Group>
                  {/* Mail2Ticket: Absender und Eingangszeit stehen sonst nirgends im
                      Cockpit — die Beschreibung ist bei diesen Tickets der Mailtext. */}
                  {ticket.senderEmail && (
                    <Group gap={6} wrap="nowrap">
                      <IconMailOpened size={13} color="var(--mantine-color-blue-4)" style={{ flexShrink: 0 }} />
                      <Text size="xs" c="dimmed" style={{ wordBreak: 'break-word' }}>
                        Per E-Mail von{' '}
                        {ticket.senderName
                          ? `${ticket.senderName} <${ticket.senderEmail}>`
                          : ticket.senderEmail}
                        {ticket.createdDate ? ` · ${formatDateWithAge(ticket.createdDate)}` : ''}
                      </Text>
                    </Group>
                  )}
                  {ticket.description ? (
                    <EmailDescription html={ticket.description} ticketLayout />
                  ) : (
                    <Text size="sm" c="dimmed">Keine Beschreibung vorhanden</Text>
                  )}
                  <Button
                    variant="light"
                    leftSection={<IconEdit size={16} />}
                    onClick={handleToggleEdit}
                  >
                    Bearbeiten
                  </Button>
                </Stack>
              )}
            </Section>

            {/* Zeiten — gebuchte Zeiten und „Zeit buchen" in einem Abschnitt;
                das Buchungsformular klappt per Button auf. */}
            <Section
              icon={<IconClockPlay size={16} />}
              title="Zeiten"
              open={timesOpen}
              onToggle={() => setTimesOpen(!timesOpen)}
            >
              <Stack gap="sm">
                {/* Steht ÜBER den Buchungen, nicht darunter: Aus welchem Topf die
                    Zeiten laufen, gehört gelesen, bevor die nächste gebucht wird. */}
                <TicketBillingScopeControl ticketId={ticketId!} />
                <TimeEntryList ticketId={ticketId!} />
                {timeFormOpen ? (
                  <Paper withBorder p="sm" radius="md">
                    <Group justify="space-between">
                      <Text size="sm" fw={600}>Zeit buchen</Text>
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="sm"
                        onClick={() => setTimeFormOpen(false)}
                        aria-label="Zeit buchen schließen"
                      >
                        <IconX size={14} />
                      </ActionIcon>
                    </Group>
                    <AddTimeEntryForm ticketId={ticketId!} prefillStart={timePrefill} />
                  </Paper>
                ) : (
                  <Button
                    variant="light"
                    leftSection={<IconClockPlus size={16} />}
                    onClick={() => setTimeFormOpen(true)}
                  >
                    Zeit buchen
                  </Button>
                )}
              </Stack>
            </Section>

            {/* Kommentare — Liste und Schreiben in einem Abschnitt */}
            <Section
              icon={<IconMessage size={16} />}
              title="Kommentare"
              open={commentListOpen}
              onToggle={() => setCommentListOpen(!commentListOpen)}
            >
              <Stack gap="sm">
                <KianReplyProposals ticketId={ticketId!} />
                <CommentList ticketId={ticketId!} />
                <AddCommentForm
                  ticketId={ticketId!}
                  prefill={commentPrefill}
                  ticketInfo={
                    ticket
                      ? {
                          number: ticket.ticketNumber,
                          subject: ticket.subject,
                          partyId: isCustomerMissing(ticket.partyName) ? null : ticket.partyId,
                          partyName: isCustomerMissing(ticket.partyName) ? null : ticket.partyName,
                        }
                      : undefined
                  }
                />
              </Stack>
            </Section>

            {/* Anhänge */}
            <Section
              icon={<IconPaperclip size={16} />}
              title="Anhänge"
              open={attachmentsOpen}
              onToggle={() => setAttachmentsOpen(!attachmentsOpen)}
            >
              <AttachmentsList
                documents={ticket.documents ?? []}
                ticketId={ticket.id}
              />
            </Section>

            {/* E-Mail-Archiv */}
            <Section
              icon={<IconMail size={16} />}
              title="E-Mail-Archiv"
              open={mailsOpen}
              onToggle={() => setMailsOpen(!mailsOpen)}
            >
              <MailList ticketId={ticketId!} />
            </Section>

            {/* Verlauf */}
            <Section
              icon={<IconHistory size={16} />}
              title="Verlauf"
              open={historyOpen}
              onToggle={() => setHistoryOpen(!historyOpen)}
            >
              <HistoryList ticketId={ticketId!} onUseTimestamp={adoptTimestamp} />
            </Section>
          </Stack>
        )}
      </ScrollArea>

      {ticket && ticketId && hasKianAnalysis && (isWideCockpit || pane === 'kian') && (
        <div
          style={{
            width: isWideCockpit ? kianPaneWidth : '100%',
            flex: isWideCockpit ? `0 0 ${kianPaneWidth}px` : 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            borderLeft: isWideCockpit ? '1px solid var(--mantine-color-dark-5)' : undefined,
          }}
        >
          <ScrollArea style={{ flex: 1 }} p="md">
            <KianAnalysisPanel
              ticketId={ticketId}
              source="WECLAPP"
              ticketEnteredAt={
                ticket.createdDate
                  ? new Date(parseInt(ticket.createdDate)).toISOString()
                  : undefined
              }
            />
          </ScrollArea>
        </div>
      )}
      </div>
    </Drawer>
  );
}

// ============================================
// SECTION HELPER
// ============================================

function Section({
  icon,
  title,
  open,
  onToggle,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div
      style={{
        borderBottom: '1px solid var(--mantine-color-dark-5)',
        marginBottom: 2,
      }}
    >
      <div
        onClick={onToggle}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          padding: '0.6rem 0',
          cursor: 'pointer',
          color: 'var(--mantine-color-gray-4)',
          fontSize: '0.8rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          userSelect: 'none',
        }}
      >
        {open ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
        {icon}
        {title}
      </div>
      <Collapse expanded={open}>
        <div style={{ paddingBottom: '0.75rem' }}>{children}</div>
      </Collapse>
    </div>
  );
}
